import type { Catalogue } from "./en.ts";

/** French copy. Same keys as `en.ts`; a missing or extra key fails `tsc`. */
export const fr = {
  sourceViewer: {
    description:
      "Affiché dans ESI à partir du fichier stocké. Le téléchargement conserve l'original.",
    loading: "Ouverture du fichier…",
    content: "Contenu du fichier",
    tooLargeTitle: "Trop volumineux pour être affiché ici",
    tooLarge: "Ce fichier dépasse 2 Mo. Téléchargez-le pour le lire.",
    notOpenableTitle: "Ce fichier ne peut pas être affiché ici",
    notOpenable: "Téléchargez-le pour le lire.",
    refused: "Vous ne pouvez plus ouvrir ce fichier.",
    failed: "Le fichier n'a pas pu être ouvert : {{reason}}",
    download: "Télécharger",
    close: "Fermer",
  },
  attachments: {
    readOnly: "Vous pouvez lire les fichiers partagés avec vous. Seul le créateur peut les gérer.",
    addToMessage: "Ajouter au message",
    filesEmpty: "Cette conversation ne contient pas encore de fichiers.",
    emptyConversation: "Posez une question pour commencer.",
    actionFailed: "Impossible d’effectuer cette action. Actualisez les fichiers et réessayez.",
    floorProject: "Les personnes pouvant lire cette conversation peuvent lire ce fichier.",
    floorEntity:
      "Les lecteurs de la conversation doivent aussi appartenir à l’entité de la personne qui ajoute le fichier ou à une sous-entité.",
    floorNamed:
      "Seules la personne qui a ajouté le fichier et les personnes qu\u{2019}elle désigne peuvent le lire.",
    attach: "Joindre des fichiers",
    title: "Fichiers de la conversation",
    scope:
      "Disponibles dans cette conversation uniquement. Le partage dépend toujours de la classification de chaque fichier.",
    choose: "Choisir des fichiers",
    confirm: "Joindre",
    cancel: "Annuler",
    remove: "Retirer du message : {{name}}",
    cancelUpload: "Annuler l’envoi : {{name}}",
    delete: "Supprimer la pièce jointe",
    deleteTitle: "Supprimer la pièce jointe ?",
    deleteBody:
      "L’assistant perd l’accès à ce fichier. Les anciennes citations afficheront Source supprimée. Les étiquettes des messages précédents restent inchangées.",
    notSent: "Non envoyé",
    sent: "Disponible dans cette conversation",
    creationPending:
      "Les fichiers restent ici jusqu’à l’ouverture de la conversation. Réessayez ou retirez-les pour envoyer une question sans fichier.",
    checking: "Vérification",
    selected: "Sélectionné",
    toReview: "À vérifier",
    reviewHint: "Choisissez qui peut voir ces fichiers avant leur envoi.",
    review: "Vérifier",
    reviewLead_one: "Vérifiez {{count}} nouveau fichier avant son envoi",
    reviewLead_other: "Vérifiez {{count}} nouveaux fichiers avant leur envoi",
    opening: "Ouverture de la conversation…",
    removeToSend: "Retirez les fichiers pour envoyer votre question seule.",
    reasonUnavailable: "Les pièces jointes sont indisponibles ici.",
    listFailed: "Impossible de charger les fichiers.",
    uploading: "Envoi",
    processing: "Traitement",
    ready: "Prêt",
    paused: "En pause : limite d’utilisation atteinte",
    failed: "Impossible d’envoyer le fichier. Vérifiez la connexion ou choisissez-le à nouveau.",
    processFailed: "Échec du traitement",
    refused: "Impossible de joindre ce fichier. Actualisez les fichiers et réessayez.",
    unavailable: "Pièce jointe indisponible",
    metadata:
      "Les pièces jointes sont indisponibles. Actualisez ou mettez à jour l’application ; retirez les fichiers sélectionnés pour envoyer uniquement du texte.",
    question: "Ajoutez une question pour envoyer ces fichiers.",
    blocked: "Attendez que chaque fichier sélectionné soit prêt ou retirez-le du message.",
    limit: "Sélectionnez au maximum 10 fichiers par message.",
    retry: "Réessayer",
    retryCheck: "Revérifier",
    reselect: "Retirez et choisissez à nouveau ce fichier ; cet envoi ne peut pas reprendre.",
    open: "Ouvrir {{name}}",
    download: "Télécharger {{name}}",
    marker: "Pièce jointe",
    path: "Cette conversation",
    checkingSend: "Vérification de l’enregistrement du message. Votre brouillon est conservé.",
    checkFailed:
      "Impossible de vérifier si le message a été enregistré. Relancez la vérification avant l’envoi.",
    checkFailedBefore:
      "Impossible de confirmer que les fichiers sélectionnés peuvent encore être envoyés : rien n’a été envoyé. Relancez la vérification avant l’envoi.",
    archived: "Cette conversation est archivée. Les fichiers sont en lecture seule.",
    project_frozen: "Ce projet est gelé. Les fichiers sont en lecture seule.",
    attachments_disabled: "Les nouvelles pièces jointes sont désactivées pour cette conversation.",
    standalone_disabled:
      "Les conversations sans projet sont désactivées. Les fichiers sont en lecture seule.",
    private_level_unavailable:
      "Aucun niveau de classification privé n’existe pour votre organisation. Demandez à un administrateur. Les fichiers sont en lecture seule.",
    done: "Terminé",
    preparing: "Préparation",
    fromOutside: "Venant de l’extérieur de l’entreprise",
    originLocked: "Fixée dès que le fichier a servi dans la conversation.",
    empty: "Choisissez des fichiers ou sélectionnez un fichier déjà ajouté et non envoyé.",
    count_one: "{{count}} fichier",
    count_other: "{{count}} fichiers",
    overLimit: "{{count}} sur {{max}}",
    more: "+{{count}}",
    moreLabel_one: "Afficher {{count}} fichier de plus",
    moreLabel_other: "Afficher {{count}} fichiers de plus",
    showAll: "Tout afficher",
    collapse: "Réduire",
    manage: "Gérer",
    details: "Détails",
    detailsFor: "Détails : {{name}}",
    showing_one: "{{count}} fichier affiché",
    showing_other: "{{count}} fichiers affichés",
    size: "Taille",
    type: "Type",
    status: "État",
    kind: {
      document: "Document",
      spreadsheet: "Tableur",
      presentation: "Présentation",
      image: "Image",
      text: "Texte",
      file: "Fichier",
    },
    max: "Jusqu’à {{mb}} Mo par fichier ; images jusqu’à {{imageMb}} Mo.",
  },
  taskProposal: {
    createTitle: "Créer une tâche",
    updateTitle: "Modifier une tâche : {{title}}",
    fields: {
      title: "Titre",
      assignee: "Assignée à",
      due: "Échéance",
      description: "Description",
      status: "Statut",
      comment: "Commentaire",
    },
    cleared: "Effacée",
    fromSources_one: "Tirée de {{count}} source de cette réponse",
    fromSources_other: "Tirée de {{count}} sources de cette réponse",
    create: "Créer",
    apply: "Appliquer",
    applying: "Enregistrement…",
    edit: "Modifier",
    cancel: "Annuler",
    dismiss: "Ignorer",
    created: "Tâche créée.",
    applied: "Tâche modifiée.",
    dismissed: "Ignorée.",
    expired: "Cette proposition a expiré.",
    open: "Ouvrir la tâche",
    refused: "Vous ne pouvez plus faire cela dans ce projet.",
    gone: "Cette tâche ne vous est plus accessible.",
    decided: "Cette proposition a déjà été traitée.",
    invalidAssignee: "Cette personne ne peut pas recevoir de tâches dans ce projet.",
  },
  tasks: {
    applyStatus: "Appliquer le statut",
    rowStatus: "Statut de {{title}}",
    rowStatusRestricted: "Statut d’une tâche restreinte",
    rowLeft: "« {{title}} » est passée à « {{status}} ».",
    rowLeftRestricted: "Une tâche restreinte est passée à « {{status}} ».",
    rowBusy:
      "Cette tâche est encore en cours d’enregistrement. Réessayez d’annuler dans un instant.",
    rowUndo: "Annuler",
    rowUncertain:
      "Le statut n’a peut-être pas été enregistré. La liste a été actualisée ; vérifiez la tâche avant de réessayer.",
    selectPersonPrompt:
      "Choisissez une personne pour appliquer ce filtre. Le filtre précédent reste actif.",
    levelPublic: "Public",
    levelInternal: "Interne",
    levelConfidential: "Confidentiel",
    myTitle: "Mes tâches",
    title: "Tâches",
    assigned: "Qui me sont attribuées",
    created: "Créées par moi",
    status: "Statut",
    open: "Ouvertes",
    all: "Toutes",
    todo: "À faire",
    in_progress: "En cours",
    blocked: "Bloquée",
    done: "Terminée",
    assignee: "Responsable",
    allPeople: "Tous les responsables",
    unassigned: "Non attribuée",
    person: "Choisir une personne",
    unavailablePerson: "Personne indisponible",
    searchPeople: "Rechercher par nom ou e-mail",
    peopleHint: "Saisissez au moins deux caractères.",
    peopleNone: "Aucune personne éligible trouvée.",
    lookupUnavailable:
      "La recherche de personnes est temporairement indisponible. Le responsable enregistré est conservé.",
    search: "Rechercher un titre de tâche",
    clearFilters: "Effacer les filtres",
    newTask: "Nouvelle tâche",
    edit: "Modifier la tâche",
    taskTitle: "Titre",
    description: "Description",
    due: "Échéance",
    noDue: "Sans échéance",
    clearDate: "Effacer la date",
    overdue: "En retard",
    restricted: "Tâche restreinte",
    unavailable: "Tâche indisponible",
    unavailableBody: "Cette tâche est indisponible. Revenez aux tâches du projet.",
    empty: "Aucune tâche ouverte",
    emptyBody: "Les actions à suivre dans vos projets apparaîtront ici.",
    noMatches: "Aucune tâche correspondante",
    noMatchesBody: "Modifiez ou effacez les filtres.",
    projects: "Voir les projets",
    more: "Afficher la suite",
    restart: "Recommencer la liste",
    cursorError:
      "Cette page n’a pas pu être chargée. Recommencez la liste pour actualiser les résultats.",
    unsupportedOpen:
      "Le filtre Ouvertes est temporairement indisponible. Réessayez ou choisissez Toutes.",
    stale: "L’actualisation a échoué. Les informations affichées peuvent être anciennes.",
    metadata: "Les actions sont temporairement indisponibles. Actualisez pour vérifier vos droits.",
    refresh: "Actualiser",
    disabled:
      "Les tâches sont désactivées pour ce projet. Les tâches existantes restent consultables.",
    frozen: "Ce projet est gelé. Les tâches sont en lecture seule.",
    sources: "Sources",
    addSource: "Ajouter une référence",
    removeSource: "Retirer la référence",
    noSources: "Aucune référence pour le moment.",
    unavailableSource: "Référence indisponible",
    document: "Fichier",
    conversation: "Conversation",
    message: "Message",
    artefact: "Artefact",
    files: "Fichiers",
    conversations: "Conversations",
    chooseConversation: "Choisissez une conversation pour voir ses messages et ses artefacts.",
    backSources: "Revenir aux conversations",
    linkLimit:
      "Une tâche peut avoir au plus 20 références. Retirez-en une avant d’en ajouter une autre.",
    comments: "Commentaires",
    comment: "Ajouter un commentaire",
    commentText: "Commentaire",
    noComments: "Aucun commentaire pour le moment.",
    deleteComment: "Supprimer le commentaire",
    confirmComment: "Supprimer définitivement ce commentaire ?",
    delete: "Supprimer la tâche",
    deleteBody:
      "Cette action supprime définitivement la tâche, ses commentaires et ses références. Les fichiers, conversations, messages et artefacts sources sont conservés.",
    creator: "Créée par {{name}}",
    createdAt: "Créée le {{time}}",
    updatedAt: "Modifiée le {{time}}",
    completedAt: "Terminée le {{time}}",
    classification: "Classification : {{label}}",
    titleInvalid: "Saisissez de 1 à 200 caractères sans caractères de contrôle.",
    descriptionInvalid: "Utilisez au plus 4 000 caractères sans caractères de contrôle.",
    commentInvalid: "Saisissez de 1 à 2 000 caractères sans caractères de contrôle.",
    dateInvalid: "Choisissez une date valide.",
    unchanged: "Aucune modification à enregistrer.",
    uncertain:
      "L’enregistrement n’a pas pu être confirmé. Les données ont été actualisées ; vérifiez-les avant de réessayer.",
    discard: "Abandonner les modifications ?",
    discardBody: "Vos modifications non enregistrées seront perdues.",
    discardAction: "Abandonner",
    nothing: "Aucun élément consultable trouvé.",
    removeCommentBody: "Ce commentaire sera définitivement supprimé.",
    lookupResults: "Personnes éligibles",
    backTasks: "Revenir aux tâches",
    startConversation: "Travailler dessus avec ESI",
    starting: "Démarrage…",
    startUncertain:
      "La conversation n’a pas pu être confirmée. Vérifiez les conversations du projet avant de réessayer.",
  },
  rootError: {
    title: "Un problème est survenu",
    body: "ESI a rencontré une erreur inattendue et n’a pas pu afficher cette page. Recharger la page suffit généralement.",
    reload: "Recharger",
    home: "Retour à l’accueil",
  },
  app: {
    name: "ESI",
    unreachable: "Connexion impossible pour le moment",
    loading: "Chargement…",
    skipToContent: "Aller au contenu principal",
    retry: "Réessayer",
    cancel: "Annuler",
    close: "Fermer",
    done: "Terminé",
    save: "Enregistrer",
    saving: "Enregistrement…",
    remove: "Retirer",
    newTab: "(s'ouvre dans un nouvel onglet)",
    you: "Vous",
    more: "Plus",
  },
  notifications: {
    bell: "Notifications",
    bellUnread_one: "Notifications, {{count}} non lue",
    bellUnread_other: "Notifications, {{count}} non lues",
    title: "Notifications",
    description:
      "Ce qui s’est passé dans les wikis et les tâches de vos projets, et pour vos retours.",
    empty: "Vous êtes à jour",
    markAll: "Tout marquer comme lu",
    unread: "Non lue",
    someone: "Quelqu’un",
    changeSet: "un ensemble de modifications",
    page: "une page",
    task: "une tâche",
    taskStart: "Une tâche",
    quoted: "« {{title}} »",
    feedbackFrom: "Équipe ESI",
    feedback: {
      bug: {
        triaged: "Votre signalement de bug a été examiné",
        planned: "Votre signalement de bug est prévu",
        fixed: "Votre signalement de bug est corrigé",
        fixedIn: "Votre signalement de bug est corrigé dans la version {{version}}",
        not_planned: "Votre signalement de bug n’est pas prévu",
        duplicate: "Votre signalement de bug a été regroupé avec un signalement similaire",
      },
      idea: {
        triaged: "Votre idée a été examinée",
        planned: "Votre idée est prévue",
        fixed: "Votre idée est disponible",
        fixedIn: "Votre idée est disponible dans la version {{version}}",
        not_planned: "Votre idée n’est pas prévue",
        duplicate: "Votre idée a été regroupée avec une idée similaire",
      },
    },
    kind: {
      change_set_submitted: "{{actor}} a soumis {{subject}} à la relecture",
      change_request: "{{actor}} a demandé des modifications sur {{subject}}",
      question: "{{actor}} a posé une question sur {{subject}}",
      reply: "{{actor}} a répondu dans une discussion sur {{subject}}",
      resolved: "{{actor}} a résolu une discussion sur {{subject}}",
      revision_done: "L’agent a révisé {{subject}} comme vous l’avez demandé",
      agent_replies_sent: "L’agent a répondu à vos commentaires sur {{subject}}",
      page_thread: "{{actor}} a ouvert une discussion sur {{subject}}",
      task_assigned: "{{actor}} vous a assigné {{subject}}",
      task_due_today: "{{subject}} arrive à échéance aujourd’hui",
      task_due_tomorrow: "{{subject}} arrive à échéance demain",
      task_due_on: "{{subject}} arrive à échéance le {{date}}",
      task_due_soon: "{{subject}} arrive bientôt à échéance",
      task_was_due: "L’échéance de {{subject}} était le {{date}}",
      feedback_status: "Votre retour a été mis à jour",
    },
    // `agent_replies_sent` avec ses nombres (wiki-resolve-with-agent D11).
    agentReplies_one: "L’agent a répondu à {{count}} de vos commentaires sur {{subject}}",
    agentReplies_other: "L’agent a répondu à {{count}} de vos commentaires sur {{subject}}",
    agentQuestions_one: "{{count}} question pour vous",
    agentQuestions_other: "{{count}} questions pour vous",
  },
  nav: {
    label: "Principale",
    home: "Accueil",
    projects: "Projets",
    conversations: "Conversations",
    tasks: "Tâches",
    knowledge: "Connaissances",
    backoffice: "Administration",
    onEsi: "sur ESI",
    toggleSidebar: "Afficher ou masquer la barre latérale",
    sidebar: "Navigation",
    sidebarHint: "Navigation principale et menu du compte.",
    openMenu: "Ouvrir la navigation",
    recentProjects: "Projets récents",
    newProject: "Nouveau projet",
    newChat: "Nouvelle conversation",
    search: "Rechercher",
    recents: "Récents",
    showMore: "Voir plus",
    showAll: "Tout voir",
    allProjects: "Tous les projets",
    noConversations: "Aucune conversation pour l'instant",
    expandProject: "Afficher les conversations de {{name}}",
    collapseProject: "Masquer les conversations de {{name}}",
    newConversationIn: "Démarrer une conversation dans {{name}}",
    sharedEntry: "{{title}}, partagée par {{name}}",
    more: "Plus",
  },
  feedback: {
    title: "Envoyer un retour",
    lead: "Signalez à l'équipe ESI ce qui ne fonctionne pas, ou proposez une idée. Voici exactement ce qui sera envoyé.",
    type: "De quoi s'agit-il ?",
    kinds: { bug: "Quelque chose ne fonctionne pas", idea: "Une idée ou une suggestion" },
    types: { bug: "Bug", idea: "Idée" },
    describeBug: "Que s'est-il passé, et qu'attendiez-vous ?",
    describeIdea: "Qu'aimeriez-vous qu'ESI fasse ?",
    hint: "L'équipe ESI lit votre texte tel quel. N'y collez pas de contenu confidentiel.",
    remaining_one: "{{count}} caractère restant.",
    remaining_other: "{{count}} caractères restants.",
    empty: "Écrivez quelques mots avant d'envoyer.",
    whatIsSent: "Ce qui sera envoyé",
    whatIsSentLead:
      "Avec votre description, tel que ce sera enregistré. Décochez ce que vous préférez ne pas envoyer. Rien de vos projets n'est joint, sauf ce que vous montrez dans une capture d'écran.",
    groups: {
      app: "Version de l'application et heure",
      route: "Page",
      device: "Navigateur et appareil",
      recentErrors: "Erreurs récentes de cet onglet",
      assistant: "Assistant",
    },
    viewport: { s: "petit écran", m: "écran moyen", l: "grand écran" },
    noErrors: "Aucune erreur dans cet onglet.",
    send: "Envoyer",
    sending: "Envoi…",
    sent: "Merci. L'équipe ESI a reçu votre retour ; vous pouvez le suivre dans Mes retours.",
    seeMine: "Voir mes retours",
    throttled:
      "Vous avez envoyé beaucoup de retours depuis une heure. Vous pourrez en envoyer d'autres à partir de {{time}}.",
    off: "Votre organisation a désactivé les retours.",
  },
  feedbackDraft: {
    suggested: "Signaler ceci à l'équipe ESI ?",
    requested: "Retour pour l'équipe ESI",
    drafted: "Rédigé par l'assistant · rien de cette conversation n'est joint",
    send: "Envoyer",
    sending: "Envoi…",
    edit: "Modifier",
    dismiss: "Ignorer",
    dismissed: "Ignoré. Je ne suggérerai plus ce problème dans cette conversation.",
    dismissedRequested: "Ignoré.",
    stopSuggesting: "Ne plus suggérer de retours",
    stopped:
      "L'assistant ne suggérera plus de retours. Vous pouvez changer cela dans les paramètres de votre compte.",
    sent: "Envoyé à l'équipe ESI.",
    seeMine: "Voir mes retours",
    unavailable: "Envoi impossible. Réessayez dans quelques minutes.",
    gone: "Ce brouillon n'est plus disponible.",
    dialogLead:
      "Rédigé par l'assistant à partir de cette conversation. Modifiez ce que vous voulez. Voici exactement ce qui sera envoyé.",
    nothingAttached: "Aucun texte, titre ni identifiant de la conversation.",
    reportProblem: "Signaler un problème",
    reportFailed: "Impossible d'ouvrir le signalement. Réessayez.",
  },
  feedbackScreenshot: {
    label: "Capture d'écran",
    lead: "Facultative. Tout ce qui vient de vos projets est masqué par des blocs gris ; vous choisissez ce que vous montrez.",
    add: "Ajouter une capture d'écran",
    taking: "Capture en cours…",
    alt: "Capture de cette page telle qu'elle sera envoyée, zones masquées en gris",
    remove: "Retirer",
    show: "Montrer pour cet envoi seulement",
    shown: "Montré : {{regions}}",
    allHidden: "Tout le contenu est masqué.",
    failed: "La capture d'écran n'a pas pu être prise. Vous pouvez envoyer votre retour sans elle.",
    notAttached: "Votre retour a été envoyé, mais la capture d'écran n'a pas pu être jointe.",
    regions: {
      page: "Contenu de la page",
      messages: "Messages",
      composer: "Message en cours de rédaction",
      conversations: "Noms des conversations",
      projects: "Noms et détails des projets",
      files: "Fichiers",
      documents: "Contenu des documents",
      wiki: "Pages du wiki",
      people: "Noms des personnes",
      tasks: "Tâches",
      memory: "Mémoires",
    },
    view: "Voir la capture",
    hide: "Masquer la capture",
    loading: "Chargement de la capture…",
    unavailable: "La capture d'écran n'est plus disponible.",
    console: {
      masked: "Capture d'écran, contenu masqué",
      unmasked: "Capture d'écran, avec du contenu que l'auteur a choisi de montrer",
      open: "Ouvrir la capture",
      opening: "Ouverture…",
      expired: "La capture d'écran a expiré ou n'a pas pu être ouverte.",
      failed: "La capture d'écran n'a pas pu être ouverte. Réessayez.",
      blocked:
        "Votre navigateur a bloqué le nouvel onglet. Autorisez les fenêtres pour la console, puis réessayez.",
    },
  },
  myFeedback: {
    title: "Mes retours",
    lead: "Ce que vous avez envoyé à l'équipe ESI, et ce qu'il en est advenu.",
    none: "Aucun retour pour l'instant",
    noneBody:
      "Utilisez Envoyer un retour dans le menu du compte pour signaler un bug ou proposer une idée.",
    fixedIn: "Corrigé dans la version {{version}}",
    statuses: {
      new: "Nouveau",
      triaged: "Examiné",
      planned: "Prévu",
      fixed: "Corrigé",
      not_planned: "Non prévu",
      duplicate: "Regroupé avec un retour similaire",
    },
    reason: "Pourquoi : {{reason}}",
  },
  account: {
    menu: "Menu du compte",
    signedInAs: "Connecté en tant que",
    theme: "Thème",
    themes: { system: "Système", light: "Clair", dark: "Sombre" },
    language: "Langue",
    languages: { en: "English", fr: "Français" },
    signOut: "Se déconnecter",
    signingOut: "Déconnexion…",
    settings: "Paramètres du compte",
    sendFeedback: "Envoyer un retour",
    about: "À propos d'ESI",
    roles: {
      tenant_admin: "Administrateur de l'organisation",
      entity_admin: "Administrateur d'entité",
      member: "Membre",
    },
  },
  about: {
    title: "À propos d'ESI",
    lead: "La version utilisée dans ce navigateur et les changements des dernières versions.",
    sendFeedback: "Envoyer un retour",
    version: "Version",
    build: "Build",
    copyBuild: "Copier l'identifiant de build",
    copied: "Identifiant de build copié",
    whatsNew: "Nouveautés",
    features: "Nouveau",
    fixes: "Corrigé",
    noNotes: "Pas encore de notes de version.",
    noUserFacing: "Améliorations internes uniquement.",
    more_one: "et {{count}} autre",
    more_other: "et {{count}} autres",
  },
  projectRoles: {
    owner: {
      name: "Propriétaire",
      description: "Responsable du projet ; peut le supprimer et en transférer la propriété.",
    },
    manager: {
      name: "Responsable de projet",
      description: "Gère les membres, les paramètres et le wiki ; peut déclassifier.",
    },
    contributor: {
      name: "Contributeur",
      description: "Ajoute des fichiers et publie des pages du wiki.",
    },
    reader: {
      name: "Lecteur",
      description: "Pose des questions et consulte fichiers et pages du wiki.",
    },
    guest: { name: "Invité", description: "Ne voit que ce qui lui est partagé." },
  },
  home: {
    title: "Interrogez vos connaissances",
    lead: "Les réponses citent les passages sur lesquels elles s'appuient et disent clairement ce qu'elles n'ont pas trouvé.",
    ask: "Posez une question…",
    send: "Demander",
    sendHint: "Entrée pour envoyer, Maj+Entrée pour aller à la ligne",
    project: "Projet",
    projectHint: "Les réponses proviennent d'un seul projet pour l'instant.",
    noProjectTitle: "Les réponses proviennent d'un projet",
    noProject:
      "Créez un projet et ajoutez-y des fichiers : ESI répond à partir de son contenu, avec des citations.",
    createProject: "Créer un projet",
    loadingProjects: "Chargement de vos projets…",
    noProjectOption: "Sans projet",
    standaloneHint: "Sans projet, les réponses n'utilisent aucune connaissance de projet.",
    standaloneDisabled:
      "Les conversations hors projet sont désactivées pour votre organisation. Choisissez un projet.",
  },
  placeholder: {
    notYet: "Pas encore disponible",
    conversations: {
      title: "Conversations",
      body: "Vos conversations de tous les projets seront listées ici. Pour l'instant, ouvrez un projet pour voir ses conversations.",
    },
    knowledge: {
      title: "Connaissances",
      body: "Toutes les sources que vous pouvez lire, tous projets confondus, seront consultables ici. Pour l'instant, le panneau de connaissances de chaque projet liste ses fichiers.",
    },
    backoffice: {
      title: "Administration",
      body: "Administration de votre organisation.",
      refused:
        "L'administration n'est pas accessible à votre rôle. Demandez à un administrateur de l'organisation si vous avez besoin d'un changement.",
    },
    notFound: {
      title: "Page introuvable",
      body: "Cette adresse ne mène nulle part dans ESI.",
      home: "Retour à l'accueil",
    },
    tasksSlot: "Aucune tâche de fond",
  },
  backoffice: {
    signin: "Connexion",
    signinHint: "Fournisseurs d'identité et domaines qui leur envoient les connexions.",
    memory: "Mémoire",
    memoryHint:
      "Conservation, règles de mémoire, chiffres d'usage et événements. Jamais ce que les personnes ont enregistré.",
    title: "Administration",
    lead: "Administration de votre organisation : personnes, entités, paramètres, annuaire et résidence des données.",
    directory: "Annuaire",
    directoryHint:
      "Fournisseur d'identité, sources SCIM et RH ; codes d'organisation non associés.",
    residency: "Résidence des données",
    residencyHint: "Où les modèles peuvent traiter les requêtes et les sources, par niveau.",
    models: "Modèles",
    modelsHint:
      "Ce qu'implique chaque modèle, ceux que votre organisation autorise, et le consentement.",
    back: "Administration",
  },
  users: {
    leaversKeepPassword:
      "Les personnes retirées de {{name}} gardent leur mot de passe ESI jusqu'à ce que vous les retiriez ici, ou connectez une source d'annuaire pour le faire automatiquement.",
    esiCredential: {
      title: "Connexion ESI",
      held: "A un mot de passe ou une clé d'accès ESI en plus de la connexion de son organisation.",
      heldNotAllowed:
        "A un mot de passe ou une clé d'accès ESI que le domaine n'autorise plus : conservé mais refusé.",
      none: "Pas encore de mot de passe ni de clé d'accès ESI. Son domaine en autorise un.",
      send: "Envoyer un lien de connexion ESI",
      sending: "Envoi…",
      sent: "Un lien pour choisir un mot de passe a été envoyé à {{email}}. Il est valable 72 heures.",
      removeEllipsis: "Retirer la connexion ESI…",
      removeEffect:
        "{{email}} perd son mot de passe et ses clés d'accès ESI, et les sessions qui les ont utilisés prennent fin. La connexion de son organisation reste.",
      remove: "Retirer la connexion ESI",
      removed: "La connexion ESI est retirée.",
      pending: "Un lien de connexion ESI a été envoyé et n'a pas encore été utilisé.",
      revokeEllipsis: "Révoquer le lien…",
      revokeEffect:
        "Le lien envoyé à {{email}} ne fonctionne plus et rien n'est ajouté à son compte. Vous pourrez en envoyer un nouveau ensuite.",
      revoke: "Révoquer le lien",
      revoked: "Le lien est révoqué.",
      refused: {
        throttled: "Trop de liens de connexion pour cette personne. Réessayez plus tard.",
        notAllowed: "Le mode de connexion de son domaine n'autorise pas la connexion ESI.",
        onlyWayIn: "C'est son seul moyen de connexion : il ne peut pas être retiré ici.",
        conflict:
          "Impossible pour l'instant : la personne ne s'est pas encore connectée par son fournisseur d'identité, un lien est encore valable, un mot de passe est déjà défini, ou un autre compte de connexion utilise cette adresse.",
      },
    },
    memory: {
      title: "Mémoire",
      lead: "Effacez la mémoire personnelle et les suggestions en attente de cette personne maintenant que son compte est désactivé. Vous ne voyez jamais ce qu'elle a enregistré.",
      erase: "Effacer la mémoire",
      counting: "Décompte de la mémoire…",
      count_one: "{{count}} souvenir personnel",
      count_other: "{{count}} souvenirs personnels",
      none: "Plus aucun souvenir personnel. Ses suggestions en attente et sa qualité d'auteur des souvenirs de projet peuvent encore être effacées.",
      effects: {
        deleted:
          "Ses souvenirs personnels et suggestions en attente sont supprimés définitivement.",
        projects:
          "Les souvenirs de projet qu'elle a suggérés restent dans leurs projets, sans son nom.",
        backups: "Les sauvegardes conservent les données supprimées jusqu'à 7 jours.",
      },
      confirmLabel: "Saisissez {{email}} pour confirmer",
      confirm_one: "Effacer {{count}} souvenir",
      confirm_other: "Effacer {{count}} souvenirs",
      done_one: "{{count}} souvenir effacé.",
      done_other: "{{count}} souvenirs effacés.",
      kept_one: "{{count}} souvenir de projet reste, sans auteur.",
      kept_other: "{{count}} souvenirs de projet restent, sans auteur.",
      changed:
        "Sa mémoire a changé depuis l'affichage du décompte. Vérifiez le nouveau nombre, puis confirmez à nouveau.",
      active: "Cette personne est de nouveau active : elle gère elle-même sa mémoire.",
    },
    title: "Utilisateurs",
    hint: "Les personnes de votre périmètre : statut, entités et rôles, dernière connexion.",
    lead: "Les personnes des entités que vous administrez. Désactiver quelqu'un le déconnecte à sa prochaine requête et conserve son travail.",
    invite: "Inviter",
    adminsOnly: "Seuls les administrateurs voient les utilisateurs.",
    search: "Rechercher des utilisateurs",
    searchPlaceholder: "Rechercher par nom ou e-mail",
    noMatch: "Aucun utilisateur ne correspond à cette recherche.",
    none: "Aucun utilisateur pour l'instant.",
    more: "Afficher plus",
    caption: "Utilisateurs de votre périmètre",
    user: "Utilisateur",
    statusLabel: "Statut",
    status: { active: "Actif", disabled: "Désactivé" },
    memberships: "Entités et rôles",
    managedBy: "Géré par",
    managedByEsi: "ESI",
    lastSignIn: "Dernière connexion",
    neverSignedIn: "Jamais",
    actions: "Actions",
    manage: "Gérer",
    manageOf: "Gérer {{name}}",
    outsideScope_one: "Aussi dans {{count}} entité hors de votre périmètre",
    outsideScope_other: "Aussi dans {{count}} entités hors de votre périmètre",
    access: "Accès",
    syncWarning:
      "Géré par {{sources}} : la prochaine synchronisation peut réactiver cet utilisateur. Désactivez-le aussi dans votre fournisseur d'identité.",
    notManageable:
      "Cet utilisateur appartient aussi à des entités que vous n'administrez pas : seul un administrateur de l'organisation peut le désactiver.",
    selfNote: "Vous ne pouvez pas désactiver votre propre compte.",
    disable: "Désactiver l'utilisateur",
    disableTitle: "Désactiver {{name}} ?",
    disableEffects: {
      signedOut:
        "Il est déconnecté à sa prochaine requête, sur tous ses appareils et applications.",
      kept: "Ses projets, fichiers et conversations restent en place.",
      reversible: "Vous pouvez le réactiver à tout moment.",
    },
    disableConfirm: "Désactiver",
    enable: "Réactiver l'utilisateur",
    roleIn: "Rôle dans {{entity}}",
    removeFrom: "Retirer de {{entity}}",
    addTo: "Ajouter à une entité",
    pickEntity: "Choisir une entité",
    role: "Rôle",
    add: "Ajouter",
    inviteTitle: "Inviter quelqu'un",
    inviteBody:
      "La personne rejoint l'entité choisie. Hors des domaines de votre fournisseur d'identité, elle reçoit un e-mail pour créer son compte.",
    email: "E-mail",
    displayName: "Nom (facultatif)",
    entity: "Entité",
    inviteConfirm: "Inviter",
    inviting: "Invitation…",
    refused: {
      self: "Vous ne pouvez pas désactiver votre propre compte.",
      lastAdmin: "Votre organisation doit garder au moins un administrateur actif.",
    },
    mfa: {
      title: "Validation en deux étapes",
      reset: "Réinitialiser la validation en deux étapes",
      effect:
        "Retire l'application d'authentification et les clés d'accès de l'utilisateur : il en configurera une nouvelle à sa prochaine connexion là où elle est exigée. Ses sessions restent ouvertes.",
      reason: "Motif (conservé dans le journal d'audit)",
      confirm: "Réinitialiser",
      done: "Validation en deux étapes réinitialisée.",
      self: "Réinitialisez votre propre validation en deux étapes depuis les paramètres de votre compte.",
      federated:
        "Cet utilisateur se connecte via le fournisseur d'identité de votre organisation : réinitialisez-la là-bas.",
    },
    invitation: {
      label: "Invitation",
      pending: "En attente, expire le {{date}}",
      expired: "Expirée le {{date}}",
      pendingShort: "Invitation en attente",
      expiredShort: "Invitation expirée",
      title: "Invitation",
      lead: "{{email}} n'a pas encore défini de mot de passe.",
      copy: "Copier le lien d'invitation",
      confirmTitle: "Créer un nouveau lien d'invitation ?",
      replaces:
        "Le nouveau lien remplace celui envoyé auparavant : l'ancien lien ne fonctionne plus, même dans l'e-mail.",
      once: "Il ne sert qu'une fois et expire au bout de 72 heures. Ne le partagez qu'avec {{email}}.",
      create: "Créer le lien",
      creating: "Création…",
      issuedTitle: "Nouveau lien d'invitation pour {{email}}",
      issuedBody:
        "Le lien envoyé auparavant ne fonctionne plus. Aucun e-mail n'a été envoyé : transmettez ce lien vous-même.",
      field: "Lien d'invitation",
      copyLink: "Copier",
      copied: "Copié",
      expires: "Expire le {{date}}",
      done: "Terminé",
      unsentTitle: "L'e-mail d'invitation n'a pas été envoyé",
      unsentBody:
        "{{email}} a maintenant un compte, mais l'e-mail n'a pas pu être remis. Copiez un lien d'invitation et transmettez-le vous-même.",
      unsentAskAdmin:
        "{{email}} a maintenant un compte, mais l'e-mail n'a pas pu être remis. Demandez à un administrateur de l'organisation de copier un lien d'invitation.",
      close: "Fermer",
      refused: {
        notPending:
          "{{email}} a déjà défini un mot de passe : il n'y a plus d'invitation à partager.",
        disabled:
          "Cet utilisateur est désactivé. Réactivez-le avant de partager un lien d'invitation.",
        federated:
          "Cet utilisateur se connecte via le fournisseur d'identité de votre organisation : aucun lien d'invitation n'est nécessaire.",
        throttled:
          "Trop de liens pour cet utilisateur au cours de la dernière heure : les liens précédents ne fonctionnent plus. Réessayez plus tard.",
        missing: "Le compte de connexion de cet utilisateur est introuvable. Contactez le support.",
        forbidden:
          "Seuls les administrateurs de l'organisation peuvent créer des liens d'invitation.",
        notFound: "Cet utilisateur n'existe plus.",
      },
    },
  },
  entities: {
    title: "Entités",
    hint: "L'arborescence de votre organisation : renommer, déplacer et supprimer des entités.",
    lead: "Les paramètres, les projets et les accès suivent cette arborescence. Les administrateurs d'entité modifient les entités sous la leur.",
    adminsOnly: "Seuls les administrateurs voient les entités.",
    root: "Organisation",
    actionsOf: "Actions pour {{name}}",
    create: "Ajouter une sous-entité",
    rename: "Renommer",
    move: "Déplacer",
    delete: "Supprimer",
    createTitle: "Ajouter une sous-entité à {{name}}",
    renameTitle: "Renommer {{name}}",
    moveTitle: "Déplacer {{name}}",
    moveBody:
      "Ses sous-entités la suivent. Les paramètres, la gouvernance et la résidence suivent alors le nouveau parent.",
    deleteTitle: "Supprimer {{name}} ?",
    deleteBody:
      "Seule une entité vide peut être supprimée : ni sous-entités, ni membres, ni projets, ni groupes, ni codes d'organisation, ni demandes de promotion en attente. Ses propres paramètres sont supprimés.",
    name: "Nom",
    newParent: "Nouveau parent",
    currentParent: "actuel",
    createConfirm: "Ajouter",
    renameConfirm: "Renommer",
    moveConfirm: "Déplacer",
    deleteConfirm: "Supprimer l'entité",
    refused: { notEmpty: "Cette entité n'est pas encore vide :" },
    usage: {
      children_one: "{{count}} sous-entité",
      children_other: "{{count}} sous-entités",
      members_one: "{{count}} membre",
      members_other: "{{count}} membres",
      projects_one: "{{count}} projet",
      projects_other: "{{count}} projets",
      groups_one: "{{count}} groupe",
      groups_other: "{{count}} groupes",
      orgCodes_one: "{{count}} code d'organisation",
      orgCodes_other: "{{count}} codes d'organisation",
      promotions_one: "{{count}} demande de promotion en attente",
      promotions_other: "{{count}} demandes de promotion en attente",
    },
  },
  adminSettings: {
    title: "Paramètres",
    hint: "Ce que l'assistant et les projets peuvent faire, par niveau, avec héritage et verrous.",
    lead: "Chaque niveau hérite de celui du dessus, sauf s'il fixe sa propre valeur. Un verrou empêche tous les niveaux inférieurs de la changer.",
    adminsOnly: "Seuls les administrateurs modifient les paramètres.",
    level: "Niveau",
    organisation: "Toute l'organisation",
    areas: {
      auth: "Connexion",
      directory: "Annuaire",
      answers: "Réponses",
      conversations: "Conversations",
      models: "Modèles",
      tools: "Outils",
      projects: "Projets",
      knowledge: "Connaissances",
      wiki: "Wiki",
      memory: "Mémoire",
      mcp: "Clients MCP",
    },
    mcpCeiling: {
      upTo: "{{levels}} peuvent être transmis",
      none: "Rien ne peut être transmis",
    },
    nativeClients: {
      any: "Toutes les applications",
      allowlist: "Uniquement les applications listées",
      summary_one: "Uniquement {{count}} application listée",
      summary_other: "Uniquement {{count}} applications listées",
      summaryNone: "Uniquement les applications listées (aucune pour l'instant)",
      empty: "Aucune application listée : seul l'outil en ligne de commande ESI peut se connecter.",
      addLabel:
        "Ajouter une application par son identifiant client (l'URL de son document de métadonnées)",
      add: "Ajouter",
      checking: "Vérification…",
      remove: "Retirer {{client}}",
      invalidId: "Saisissez une URL https, telle que l'application la publie.",
      unreadable:
        "Les métadonnées de cette application n'ont pas pu être lues. Vérifiez l'identifiant client.",
      alwaysAllowed: "L'outil en ligne de commande ESI est toujours autorisé.",
      firstParty: "L'outil en ligne de commande ESI est toujours autorisé.",
    },
    mfa: { optional: "Facultatif", required: "Obligatoire" },
    modelRoles: { chat: "Équilibré", reasoning: "Approfondi", fast: "Rapide" },
    hours_one: "{{count}} heure",
    hours_other: "{{count}} heures",
    megabytes_one: "{{count}} Mo",
    megabytes_other: "{{count}} Mo",
    usd: "{{count}} USD",
    percent: "{{count}} %",
    operatorBudget: "Budget de l'opérateur",
    instanceResidency: "Fixé pour ce déploiement : les modèles mondiaux n'y sont pas disponibles",
    lockedHere: "Verrouillé pour les niveaux inférieurs",
    egress: {
      title: "Outils sortants",
      readers: "Comme ses lecteurs (par défaut)",
      project: "Comme les fichiers lisibles par toute la conversation",
      entity: "Comme les fichiers réservés à l\u{2019}entité de l\u{2019}auteur",
      named: "Comme les fichiers des seules personnes désignées",
      confirmLevel:
        "La recherche web et la lecture de pages web accepteront le contenu des fichiers {{level}} comme si ses lecteurs étaient {{audience}}, et l\u{2019}inspection des arguments cessera de bloquer leurs copies mot pour mot.",
      confirmRisk:
        "Des personnes qui ne peuvent pas lire ces fichiers pourront voir ce qui est envoyé.",
      confirm: "Élargir et enregistrer",
      audience: { project: "le projet", entity: "l\u{2019}entité" },
    },
    levels: {
      howItWorks:
        "Les niveaux vont du moins sensible au plus sensible. Un contenu issu de plusieurs niveaux prend le plus sensible.",
      listLabel: "Niveaux de classification, du moins sensible au plus sensible",
      leastSensitive: "Moins sensible",
      mostSensitive: "Plus sensible",
      files_one: "{{count}} fichier",
      files_other: "{{count}} fichiers",
      moveUp: "Monter {{level}}",
      moveDown: "Descendre {{level}}",
      details: "Détails de {{level}}",
      unnamed: "Niveau sans nom",
      moved: "{{level}} déplacé en position {{position}} sur {{total}}",
      add: "Ajouter un niveau",
      newName: "Nouveau niveau",
      added: "{{level}} ajouté en position {{position}} sur {{total}}",
      remove: "Supprimer le niveau",
      removed: "{{level}} supprimé.",
      undo: "Annuler",
      restored: "{{level}} rétabli en position {{position}}",
      usedBy: "Utilisé par {{uses}}.",
      usesFiles_one: "{{count}} fichier",
      usesFiles_other: "{{count}} fichiers",
      usesDefaults_one: "{{count}} classification par défaut",
      usesDefaults_other: "{{count}} classifications par défaut",
      nextStep: {
        files: "Reclassez ses fichiers avant de le supprimer.",
        defaults: "Changez la classification par défaut avant de le supprimer.",
        both: "Reclassez les fichiers et changez la classification par défaut avant de le supprimer.",
      },
      fields: {
        name: "Nom",
        readers: "Qui peut lire",
        download: "Téléchargements",
        downloadLabel: "Les personnes qui peuvent lire peuvent télécharger",
      },
      readersOption: {
        project: "Toute personne qui peut lire la conversation ou le projet",
        entity: "Entités, groupes ou personnes désignés, jamais tout le projet",
        named: "Seulement les personnes ou groupes désignés sur le fichier",
      },
      inPractice: "En pratique",
      whatTheyMean: "Ce que signifient ces niveaux",
      projectLevels: "Niveaux de classification des fichiers de ce projet",
      notInScheme: "Ne fait plus partie des niveaux de classification.",
      problem_one: "{{count}} problème",
      problem_other: "{{count}} problèmes",
      fixToSave_one: "Corrigez {{count}} problème pour enregistrer",
      fixToSave_other: "Corrigez {{count}} problèmes pour enregistrer",
      problems: {
        nameEmpty: "Donnez un nom à ce niveau.",
        nameTooLong: "Le nom ne doit pas dépasser 60 caractères.",
        nameDuplicate: "Un autre niveau porte déjà ce nom.",
        egressStricter:
          "Plus strict que ses lecteurs : les outils sortants ne peuvent qu\u{2019}élargir un niveau.",
        empty: "Gardez au moins un niveau.",
      },
      reviewAndSave: "Vérifier et enregistrer",
      reviewTitle: "Avant d\u{2019}enregistrer",
      consequence: {
        name: "Affiché sur les fichiers et dans les réponses.",
        readers: {
          project:
            "Toute personne qui peut lire le projet ou la conversation peut lire ces fichiers.",
          entity:
            "Jamais partagés avec tout le projet : seuls les entités, groupes ou personnes désignés sur chaque fichier peuvent le lire (dans les conversations, l\u{2019}entité de l\u{2019}auteur).",
          named: "Seuls les groupes et les personnes désignés sur un fichier peuvent le lire.",
        },
        download: {
          on: "Les lecteurs peuvent télécharger ces fichiers.",
          off: "Les lecteurs peuvent ouvrir ces fichiers dans l\u{2019}application, mais pas les télécharger.",
        },
        outbound: {
          readers: {
            allow:
              "Après la lecture de ces fichiers dans une conversation, la recherche web et la lecture de pages web s\u{2019}exécutent sans demander.",
            confirm:
              "Après la lecture de ces fichiers dans une conversation, la recherche web et la lecture de pages web demandent d\u{2019}abord.",
            block:
              "Après la lecture de ces fichiers dans une conversation, la recherche web et la lecture de pages web sont bloquées.",
          },
          treated: {
            project: {
              allow:
                "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers du projet : avec votre politique, elles s\u{2019}exécutent sans demander.",
              confirm:
                "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers du projet : avec votre politique, elles demandent d\u{2019}abord.",
              block:
                "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers du projet : avec votre politique, elles sont bloquées.",
            },
            entity: {
              allow:
                "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers jamais partagés avec tout le projet : avec votre politique, elles s\u{2019}exécutent sans demander.",
              confirm:
                "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers jamais partagés avec tout le projet : avec votre politique, elles demandent d\u{2019}abord.",
              block:
                "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers jamais partagés avec tout le projet : avec votre politique, elles sont bloquées.",
            },
            named: {
              allow:
                "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers des seules personnes désignées : avec votre politique, elles s\u{2019}exécutent sans demander.",
              confirm:
                "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers des seules personnes désignées : avec votre politique, elles demandent d\u{2019}abord.",
              block:
                "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers des seules personnes désignées : avec votre politique, elles sont bloquées.",
            },
          },
          unknown:
            "La recherche web et la lecture de pages web traitent ces fichiers comme leurs lecteurs, selon la politique de votre organisation.",
          treatedUnknown: {
            project:
              "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers du projet.",
            entity:
              "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers jamais partagés avec tout le projet.",
            named:
              "La recherche web et la lecture de pages web traitent ces fichiers comme des fichiers des seules personnes désignées.",
          },
        },
      },
      impact: {
        declassification: {
          on: "Les propriétaires et les responsables qui peuvent lire ces fichiers peuvent quand même publier plus largement un contenu qui en est issu.",
          off: "Un contenu issu de ces fichiers ne peut jamais être publié plus largement que ses lecteurs.",
        },
        default:
          "Les nouveaux fichiers reçoivent ce niveau, sauf si l\u{2019}auteur en choisit un autre.",
        conversationFiles:
          "Les fichiers ajoutés aux conversations hors projet reçoivent ce niveau.",
      },
      summary: {
        readers: {
          project: "Lecteurs du projet",
          entity: "Entités désignées",
          named: "Personnes désignées",
        },
        download: "Téléchargement",
        noDownload: "Pas de téléchargement",
        outbound: {
          readers: "Sortants comme ses lecteurs",
          project: "Sortants comme le projet",
          entity: "Sortants comme l\u{2019}entité",
          named: "Sortants comme les personnes désignées",
        },
        default: "Par défaut pour les nouveaux fichiers",
        conversationFiles: "Fichiers des conversations",
      },
      review: {
        added: "{{level}} est ajouté en position {{position}} sur {{total}}.",
        renamed: "{{from}} est renommé {{to}}.",
        renamedFiles_one: "Son fichier affiche le nouveau nom.",
        renamedFiles_other: "Ses {{count}} fichiers affichent le nouveau nom.",
        moved: "{{level}} passe au-dessus de {{below}}.",
        movedFiles_one:
          "Leur fichier garde son niveau ; un contenu issu de ces niveaux sera désormais classé {{level}}, et abaisser un fichier de {{level}} à {{lower}} devient un élargissement que seuls les responsables peuvent faire.",
        movedFiles_other:
          "Leurs {{count}} fichiers gardent leur niveau ; un contenu issu de ces niveaux sera désormais classé {{level}}, et abaisser un fichier de {{level}} à {{lower}} devient un élargissement que seuls les responsables peuvent faire.",
        readersTightened: {
          project: "{{level}} autorise désormais les lecteurs du projet.",
          entity:
            "{{level}} n\u{2019}est plus partagé avec des projets entiers : seulement avec les entités, groupes ou personnes désignés sur chaque fichier.",
          named:
            "{{level}} n\u{2019}autorise désormais que les personnes et groupes désignés sur chaque fichier.",
        },
        keepReaders_one:
          "Son fichier garde ses lecteurs actuels jusqu\u{2019}à ce que quelqu\u{2019}un les change ; les fichiers ajoutés aux conversations suivent la nouvelle règle aussitôt.",
        keepReaders_other:
          "Ses {{count}} fichiers gardent leurs lecteurs actuels jusqu\u{2019}à ce que quelqu\u{2019}un les change ; les fichiers ajoutés aux conversations suivent la nouvelle règle aussitôt.",
        readersLoosened: {
          project:
            "Les fichiers {{level}} peuvent désormais être lus par toute personne qui peut lire le projet ou la conversation.",
          entity:
            "Les fichiers {{level}} peuvent désormais être lus par les entités, groupes ou personnes désignés, jamais par tout le projet.",
          named:
            "Les fichiers {{level}} ne peuvent désormais être lus que par les personnes et groupes désignés.",
        },
        download: {
          on: "Les personnes qui peuvent lire les fichiers {{level}} peuvent désormais les télécharger.",
          off: "Les personnes qui peuvent lire les fichiers {{level}} ne peuvent plus les télécharger.",
        },
        egress: {
          project:
            "La recherche web et la lecture de pages web traitent désormais les fichiers {{level}} comme des fichiers du projet.",
          entity:
            "La recherche web et la lecture de pages web traitent désormais les fichiers {{level}} comme des fichiers jamais partagés avec tout le projet.",
          named:
            "La recherche web et la lecture de pages web traitent désormais les fichiers {{level}} comme des fichiers des seules personnes désignées.",
        },
        removed: "{{level}} est supprimé.",
      },
    },
    unavailable: "Sans effet : non disponible sur ce déploiement.",
    providers: {
      agentcore: "Amazon Bedrock AgentCore",
      lambda: "Lecteur de pages ESI, dans cette Région",
    },
    navLabel: "Pages des paramètres",
    groups: {
      general: {
        label: "Général",
        purpose:
          "L'usage des sources dans les réponses, les conversations hors projet, et qui peut créer et rejoindre des projets.",
      },
      security: {
        label: "Connexion et sécurité",
        purpose:
          "Qui obtient un compte à la première connexion, second facteur, applications que l'on peut connecter, connexion par appareil et groupes du fournisseur d'identité.",
      },
      models: {
        label: "Modèles et résidence",
        purpose:
          "Où les modèles s'exécutent, les niveaux d'effort permis et la façon de choisir un modèle.",
      },
      knowledge: {
        label: "Connaissances et mémoire",
        purpose:
          "Classification des fichiers, lecture des scans et des images, rédaction du wiki et ce que l'assistant retient.",
      },
      limits: {
        label: "Usage et limites",
        purpose:
          "Le plus gros fichier qu'un projet accepte et le nombre de pages que les scans peuvent lire.",
      },
      integrations: {
        label: "Intégrations",
        purpose: "Les services externes que l'assistant peut utiliser, et leurs limites.",
      },
      clients: {
        label: "Clients externes",
        purpose:
          "Si les clients MCP comme Claude Code accèdent à vos connaissances, ce qu'ils peuvent enregistrer et recevoir, et les outils qu'ils peuvent utiliser.",
      },
    },
    sections: {
      answers: "Réponses et conversations",
      projects: "Projets",
      signIn: "Connexion",
      mcpAccess: "Accès",
      mcpToolGroups: "Groupes d'outils",
      directory: "Annuaire",
      esiTeam: "Partage avec l'équipe ESI",
      models: "Modèles",
      classification: "Classification",
      files: "Fichiers scannés et images",
      wiki: "Wiki",
      memory: "Mémoire",
      uploads: "Fichiers et scans",
      webSearch: "Recherche sur le web",
      webFetch: "Lecture de pages web",
      codeExecution: "Exécution de code",
      egress: "Appels d'outils sortants",
      other: "Autres",
    },
    setHereCount_one: "{{count}} fixé à ce niveau",
    setHereCount_other: "{{count}} fixés à ce niveau",
    related: "Voir aussi :",
    summary: {
      setHere: "Fixés à ce niveau :",
      lockedAbove: "Verrouillés au-dessus :",
      allDefault_one:
        "Rien n'est fixé pour l'organisation : ce paramètre suit les valeurs par défaut.",
      allDefault_other:
        "Rien n'est fixé pour l'organisation : les {{count}} paramètres suivent les valeurs par défaut.",
      allInherited_one: "Rien n'est fixé à ce niveau : ce paramètre est hérité.",
      allInherited_other: "Rien n'est fixé à ce niveau : les {{count}} paramètres sont hérités.",
    },
    marks: { overridden: "Redéfini à ce niveau" },
    search: {
      label: "Rechercher un paramètre",
      placeholder: "Nom ou sujet, par ex. mémoire",
      results: "Paramètres correspondants",
      none: "Aucun paramètre ne correspond.",
      found_one: "{{count}} paramètre trouvé. Utilisez les flèches, puis Entrée.",
      found_other: "{{count}} paramètres trouvés. Utilisez les flèches, puis Entrée.",
    },
  },
  errors: {
    legacy: {
      uploadMismatch: "Le fichier envoyé ne correspond pas à la taille ou au type déclaré",
      readersRequired: "Cette classification nécessite de nommer les lecteurs",
    },
    codes: {
      upload_size_limit: "Les fichiers sont limités à {{maxMb}} Mo dans ce projet",
      unknown_models: "Modèles inconnus : {{models}}",
      unknown_setting: "Paramètre inconnu : « {{key}} »",
      invalid_setting:
        "Valeur invalide pour « {{key}} ». Vérifiez les contraintes de ce paramètre.",
      rule_document_usage: "Cette règle donne accès à {{count}} document(s)",
      rule_project_usage: "Cette règle accorde un rôle dans {{count}} projet(s)",
      attribute_rule_usage: "{{count}} règle(s) utilisent {{key}}",
      attribute_value_rule_usage: "{{count}} règle(s) utilisent {{key}} = {{value}}",
      document_reader_required: "Vous devez faire partie des lecteurs du document",
      search_length: "Une recherche doit contenir entre {{min}} et {{max}} caractères",
      operator: "Les opérateurs de l'instance ne peuvent pas autoriser les clients natifs",
      inactive: "Le compte ou son organisation n'est pas actif",
      mfa_required: "La vérification en deux étapes est requise pour cette organisation",
      device_grant_disabled:
        "Votre organisation n'autorise pas la connexion avec un code d'appareil",
      client_outdated: "Rechargez ESI pour continuer avec la dernière version.",
      cannot_read_project: "Cette personne ne peut pas lire le projet.",
      cannot_read_sources: "Cette personne ne peut pas lire les sources.",
      self: "Vous avez déjà accès à votre propre conversation.",
      cannot_receive: "Cette personne ne peut pas recevoir la conversation.",
      quota: "Votre limite d'utilisation est atteinte. Réessayez plus tard.",
      auth_unavailable: "La connexion est temporairement indisponible. Réessayez plus tard.",
      client_not_allowed: "Cette application n'est pas autorisée à se connecter.",
      database_unavailable: "Le service est brièvement indisponible. Réessayez dans un instant.",
      flow_expired: "La connexion a expiré. Recommencez.",
      forbidden_origin: "L'origine de cette demande n'est pas autorisée.",
      internal_error: "Le serveur a rencontré un problème. Réessayez plus tard.",
      invalid_code: "Ce code est invalide. Vérifiez-le et réessayez.",
      invalid_credentials: "Vérifiez vos identifiants et réessayez.",
      invalid_link: "Ce lien est invalide ou a expiré.",
      invalid_report: "Ce rapport n'a pas pu être accepté.",
      method_not_allowed: "Cette action n'est pas prise en charge ici.",
      model_unavailable: "L'assistant est temporairement indisponible. Réessayez plus tard.",
      payload_too_large: "Cette demande est trop volumineuse. Réduisez-la et réessayez.",
      schema_not_ready: "Le serveur est en cours de mise à jour. Réessayez dans un instant.",
      unavailable: "Ce service est temporairement indisponible.",
      unsupported_media_type: "Ce format de fichier n'est pas pris en charge.",
      weak_password: "Choisissez un mot de passe plus sûr.",
      password_already_set:
        "Ce compte a déjà un mot de passe. Connectez-vous, ou réinitialisez votre mot de passe.",
      snapshot_too_large: "L'instantané de l'annuaire est trop volumineux.",
      pull_failed: "L'annuaire n'a pas pu être joint. Relancez la synchronisation.",
      invalid_records: "L'annuaire a renvoyé des données invalides.",
      conflict: "Cet élément a changé. Rechargez la page et réessayez.",
      project_frozen: "Ce projet est en lecture seule.",
      personal_project: "Seul le propriétaire peut gérer ce projet personnel.",
      turn_in_progress: "Une réponse est en cours. Réessayez une fois la réponse terminée.",
      conversation_changed: "La conversation a changé. Rechargez la page et réessayez.",
      upload_already_confirmed: "Cet envoi est déjà confirmé.",
      upload_missing: "Envoyez le fichier avant de confirmer l'envoi.",
      change_set_closed: "Ce lot de modifications n'est plus ouvert.",
      change_set_changed:
        "Ce lot de modifications a changé. Rechargez-le et vérifiez les changements.",
      item_stale:
        "La page a changé. Mettez cette proposition à jour à partir de la version actuelle.",
      too_many_open_change_sets:
        "Ce projet contient trop de lots de modifications ouverts. Publiez-en ou abandonnez-en un d'abord.",
      page_already_in_change_set:
        "Cette page fait déjà l'objet d'une proposition dans ce lot de modifications.",
      proposal_decided: "Cette proposition a déjà été traitée.",
      proposal_expired: "Cette proposition a expiré.",
      version_current: "C'est déjà la version actuelle de la page.",
      cannot_review_hidden_items:
        "Certains éléments ne vous sont pas accessibles. Demandez à une personne qui peut tous les lire de publier ce lot de modifications.",
      self_publish_mcp_draft:
        "Votre client MCP a proposé cette modification : une autre personne autorisée à publier doit la publier.",
      draft_stale: "La page a changé. Créez un nouveau brouillon.",
      draft_not_open: "Ce brouillon n'est plus ouvert.",
      draft_not_stale: "Ce brouillon est déjà à jour.",
      draft_changed: "Le brouillon a changé. Rechargez la page et réessayez.",
      wiki_title_taken: "Une autre page porte déjà ce titre.",
      already_withdrawn: "Cette déclassification a déjà été retirée.",
      export_not_ready: "Cet export n'est pas prêt. Réessayez plus tard.",
      export_stale: "Les accès ont changé. Créez un nouvel export.",
      reauth_required: "Reconnectez-vous avant de continuer.",
      last_factor: "Votre compte doit conserver un second facteur.",
      totp_required: "Configurez d'abord votre application d'authentification.",
      entity_not_empty: "Déplacez le contenu de cette entité avant de la supprimer.",
      self_disable: "Vous ne pouvez pas désactiver votre propre compte.",
      last_tenant_admin: "Conservez au moins un administrateur actif dans l'organisation.",
      domain_not_bound:
        "Seul un domaine vérifié et associé à un fournisseur d'identité actif a un mode de connexion.",
      last_sign_in_method:
        "Aucun administrateur actif de l'organisation ne pourrait plus se connecter.",
      idp_inactive: "Ce fournisseur de connexion a été retiré de votre organisation.",
      confirmation_mismatch:
        "La confirmation ne correspond pas. Saisissez-la exactement comme indiqué.",
      approval_expired: "Cette approbation a expiré. Demandez-la à nouveau.",
      approval_decided: "Cette approbation a déjà été traitée.",
      approval_mismatch: "L'action a changé. Demandez une nouvelle approbation.",
      seat_limit: "Votre organisation a atteint sa limite d'utilisateurs.",
      storage_limit: "Votre organisation a atteint sa limite de stockage.",
      plan_in_use: "Déplacez les organisations utilisant cette offre avant de la supprimer.",
      self_mfa_reset: "Réinitialisez vos seconds facteurs dans les paramètres du compte.",
      invitation_not_pending:
        "Cet utilisateur a déjà défini un mot de passe : il n'y a plus d'invitation à partager.",
      user_disabled: "Cet utilisateur est désactivé.",
      admin_email_in_use:
        "Cet e-mail a déjà un compte dans une autre organisation. Utilisez un autre e-mail.",
      account_unavailable:
        "Nous ne pouvons pas créer de compte avec cet e-mail. Contactez votre administrateur.",
      invitation_pending:
        "Cet administrateur ne s'est pas encore connecté : il n'y a rien à réinitialiser. Renvoyez plutôt son invitation.",
      tenant_not_active:
        "Seuls les administrateurs d'une organisation active peuvent être invités.",
      resolution_running: "L’agent traite déjà les commentaires de cet ensemble de modifications.",
      item_changed_since:
        "Cette page a changé depuis la révision de l’agent : modifiez-la à la main.",
      account_missing:
        "Le compte de connexion de cet utilisateur est introuvable. Contactez le support.",
      not_standalone: "Seule une conversation hors projet peut être déplacée.",
      move_changed: "Les effets du déplacement ont changé. Vérifiez-les à nouveau.",
      residency_narrower:
        "Choisissez un projet autorisant la résidence des modèles de cette conversation.",
      access_request_pending: "Une demande d'accès est déjà en attente.",
      access_request_decided: "Cette demande d'accès a déjà été traitée ou a expiré.",
      access_request_stale: "Les accès ont changé. Vérifiez à nouveau la demande.",
      access_held: "Cette modification nécessite l'approbation du propriétaire du projet.",
      memory_kind: "Cette mémoire ne peut pas être modifiée ici.",
      memory_queue_full: "La file des mémoires est pleine. Réessayez plus tard.",
      memory_duplicate: "Cette mémoire existe déjà.",
      memory_user_active: "Désactivez l'utilisateur avant d'effacer ses mémoires.",
      memory_count_changed: "Le nombre de mémoires a changé. Vérifiez-le à nouveau.",
      implications_changed: "Les conditions des modèles ont changé. Vérifiez-les à nouveau.",
      name_taken: "Ce nom est déjà utilisé.",
      folder_limit: "Cette section a atteint sa limite de dossiers.",
      tasks_disabled: "Les tâches sont désactivées pour ce projet.",
      link_limit: "Cette tâche a atteint sa limite de liens.",
      attachments_disabled: "Les pièces jointes sont désactivées ici.",
      attachment_limit: "Cette conversation a atteint sa limite de pièces jointes.",
      attachment_not_ready: "Cette pièce jointe n'est pas encore prête.",
      attachment_sent:
        "Ce fichier a déjà servi dans la conversation : son origine ne peut plus changer.",
      origin_fixed: "L'origine de ce fichier a été fixée lors de son ajout.",
      private_level_unavailable:
        "Aucun niveau de classification privé n'existe pour votre organisation. Demandez à un administrateur.",
      classification_not_private:
        "Les fichiers d'une conversation hors projet sont privés ; cette classification ne l'est pas.",
      egress_floor_stricter_than_readers:
        "Les outils sortants ne peuvent pas traiter un niveau plus strictement que ses lecteurs. Choisissez une option plus large.",
      egress_floor_not_editable:
        "Ce serveur ne permet pas encore de régler le traitement d'un niveau par les outils sortants.",
      logo_invalid: "Cette image ne peut pas servir de logo. Utilisez une image PNG ou WebP fixe.",
      not_openable: "Ce fichier ne peut pas être ouvert ici. Téléchargez-le.",
      too_large_to_view: "Ce fichier est trop volumineux pour être affiché ici. Téléchargez-le.",
      is_file: "Cet artefact est un fichier : téléchargez-le pour l'ouvrir.",
      anchor_not_found: "Ce passage ne figure plus dans la page : sélectionnez-le à nouveau.",
      wiki_item_invalid:
        "Cette page ne peut pas être enregistrée telle quelle : vérifiez son titre et ses premières lignes.",
      federated_account:
        "Cet utilisateur se connecte via le fournisseur d'identité de votre organisation : aucun lien d'invitation n'est nécessaire.",
      archived: "Désarchivez la conversation avant de continuer.",
      not_in_project: "Déplacez la conversation dans un projet avant de la partager.",
      already_added: "Cet artefact figure déjà dans les connaissances du projet.",
      not_newer: "Aucune version plus récente n'est disponible.",
      version_mismatch: "Ceci a changé depuis son ouverture. Rechargez, puis réessayez.",
      folder_not_empty: "Ce dossier n'est pas vide.",
      change_requested:
        "Une demande de modification ouverte bloque cette action. Résolvez-la d’abord.",
      change_set_working:
        "Son auteur n'a pas encore soumis ce lot de modifications à la relecture.",
      folder_missing:
        "Un dossier de ce lot n'existe plus. Rejetez cet élément pour publier le reste.",
      standalone_disabled: "Les conversations hors projet sont désactivées.",
      fingerprint_linked:
        "Cette empreinte est liée à un autre groupe. Détachez-la d'abord de ce groupe.",
      assist_no_reports:
        "Tous les signalements de ce groupe sont tenus à l'écart de l'assistant par leur organisation.",
      suggestion_decided: "Cette suggestion a déjà été traitée.",
      issue_exists: "Ce groupe a déjà un ticket.",
      preview_changed: "Le ticket a changé depuis l'aperçu. Affichez-le à nouveau.",
      model_connection_probe_required: "Testez la connexion avec succès avant de l'activer.",
      model_connection_logging_confirm:
        "Ce compte journalise les appels aux modèles : confirmez pour activer la connexion.",
      model_connection_same_account:
        "Une connexion vise un autre compte AWS : le compte de ce déploiement est toujours disponible.",
      model_account_same_account:
        "Le compte de ce déploiement est toujours disponible et n'est jamais ajouté à la liste autorisée.",
      model_account_capacity: "La liste des comptes autorisés est pleine pour ce déploiement.",
      model_provider_disabled:
        "Bedrock est désactivé pour les connexions de l'instance : activez-le d'abord sous Fournisseurs.",
      model_account_same_binding: "Le compte a déjà ce rattachement.",
      model_account_tenant_bound:
        "Ce compte est rattaché à une organisation : seules ses connexions peuvent l'utiliser.",
      model_account_tenant_unavailable:
        "Cette organisation est en cours de suppression : elle ne peut pas détenir de compte de modèles.",
      issue_text_flagged:
        "Le texte du ticket semble copié d'un signalement. Modifiez le titre ou le résumé.",
      reason_text_flagged:
        "La raison contient les mots d'un signalement, une adresse, un identifiant ou le nom d'un tenant. Modifiez-la.",
      tracker_not_configured: "GitLab n'est pas encore configuré sur cette instance.",
      feedback_disabled: "Votre organisation a désactivé les retours.",
      memory_disabled: "La mémoire est désactivée pour votre compte.",
      project_memory_disabled: "La mémoire est désactivée pour ce projet.",
      wiki_trust_level:
        "Le niveau de confiance de ce projet ne vous permet pas d’approuver ni de publier ces modifications.",
      wiki_suggestions_disabled: "Les suggestions wiki sont désactivées pour ce projet.",
      nothing_to_declassify: "Il n'y a rien à déclassifier.",
      empty_reason: "Indiquez un motif avant de continuer.",
      readers_below_floor: "Les lecteurs sélectionnés dépassent les limites de la classification.",
      untested_lab_unacknowledged:
        "Non testé par ESI : confirmez l’avertissement avant d’ajouter ce laboratoire.",
      model_pin_disallowed:
        "Cette version n'est pas autorisée ici : choisissez-en une que ce périmètre peut utiliser.",
      model_roles_uncovered: "Autorisez un modèle utilisable pour chaque rôle requis.",
      too_deep: "Ce dossier serait imbriqué trop profondément.",
      cycle: "Un groupe ou un dossier ne peut pas se contenir lui-même.",
      wrong_section: "Choisissez un dossier dans la même section.",
      not_a_member: "Choisissez un membre de ce projet.",
      origin_derived: "L'origine de ce contenu dérivé ne peut pas être modifiée.",
      model_budget: "Le budget des modèles de l'organisation est atteint. Réessayez plus tard.",
      user_budget: "Votre budget d'utilisation est atteint. Réessayez plus tard.",
      triage_budget_reached: "Budget quotidien de tri atteint. Réessayez demain.",
      throttled: "Trop de demandes. Patientez un instant et réessayez.",
      invalid: "Vérifiez les informations et réessayez.",
      unprocessable: "Cette modification n'est pas autorisée. Vérifiez les informations.",
      forbidden: "Vous n'avez pas l'autorisation d'effectuer cette action.",
      not_found: "Cet élément n'existe pas ou vous n'y avez pas accès.",
      unauthorized: "Votre session a expiré. Reconnectez-vous.",
      unsupported: "Cette fonctionnalité n'est pas encore disponible sur ce serveur.",
      unknown_user: "Cet utilisateur est inconnu de la source d'annuaire.",
      unknown_group: "Ce groupe est inconnu de la source d'annuaire.",
      unknown_entity: "Cette entité est inconnue.",
      depth_exceeded: "La limite d'imbrication des groupes est atteinte.",
      invalid_attribute: "Un attribut contient une valeur invalide.",
      no_adapter: "Cette source d'annuaire n'est pas encore connectée.",
      credential_missing: "La source n'a pas de clé utilisable. Remplacez sa clé.",
      credential_refused:
        "La clé de la source n'a pas pu être lue : elle est absente, ou appartient à une autre organisation. Remplacez-la.",
      token_refused:
        "Google a refusé la clé du compte de service. Remplacez la clé ou vérifiez le compte.",
      missing_privilege:
        "Il manque un droit de lecture au compte de service. Testez la source pour savoir lequel.",
      scope_not_found:
        "Le groupe ou l'unité organisationnelle de provisionnement est introuvable dans Google Workspace.",
    },
    network: "Le serveur est injoignable. Vérifiez votre connexion et réessayez.",
    unexpected: "Une erreur s'est produite. Réessayez.",
    sessionEnded: "Votre session a expiré. Reconnectez-vous.",
    forbidden: "Vous n'avez pas l'autorisation de faire cela.",
    notFound: "Cet élément n'existe pas, ou vous n'y avez pas accès.",
    notImplemented: "Cette fonction n'est pas encore disponible sur ce serveur.",
    server: "Le serveur a rencontré un problème. Réessayez dans un instant.",
    refused: "La demande a été refusée. Vérifiez les informations et réessayez.",
    signInAgain: "Se reconnecter",
  },
  buildRefresh: {
    available: "Une nouvelle version d'ESI est disponible",
    required: "Certaines actions peuvent échouer tant que la page n'est pas rechargée.",
    reload: "Recharger",
    later: "Plus tard",
  },
  session: {
    ended: {
      expired: "Votre session a expiré.",
      signed_out: "Vous vous êtes déconnecté d'ESI dans un autre onglet ou une autre fenêtre.",
      revoked:
        "Cette session a été fermée depuis les paramètres de votre compte ou par votre organisation.",
    },
    unsaved:
      "Reconnectez-vous pour continuer. Copiez d'abord ce que vous n'avez pas envoyé : la connexion recharge la page.",
    signInAgain: "Se reconnecter",
    copyUnsaved: "Copier le texte non enregistré",
    idpRefresh: {
      lead: "Actualisez votre connexion pour garder l'accès que vous donnent les groupes de votre entreprise.",
      unsaved:
        "Terminez ou copiez d'abord votre travail en cours : l'actualisation recharge la page.",
      action: "Actualiser la connexion",
      later: "Plus tard",
    },
    copiedOk: "Copié. Collez-le en lieu sûr avant de vous reconnecter.",
    copyFailed: "Copie impossible. Sélectionnez votre texte et copiez-le vous-même.",
    dismiss: "Masquer",
  },
  signIn: {
    title: "Se connecter",
    lead: "Utilisez votre adresse professionnelle. Si votre organisation a sa propre connexion, vous la poursuivrez là-bas.",
    errors: {
      expired: "La connexion a pris trop de temps et a expiré. Recommencez.",
      failed: "La connexion a échoué. Vérifiez votre adresse e-mail et réessayez.",
      no_account:
        "Aucun compte ESI ne correspond à cette connexion. Demandez un accès à votre administrateur.",
      seat_limit:
        "Votre organisation n'a plus de place disponible dans ESI. Demandez à votre administrateur d'en libérer ou d'en ajouter.",
      invalid: "L'adresse, le mot de passe ou le code est incorrect. Vérifiez-les et réessayez.",
      throttled_one: "Trop de tentatives. Réessayez dans {{count}} minute.",
      throttled_other: "Trop de tentatives. Réessayez dans {{count}} minutes.",
      throttledLater: "Trop de tentatives. Réessayez plus tard.",
      flowExpired:
        "La connexion a pris trop de temps et a expiré. Saisissez à nouveau votre adresse.",
      invalidCode:
        "Ce code n'est pas valide. Vérifiez votre application d'authentification et réessayez.",
      passkeyCancelled:
        "La clé d'accès n'a pas été utilisée. Réessayez, ou connectez-vous avec votre mot de passe.",
      unknown:
        "La connexion n'a pas abouti. Recommencez, ou contactez votre administrateur si le problème persiste.",
    },
    notice: {
      expired: "Votre session a expiré. Reconnectez-vous pour reprendre là où vous étiez.",
      signed_out: "Vous vous êtes déconnecté. Reconnectez-vous pour continuer.",
      revoked: "Cette session a été fermée. Reconnectez-vous pour continuer.",
    },
    email: "Adresse e-mail professionnelle",
    submit: "Continuer",
    password: "Mot de passe",
    forgot: "Mot de passe oublié ?",
    signIn: "Se connecter",
    usePasskey: "Utiliser une clé d'accès",
    signingInAs: "Connexion en tant que",
    changeEmail: "Modifier",
    totpLead: "Saisissez le code à 6 chiffres de votre application d'authentification.",
    verify: "Vérifier",
    newPasswordLead: "Choisissez un mot de passe pour votre compte avant de continuer.",
    passkeyWaiting: "Suivez les instructions de votre navigateur pour utiliser votre clé d'accès.",
    or: "ou",
    totpHelp:
      "Plus d'accès à votre application ? Demandez à votre administrateur de réinitialiser la validation en deux étapes.",
    choice: {
      lead: "Votre organisation autorise aussi un mot de passe ou une clé d'accès ESI sur ce domaine.",
      label: "Façons de se connecter en tant que {{email}}",
      continueWith: "Continuer avec {{name}}",
      credentials: "Utiliser un mot de passe ou une clé d'accès",
    },
    steps: {
      password: "Saisissez votre mot de passe",
      passkey: "Utilisez votre clé d'accès",
      newPassword: "Choisissez un mot de passe",
      redirect: "Vers la connexion de votre organisation",
      redirectLead: "Votre organisation gère elle-même la connexion. Vous y accédez par :",
      choice: "Choisissez comment vous connecter",
    },
    language: "Langue",
    onEsi: "Connexion à ESI",
    enrol: {
      title: "Configurer la validation en deux étapes",
      lead: "Votre organisation exige une seconde étape à la connexion. Configurez-la maintenant pour continuer.",
      totp: "Utiliser une application d'authentification",
      passkey: "Utiliser une clé d'accès",
      totpHint:
        "Un code à 6 chiffres d'une application sur votre téléphone, comme Microsoft ou Google Authenticator.",
      passkeyHint:
        "L'empreinte, le visage ou le verrouillage d'écran de votre appareil. Rien à saisir.",
      other: "Choisir une autre méthode",
    },
  },
  console: {
    badge: "Console",
    signIn: {
      title: "Connexion opérateur",
      lead: "Réservée aux opérateurs de cette instance ESI. Connectez-vous avec votre compte opérateur.",
      email: "E-mail",
      totpHelp:
        "Plus d’accès à votre application d’authentification ? Un administrateur AWS de cette instance peut la réinitialiser.",
      enrolLead:
        "Les opérateurs confirment toujours leur connexion par une deuxième étape. Configurez une application d’authentification pour continuer.",
      unavailable:
        "La connexion opérateur n’est pas encore disponible. Réessayez dans quelques minutes.",
    },
    nav: {
      label: "Console",
      tenants: "Organisations",
      plans: "Offres",
      modelConnections: "Connexions aux modèles",
      policies: "Règles",
      support: "Accès support",
      reports: "Utilisation",
      feedback: "Retours",
      operators: "Opérateurs",
      profile: "Profil",
      groups: { governance: "Gouvernance", access: "Accès" },
    },
    operator: {
      menu: "Menu opérateur",
      role: "Opérateur de l'instance",
      profile: "Profil",
    },
    profile: {
      title: "Profil",
      lead: "Qui vous êtes sur cette console et comment vous vous connectez.",
      identity: "Identité",
      name: "Nom",
      nameHint: "Visible des autres opérateurs dans les sessions et l'activité.",
      nameInvalid: "Utilisez des lettres ou des chiffres, 80 caractères au plus, sur une ligne.",
      saved: "Nom enregistré.",
      email: "E-mail",
      totpLead:
        "Chaque opérateur se connecte avec le code d'une application d'authentification. Appareil perdu ? Demandez à un administrateur de l'instance de la réinitialiser : les clés d'accès ajoutées avant ne fonctionnent plus.",
      passkeysLead:
        "Connectez-vous à la console avec une clé d'accès au lieu de votre mot de passe et du code d'authentification.",
      stepUp: "Confirmez votre identité pour modifier vos clés d'accès.",
      stepUpAdd:
        "Saisissez votre mot de passe et le code de votre application d'authentification pour ajouter une clé d'accès. Une clé d'accès ne peut pas en ajouter une autre.",
      unavailable: "Les clés d'accès ne sont pas disponibles sur cette console.",
    },
    operators: {
      title: "Opérateurs",
      lead: "Les personnes qui exploitent cette instance, leurs navigateurs connectés et ce qu'elles ont modifié.",
      tabs: { sessions: "Sessions", members: "Opérateurs", audit: "Activité" },
      tabsLabel: "Pages des opérateurs",
      membersTitle: "La gestion des opérateurs arrive",
      membersBody:
        "L'ajout et le retrait d'opérateurs se feront ici, chaque modification confirmée par votre seconde étape et visible de tous les opérateurs. D'ici là, les opérateurs sont gérés par les administrateurs de l'instance.",
      auditTitle: "L'activité des opérateurs arrive",
      auditBody:
        "Chaque modification d'un opérateur (organisations, offres, règles, limites, demandes de support) sera listée ici, de la plus récente à la plus ancienne.",
      notYet: "Pas encore disponible",
    },
    tenantStatus: {
      active: "Active",
      suspended: "Suspendue",
      deletion_scheduled: "Suppression programmée",
      deleted: "Supprimée",
    },
    spendAlert: { warning: "80 % de la limite de dépense", reached: "Limite de dépense atteinte" },
    syntheticSpendDetail:
      "Inclut {{amount}} de dépense de démonstration synthétique : elle compte pour l'alerte à 80 %, mais seule la dépense réelle peut atteindre la limite.",
    spendNotice: {
      warning: {
        hard: "Ce tenant a consommé 80 % de sa limite mensuelle de dépense IA. À la limite, ses échanges et traitements en arrière-plan s'arrêtent jusqu'au mois prochain ou au relèvement de la limite.",
        soft: "Ce tenant a consommé 80 % de sa limite mensuelle de dépense IA (souple : simple avertissement).",
      },
      reached: {
        hard: "Ce tenant a atteint sa limite mensuelle stricte de dépense IA : les échanges sont refusés et les traitements en arrière-plan en pause jusqu'au mois prochain ou au relèvement de la limite.",
        soft: "Ce tenant a dépassé sa limite mensuelle souple de dépense IA : rien n'est arrêté.",
      },
    },
    tenants: {
      title: "Organisations",
      lead: "Toutes les organisations de cette instance, avec leur offre et leur usage. Leur contenu n'est pas visible ici.",
      caption: "Organisations de l'instance",
      create: "Nouvelle organisation",
      createTitle: "Nouvelle organisation",
      createBody: "Crée l'organisation et invite son premier administrateur par e-mail.",
      createConfirm: "Créer l'organisation",
      rename: "Modifier le nom",
      name: "Nom",
      status: "Statut",
      plan: "Offre",
      users: "Utilisateurs actifs",
      entities: "Entités",
      ocr: "Pages OCR ce mois-ci",
      spend: "Dépense IA ce mois-ci",
      created: "Créée le",
      adminEmail: "E-mail du premier administrateur",
      emailInUse:
        "Cet e-mail a déjà un compte dans une autre organisation. Utilisez un autre e-mail : le passage d'un compte d'une organisation à l'autre viendra plus tard.",
      adminName: "Nom du premier administrateur",
      defaultPlan: "L'offre par défaut",
      none: "Aucune organisation",
      noneHint: "Créez la première organisation pour inviter son administrateur.",
      purgeOn: "Purgée le {{date}}",
      back: "Toutes les organisations",
      admins: "Administrateurs de l'organisation",
      adminsNote:
        "Les interlocuteurs contractuels de l'organisation : les seules personnes que vous voyez nommément.",
      noAdmins:
        "Cette organisation n'a aucun administrateur actif : personne ne peut approuver un accès support.",
      idps: "Fournisseurs d'identité",
      noIdps: "Comptes ESI uniquement.",
      idpStatus: { active: "actif", disabled: "désactivé" },
      domains: "Domaines vérifiés",
      noDomains: "Aucun domaine vérifié.",
      createdOn: "Créée le {{date}}",
      adminCount_one: "{{count}} administrateur",
      adminCount_other: "{{count}} administrateurs",
    },
    tenantTabs: {
      label: "Pages de l'organisation",
      overview: "Vue d'ensemble",
      admins: "Administrateurs",
      "sign-in": "Connexion",
      limits: "Offre et limites",
      lifecycle: "Cycle de vie",
    },
    tenantList: {
      search: "Rechercher une organisation",
      filters: "Afficher",
      all: "Toutes",
      active: "Actives",
      suspended: "Suspendues",
      deletion: "Suppression programmée",
      synthetic: "Données synthétiques",
      noMatch: "Aucune organisation ne correspond",
      noMatchHint: "Essayez un autre nom ou affichez toutes les organisations.",
      showAll: "Afficher toutes les organisations",
      syntheticMark: "Données synthétiques",
      usersCount_one: "{{count}} utilisateur actif",
      usersCount_other: "{{count}} utilisateurs actifs",
      used: "{{percent}} % utilisé",
    },
    limits: {
      title: "Offre et limites",
      seats: "Places",
      storageBytes: "Stockage (Go)",
      ocrMonthlyPages: "Pages OCR par mois",
      modelMonthlyUsd: "Dépense IA par mois (USD)",
      fromPlan: "Vide : selon l'offre ({{value}})",
      note: "Les changements s'appliquent dès la requête suivante et sont consignés dans le journal d'audit de l'organisation. Abaisser une limite ne désactive personne.",
      saved: "Limites enregistrées.",
    },
    lifecycle: {
      title: "Cycle de vie",
      suspend: "Suspendre",
      reactivate: "Réactiver",
      delete: "Programmer la suppression",
      cancel: "Annuler la suppression",
      expedite: "Supprimer maintenant…",
      suspendTitle: "Suspendre {{name}} ?",
      suspendBody:
        "Toutes les sessions et tous les jetons de ses utilisateurs cessent de fonctionner à leur prochaine requête. Rien n'est supprimé.",
      suspendConfirm: "Suspendre",
      reactivateTitle: "Réactiver {{name}} ?",
      reactivateBody: "Ses utilisateurs peuvent de nouveau se connecter.",
      reactivateConfirm: "Réactiver",
      deleteTitle: "Programmer la suppression de {{name}} ?",
      deleteBody:
        "Ses utilisateurs sont déconnectés immédiatement. Après 30 jours, son contenu, ses utilisateurs et son annuaire sont purgés ; seuls son journal d'audit et ses rapports agrégés restent. Vous pouvez l'annuler d'ici là.",
      deleteConfirm: "Programmer la suppression",
      cancelTitle: "Annuler la suppression de {{name}} ?",
      cancelBody:
        "L'organisation retrouve le statut qu'elle avait avant la programmation de sa suppression, avec toutes ses données (suspendue si ce statut est inconnu).",
      cancelConfirm: "Annuler la suppression",
      expediteTitle: "Supprimer {{name}} maintenant ?",
      expediteBody:
        "La purge commence maintenant au lieu d'après le {{date}}. Son contenu, ses utilisateurs et son annuaire sont supprimés définitivement ; seuls son journal d'audit et ses rapports agrégés restent. Cette action est irréversible.",
      expediteConfirm: "Supprimer maintenant",
      expediteStepUp: "Confirmez votre identité avant de supprimer cette organisation maintenant.",
      typeId: "Saisissez les 8 premiers caractères de son identifiant, {{prefix}}",
      idPrefix: "Identifiant de l'organisation",
      purging: "Suppression en cours : la purge a commencé et ne peut plus être annulée.",
      typeName: "Saisissez {{name}} pour confirmer",
      scheduledNotice: "Suppression programmée : l'organisation sera purgée après le {{date}}.",
    },
    mfa: {
      reset: "Réinitialiser la validation en deux étapes",
      resetOf: "Réinitialiser la validation en deux étapes de {{name}}",
      title: "Réinitialiser la validation en deux étapes de {{email}}",
      body: "Retire l'application d'authentification et les clés d'accès de cet administrateur ; il en configurera une nouvelle à sa prochaine connexion. Consigné dans le journal d'audit de l'organisation.",
      reason: "Motif",
      confirm: "Réinitialiser",
      done: "Validation en deux étapes réinitialisée.",
      invitationPending:
        "{{email}} ne s'est pas encore connecté : il n'y a pas de validation en deux étapes à réinitialiser. Renvoyez plutôt son invitation.",
    },
    adminInvitation: {
      failed: "L'invitation à {{email}} n'a pas pu être envoyée : {{reason}}",
      expired: "L'invitation à {{email}} a expiré le {{date}} sans avoir été acceptée.",
      failedShort: "Invitation non envoyée : {{reason}}",
      expiredShort: "Invitation expirée le {{date}}",
      sentShort: "Invitation envoyée le {{date}}",
      pendingShort: "Invitation en attente",
      resend: "Renvoyer l'invitation",
      resendOf: "Renvoyer l'invitation de {{name}}",
      resent: "Invitation envoyée à {{email}}.",
      notResent: "L'invitation à {{email}} n'a pas pu être envoyée : {{reason}}",
      reason: {
        sandbox_unverified:
          "ce déploiement n'envoie qu'aux adresses vérifiées (bac à sable du service d'e-mail)",
        not_authorized: "ce déploiement n'est pas autorisé à envoyer des e-mails",
        sending_paused: "l'envoi d'e-mails est suspendu pour ce déploiement",
        unknown: "le service d'e-mail l'a refusée",
      },
    },
    models: {
      title: "Connexions aux modèles",
      lead: "Les comptes AWS qui servent les modèles. Les changements s'appliquent sous 30 secondes, sans déploiement.",
      perimeter: {
        title: "Le périmètre réseau ne correspond pas à la liste des comptes",
        error: {
          capacity_exceeded:
            "La dernière mise à jour n'a rien écrit : la liste dépasse ce que les politiques du périmètre peuvent contenir.",
          write_failed: "La dernière mise à jour a échoué : une écriture AWS n'a pas abouti.",
        },
        removals:
          "Retirés, encore ouverts au niveau réseau : {{accounts}}. Aucune connexion ne les utilise.",
        adds: "Ajoutés, pas encore admis : {{accounts}}. Leurs connexions ne sont pas utilisées.",
        retry:
          "La mise à jour est relancée toutes les 15 minutes ; l'alarme de l'environnement se déclenche si une relance trouve encore l'écart. Si cela persiste, suivez le runbook.",
      },
      providers: {
        title: "Fournisseurs",
        lead: "Les fournisseurs que les connexions de l'instance peuvent utiliser, et ceux que les organisations peuvent connecter avec leurs propres comptes. Un fournisseur sans adaptateur ESI ne peut pas encore être activé.",
        kind: "Fournisseur",
        instance: "Instance",
        tenant: "Organisations",
        instanceOf: "{{name}} pour les connexions de l'instance",
        tenantOf: "{{name}} pour les connexions propres aux organisations",
        notYet: "Pas encore disponible",
        tenantLater:
          "Les organisations connecteront leur propre Bedrock dans une prochaine version ; le réglage est conservé d'ici là.",
        bedrockOff:
          "Bedrock est désactivé pour l'instance : les connexions ci-dessous ne servent plus. Le compte de ce déploiement continue de servir.",
        confirmTitle: "Activer ce fournisseur ?",
        confirm: {
          instance:
            "Les connexions de l'instance pourront de nouveau utiliser {{name}}, dès la prochaine requête.",
          tenant:
            "Les administrateurs d'organisation pourront connecter {{name}} dans les comptes AWS rattachés à leur organisation.",
        },
        turnOn: "Activer",
      },
      labs: {
        title: "Laboratoires",
        lead: "Les laboratoires dont Bedrock sert les modèles sur cette instance. Chaque tenant commence avec le laboratoire par défaut ; ses administrateurs peuvent ajouter tout laboratoire non bloqué ici, après un avertissement si ESI ne l’a pas testé. Bloquer un laboratoire le retire de tous les tenants à la fois.",
        state: {
          blocked: "Bloqué",
          untested: "Disponible · Non testé par ESI",
          tested: "Testé par ESI · {{date}}, catalogue {{version}}",
        },
        geographies: "Versions : {{list}}",
        geo: { eu: "UE", global: "monde" },
        notInEu: "Monde uniquement : non disponible dans l’UE",
        default: "Par défaut pour chaque tenant",
        tenants_one: "{{count}} tenant l’utilise",
        tenants_other: "{{count}} tenants l’utilisent",
        accessDenied:
          "Une vérification de ses modèles a été refusée : activez l’accès aux modèles de ce laboratoire dans la console Bedrock du compte (procédure : accès aux modèles Bedrock).",
        block: "Bloquer",
        blockOf: "Bloquer {{name}}",
        unblock: "Débloquer",
        confirmTitle: "Débloquer ce laboratoire ?",
        confirm:
          "Les administrateurs de tenant pourront de nouveau ajouter {{name}}, et les tenants qui l’avaient ajouté le retrouvent dès la prochaine requête.",
      },
      health: {
        title: "Vérification des modèles",
        help: "ESI vérifie que chaque modèle répond sur chaque connexion, par un appel de test d'un mot. Les versions qui ne répondent plus sont écartées jusqu'à ce qu'elles répondent à nouveau.",
        hourly: "Toutes les heures",
        daily: "Tous les jours",
        unknown: {
          assume_denied:
            "État inconnu : ESI ne peut pas vérifier cette connexion, son rôle ne fait pas confiance au rôle de vérification. Ses modèles restent utilisés, mais ESI ne passera pas à une version plus récente par elle. Ajoutez le rôle de vérification à la politique de confiance du rôle (Afficher la configuration du rôle).",
          assume_failed:
            "État inconnu : ESI n'a pas pu joindre le rôle de cette connexion pour la vérifier. Ses modèles restent utilisés, mais ESI ne passera pas à une version plus récente par elle tant qu'une vérification n'aura pas réussi.",
        },
        listDenied:
          "Impossible de lister les modèles : ajoutez les deux actions List au rôle (bedrock:ListInferenceProfiles, bedrock:ListFoundationModels)",
      },
      switch: {
        title: "Connexions à d'autres comptes AWS",
        on: "Les appels peuvent utiliser les connexions ci-dessous, dans cet ordre.",
        off: "Chaque appel utilise uniquement le compte de ce déploiement. Les connexions ci-dessous restent inchangées.",
        onWord: "Activé",
        offWord: "Désactivé",
      },
      connections: {
        title: "Connexions",
        lead: "Bedrock dans un autre compte AWS, via un rôle qu'ESI endosse. Les appels les essaient de haut en bas, puis le compte de ce déploiement.",
        add: "Enregistrer une connexion",
        needAccount: "Ajoutez d'abord un compte à la liste autorisée",
        order: "Connexions dans l'ordre de routage",
      },
      default: {
        title: "Compte de ce déploiement",
        body: "Compte {{account}}, toujours en dernier. Il continue de servir quand toutes les connexions ci-dessus sont désactivées ou en échec.",
        thisAccount: "de ce déploiement",
      },
      status: {
        routing: "En service",
        ready: "Test réussi",
        stale: "Test à refaire",
        unprobed: "Non testée",
        notApplied: "En attente du périmètre",
        assumeDenied: "Rôle inaccessible",
        modelDenied: "Accès au modèle refusé",
        tenantBound: "Compte rattaché à une organisation",
        providerOff: "Hors service : Bedrock est désactivé pour l'instance",
        error: "Échec du test",
      },
      logging: {
        on: "Le compte journalise les appels",
        off: "Journalisation désactivée",
        unknown: "Journalisation inconnue (illisible)",
        confirmUnknown:
          "ESI n'a pas pu lire si ce compte journalise les appels aux modèles, prompts compris. Ne l'activez que si vous connaissez son réglage de journalisation.",
        confirm:
          "Ce compte journalise les appels aux modèles, prompts compris. Activez-la seulement si cette journalisation est prévue.",
        enableAnyway: "Activer quand même",
        keepOff: "Laisser désactivée",
      },
      retention: { default: "Rétention par défaut", aws_review: "Rétention revue AWS" },
      actionsFor: "Actions pour {{name}}",
      probe: "Tester maintenant",
      probing: "Test en cours : un appel minimal, sans contenu client…",
      showSetup: "Afficher la configuration du rôle",
      moveUp: "Monter",
      moveDown: "Descendre",
      delete: "Supprimer la connexion",
      enableNeedsProbe: "Testez-la avec succès pour l'activer.",
      enableNeedsPerimeter: "Son compte n'est pas encore admis par le périmètre.",
      create: {
        title: "Enregistrer une connexion",
        body: "ESI génère l'identifiant externe. Ensuite, l'administrateur du compte crée le rôle à partir de la configuration affichée.",
        label: "Nom",
        id: "Identifiant de la connexion",
        idHelp: "Minuscules, chiffres et tirets, par exemple claude5-account.",
        account: "Compte",
        retention: "Rétention déclarée",
        retentionHelp:
          "Le mode de rétention des données Bedrock de ce compte. Vous l'attestez ; ESI ne peut pas le lire.",
        submit: "Enregistrer",
      },
      setup: {
        title: "Configuration du rôle pour {{id}}",
        body: "À transmettre à l'administrateur du compte. Le rôle ne fait confiance qu'à ce déploiement, avec cet identifiant externe.",
        role: "Nom du rôle",
        externalId: "Identifiant externe",
        sourceIdentity: "Identité source",
        trust: "Politique de confiance",
        permissions: "Politique de permissions",
        copy: "Copier",
        copied: "Copié",
        next: "Une fois le rôle créé, testez la connexion, puis activez-la.",
      },
      accounts: {
        title: "Liste des comptes autorisés",
        lead: "Les connexions ne peuvent viser que ces comptes. En ajouter un élargit le périmètre réseau : il faut votre code d'authentification et chaque opérateur est prévenu.",
        add: "Ajouter un compte",
        empty: "Aucun compte : seul le compte de ce déploiement sert les modèles.",
        applied: "Admis",
        pending: "En attente du périmètre",
        remove: "Retirer",
        removeOf: "Retirer le compte {{account}}",
        capacity: "{{used}} comptes sur {{max}}",
        stepUp:
          "Ajouter un compte élargit le périmètre. Confirmez avec votre mot de passe et votre code d'authentification.",
        bindStepUp:
          "Rattacher un compte change qui peut y appeler des modèles. Confirmez avec votre mot de passe et votre code d'authentification.",
        boundInstance: "Instance",
        boundTenant: "Organisation : {{name}}",
        unbound: "Non rattaché : l'organisation {{name}} a été supprimée",
        bindToTenant: "Rattacher à une organisation",
        bindToTenantOf: "Rattacher le compte {{account}} à une organisation",
        changeBinding: "Changer le rattachement",
        changeBindingOf: "Changer le rattachement du compte {{account}}",
      },
      binding: {
        title: "Rattacher le compte {{account}}",
        body: "Un compte sert les connexions de l'instance, ou celles d'une seule organisation. Chaque opérateur reçoit un e-mail.",
        for: "Sert",
        help: "Le compte d'une organisation n'est jamais utilisé par l'instance ni par une autre organisation.",
        instance: "L'instance",
        tenants: "Une organisation",
        disables_one:
          "La connexion enregistrée dans ce compte est désactivée, conservée, et n'est pas transmise.",
        disables_other:
          "Les {{count}} connexions enregistrées dans ce compte sont désactivées, conservées, et ne sont pas transmises.",
        submit: "Rattacher le compte",
      },
      addAccount: {
        title: "Ajouter un compte AWS",
        body: "Son rôle esi-model-access devient accessible depuis ce déploiement.",
        account: "Identifiant du compte AWS",
        label: "Nom",
        widens:
          "Chaque opérateur reçoit un e-mail. Retirer le compte plus tard arrête aussitôt ses connexions.",
        submit: "Ajouter le compte",
      },
      refused: {
        probe: "Testez la connexion avec succès avant de l'activer.",
        logging: "Ce compte journalise les appels : confirmez pour l'activer.",
        sameAccount: "C'est le compte de ce déploiement, toujours disponible.",
        capacity: "La liste autorisée est pleine pour ce déploiement.",
        providerOff:
          "Bedrock est désactivé pour les connexions de l'instance : activez-le d'abord sous Fournisseurs.",
        tenantBound:
          "Ce compte est rattaché à une organisation : l'instance ne peut pas l'utiliser.",
        sameBinding: "Le compte sert déjà cela.",
        tenantUnavailable:
          "Cette organisation est en cours de suppression : elle ne peut pas détenir de compte.",
      },
    },
    plans: {
      title: "Offres",
      lead: "Des limites regroupées par offre. Chaque organisation a une offre ; un opérateur peut modifier une limite pour une seule organisation.",
      create: "Nouvelle offre",
      createTitle: "Nouvelle offre",
      editTitle: "Modifier {{name}}",
      dialogBody:
        "Modifier une offre change les limites de toutes les organisations concernées qui n'ont pas de dérogation.",
      name: "Nom",
      mode: "Limite de dépense IA",
      modes: { soft: "Avertir seulement", hard: "Refuser les nouvelles questions" },
      tenants: "Organisations",
      default: "Par défaut",
      isDefault: "Les nouvelles organisations reçoivent cette offre.",
      makeDefault: "Attribuer cette offre aux nouvelles organisations",
      emptyIsUnlimited: "Laissez une limite vide pour ne pas en fixer.",
      edit: "Modifier",
      editOf: "Modifier {{name}}",
      delete: "Supprimer l'offre",
      inUse_one: "{{count}} organisation utilise cette offre : changez-la d'offre d'abord.",
      inUse_other: "{{count}} organisations utilisent cette offre : changez-les d'offre d'abord.",
    },
    policies: {
      title: "Règles de l'instance",
      lead: "Des valeurs dont héritent toutes les organisations. Une valeur verrouillée ne peut être changée par aucune organisation ni entité ; le déploiement peut en fixer certaines.",
      reset: "Revenir au défaut",
      lockedEverywhere: "Verrouillée pour toutes les organisations",
      changedAt: "Modifiée le {{when}}",
      navLabel: "Pages des règles",
      setCount_one: "{{count}} définie pour l'instance",
      setCount_other: "{{count}} définies pour l'instance",
      groups: {
        general:
          "Comment les réponses utilisent les sources, les conversations hors projet et leurs artefacts, et qui peut créer et rejoindre des projets.",
        security: "Le second facteur exigé des comptes natifs pour se connecter.",
        models:
          "Où s'exécutent les modèles, les efforts autorisés et la façon de choisir un modèle.",
        knowledge:
          "La déclassification, la lecture des scans et des images, la rédaction du wiki et ce que l'assistant mémorise.",
        limits: "Le plus gros fichier qu'un projet accepte et le nombre de pages lues par scan.",
        integrations:
          "La recherche web et ses limites, et le sort des appels d'outils sortants après lecture de contenu classifié.",
        clients:
          "L'accès depuis les clients MCP, ce qu'ils peuvent enregistrer et recevoir, et les groupes d'outils qu'ils peuvent utiliser.",
      },
      summary: {
        setHere: "Définies pour l'instance :",
        locked: "Verrouillées pour toutes les organisations :",
        fixed: "Fixées par le déploiement :",
        allDefault_one:
          "Rien n'est défini pour l'instance : cette règle suit la valeur par défaut d'ESI.",
        allDefault_other:
          "Rien n'est défini pour l'instance : les {{count}} règles suivent les valeurs par défaut d'ESI.",
      },
      marks: { setHere: "Définie pour l'instance", fixed: "Fixée pour ce déploiement" },
      search: { label: "Rechercher une règle", placeholder: "Nom ou sujet, par ex. mémoire" },
      keys: {
        triageAlert: {
          label: "Alerte de dépense de l'assistant de tri",
          help: "Dépense quotidienne de l'assistant de tri des retours, en USD, qui alerte les opérateurs une fois par jour. Le tri continue au-delà.",
        },
        triageCap: {
          label: "Plafond quotidien de l'assistant de tri",
          help: "Facultatif. Une fois la dépense du jour atteinte, l'assistant s'arrête jusqu'au jour UTC suivant. Vide : aucun plafond.",
        },
        artefacts: {
          label: "Artefacts",
          help: "L'assistant peut créer et réviser des documents, du code, des schémas et des tableaux conservés à côté de la conversation.",
        },
      },
    },
    support: {
      title: "Accès support",
      lead: "Demandez à une organisation un accès limité dans le temps et en lecture seule à son administration. Ses administrateurs décident ; chacune de vos requêtes figure dans son journal d'audit.",
      caption: "Vos demandes d'accès support",
      request: "Demander un accès support",
      requestTitle: "Demander un accès support",
      requestBody:
        "Les administrateurs de l'organisation voient le motif et la durée, et peuvent approuver pour moins longtemps ou refuser.",
      tenant: "Organisation",
      pickTenant: "Choisir une organisation",
      reasonHint: "Un numéro de ticket et ce que vous devez consulter.",
      duration: "Durée (heures)",
      durationHint: "72 heures au plus.",
      send: "Envoyer la demande",
      none: "Aucune demande d'accès support",
      noneHint: "Demandez un accès depuis la page d'une organisation ou ici.",
      withContent: "Projets ouverts par l'organisation",
      open: "Ouvrir {{tenant}}",
      banner: "Accès support à {{tenant}} jusqu'au {{until}} · lecture seule, audité",
      bannerEnded: "L'accès support à {{tenant}} est terminé",
      bannerLabel: "Accès support",
      leave: "Quitter",
      ended:
        "Cet accès n'est plus valable : il a expiré, a été révoqué, ou l'organisation n'est pas active.",
      backofficeTitle: "Administration de l'organisation",
      backofficeLead:
        "En lecture seule, comme la voient ses administrateurs. Chaque page ouverte est consignée dans son journal d'audit.",
    },
    feedback: {
      title: "Retours",
      lead: "Les bugs et idées envoyés depuis l'application. L'ouverture d'un retour est enregistrée dans le journal d'audit de l'instance.",
      filters: "Filtrer les retours",
      status: "Statut",
      type: "Type",
      tenant: "Organisation",
      build: "Build",
      any: "Tous",
      anyTenant: "Toutes les organisations",
      unknownTenant: "Une organisation absente de la liste",
      groupFilter: "Groupe",
      unsorted: "Sans groupe",
      unknownGroup: "Un groupe absent de la liste",
      groupFilterRefused: "Pas encore disponible : actualisez dans quelques minutes.",
      selectAll_one: "Sélectionner le signalement affiché",
      selectAll_other: "Sélectionner les {{count}} signalements affichés",
      selectFirst_one: "Sélectionner le premier signalement",
      selectFirst_other: "Sélectionner les {{count}} premiers signalements",
      selected_one: "{{count}} signalement sélectionné",
      selected_other: "{{count}} signalements sélectionnés",
      selectRow: "Sélectionner : {{title}}",
      addSelected: "Ajouter la sélection à un groupe",
      steps: "Autres signalements de cette liste",
      previous: "Précédent",
      next: "Suivant",
      previousKey: "Signalement précédent ([)",
      nextKey: "Signalement suivant (])",
      none: "Aucun retour pour l'instant",
      noneHint: "Les retours envoyés depuis l'application apparaissent ici.",
      noMatch: "Aucun retour ne correspond",
      noMatchHint: "Modifiez ou effacez les filtres.",
      clear: "Effacer les filtres",
      older: "Afficher les plus anciens",
      rowTitle: "{{type}} de {{tenant}}",
      origin: {
        menu: "Depuis le menu",
        agent_requested: "Demandé à l'assistant",
        agent_suggested: "Suggéré par l'assistant",
        answer_action: "Depuis une réponse",
      },
      assistant: "Assistant",
      assistantNone: "Aucun : envoyé sans le contexte de l'assistant",
      noContext: "Aucun contexte envoyé",
      description: "Description",
      context: "Contexte",
      notSent: "Non envoyé",
      tabs: "Vues des retours",
      tabReports: "Signalements",
      tabGroups: "Groupes",
      assist: {
        title: "Suggestion de l'assistant",
        thinking: "L'assistant lit ce signalement…",
        again: "Suggérer à nouveau",
        group: "Groupe",
        groupMeta_one: "{{type}} · {{count}} signalement",
        groupMeta_other: "{{type}} · {{count}} signalements",
        newGroup: "nouveau groupe",
        noGroup: "Aucun groupe ne convient : choisissez-en un ci-dessous",
        duplicates: "Doublons",
        duplicate: "{{type}} · {{tenant}} · {{date}}",
        noDuplicates: "Aucun trouvé",
        type: "Type",
        severity: "Gravité",
        area: "Domaine",
        why: "Pourquoi",
        severities: { low: "Faible", medium: "Moyenne", high: "Élevée" },
        accept: "Accepter",
        change: "Modifier…",
        changeTitle: "Modifier la suggestion",
        reject: "Rejeter",
        acceptChanges: "Accepter avec ces modifications",
        cancel: "Annuler",
        newGroupOption: "Nouveau groupe",
        newGroupTitle: "Titre du nouveau groupe",
        footnote:
          "Fonctionne sur les modèles propres d'ESI dans l'UE, aux frais de l'instance. Rien ne change tant que vous n'acceptez pas.",
        optedOut:
          "Trié à la main : cette organisation tient ses signalements à l'écart de l'assistant.",
        rejected: "Aucune suggestion pour ce signalement. Triez-le à la main ci-dessous.",
        decidedElsewhere:
          "Cette suggestion a déjà été traitée ou proposée à nouveau. Rechargez le signalement.",
        budgetReached: "Budget quotidien de tri atteint. L'assistant revient demain (UTC).",
        suggested: "Suggéré : {{title}}",
        suggestedNew: "Suggéré : {{title}} (nouveau groupe)",
        newGroupChip: "Nouveau groupe",
        batch: "Suggérer pour les non triés",
        batchHint:
          "L'assistant peut suggérer un groupe, une gravité et un domaine pour 50 signalements non triés de ce filtre au plus.",
        batchRunning: "Suggestions en cours… {{done}} signalements traités",
        batchDone_one: "{{count}} nouvelle suggestion sur {{total}} signalements non triés.",
        batchDone_other: "{{count}} nouvelles suggestions sur {{total}} signalements non triés.",
        batchNone: "Chaque signalement non trié de ce filtre a déjà une suggestion.",
        spend: "Assistant de tri aujourd'hui : {{usd}} · alerte à {{alert}}",
        spendCap: "arrêt à {{cap}}",
        draft: "Rédiger avec l'assistant",
        drafting: "Rédaction…",
        draftUsed_one: "Rédigé à partir de {{count}} signalement.",
        draftUsed_other: "Rédigé à partir de {{count}} signalements.",
        draftLeftOut_one:
          "{{count}} signalement a été écarté (son organisation tient ses signalements à l'écart de l'assistant).",
        draftLeftOut_other:
          "{{count}} signalements ont été écartés (leurs organisations tiennent leurs signalements à l'écart de l'assistant).",
        draftEdit:
          "Relisez avant d'enregistrer. L'aperçu du ticket refuse toujours un texte copié d'un signalement.",
        draftNoReports:
          "Tous les signalements de ce groupe viennent d'organisations qui les tiennent à l'écart de l'assistant.",
        discardDraft: "Abandonner le brouillon",
        notSet: "Non défini",
        severityHint: "Le degré de gravité ; le ticket le porte en libellé severity::.",
        severityHelp: {
          low: "Cosmétique, ou un contournement existe. Le ticket reçoit severity::low.",
          medium:
            "Une fonction se comporte mal pour certains utilisateurs. Le ticket reçoit severity::medium.",
          high: "Données, sécurité, ou un parcours essentiel cassé. Le ticket reçoit severity::high.",
        },
        areaHint: "La partie du produit, parmi les libellés de domaine du backlog.",
      },
      triage: {
        newGroup: "Nouveau groupe",
        guide:
          "Un groupe réunit les signalements sur un même bug ou une même idée. Il devient un ticket GitLab, et chaque auteur de signalement du groupe voit son statut.",
        newGroupHint:
          "Un groupe réunit les signalements sur un même bug ou une même idée et devient un ticket GitLab. Vous pourrez en ajouter ensuite.",
        groupsNone: "Aucun groupe",
        groupsNoneHint:
          "Ouvrez un signalement et ajoutez-le à un groupe depuis sa section Groupe, ou créez un groupe ici.",
        counts_one: "{{count}} signalement",
        counts_other: "{{count}} signalements",
        tenants_one: "{{count}} organisation",
        tenants_other: "{{count}} organisations",
        issue: "Ticket n° {{number}}",
        titleLabel: "Titre",
        titleHint: "Devient le titre du ticket GitLab. Vos propres mots, jamais ceux d'un auteur.",
        summaryLabel: "Résumé",
        summaryHint:
          "Rédigé par vous pour le ticket GitLab. Ne collez jamais les mots, noms ou adresses d'un auteur de signalement.",
        typeLabel: "Type",
        typeHint: "Bug ou idée. Détermine le libellé kind:: du ticket.",
        statusLabel: "Statut",
        statusNotified:
          "Les auteurs des signalements sont prévenus dans leurs notifications quand vous enregistrez un nouveau statut, une fois par statut.",
        reasonLabel: "Raison pour les auteurs",
        reasonHint:
          "Facultatif. Montrée aux auteurs des signalements de ce groupe avec « Non prévu », dans tous les tenants. Vos propres mots : jamais le texte d'un signalement, un nom, une adresse ou un identifiant.",
        reasonFindingsTitle: "Modifiez la raison avant d'enregistrer",
        reasonFindings: {
          email: "Une adresse e-mail",
          uuid: "Un identifiant",
          quote: "Une citation de plus de 80 caractères",
          copied: "Des mots copiés d'un signalement",
          mention: "Une mention",
          tenant: "Le nom d'un tenant ayant un signalement dans ce groupe",
        },
        statusHelp: {
          new: "Chaque auteur de signalement de ce groupe lit « Nouveau » : personne ne l'a encore examiné.",
          triaged:
            "Chaque auteur de signalement de ce groupe lit « Examiné » : l'équipe ESI l'a examiné.",
          planned:
            "Chaque auteur de signalement de ce groupe lit « Prévu » : une modification est programmée.",
          fixed:
            "Chaque auteur de signalement de ce groupe lit « Corrigé », puis « Corrigé dans la version … » de lui-même quand une version ferme le ticket GitLab. Ferme le groupe.",
          not_planned:
            "Chaque auteur de signalement de ce groupe lit « Non prévu ». Ferme le groupe.",
          duplicate:
            "Chaque auteur de signalement de ce groupe lit « Regroupé avec un retour similaire » : il est suivi ailleurs. Ferme le groupe.",
        },
        save: "Enregistrer",
        saved: "Enregistré",
        create: "Créer le groupe",
        cancel: "Annuler",
        details: "Détails",
        detailsHint:
          "Le titre, le résumé et le type vont dans le ticket GitLab de ce groupe. Le statut est ce que voient ses auteurs de signalement.",
        reports: "Signalements",
        reportsNone:
          "Aucun signalement pour l'instant. Ouvrez un signalement et ajoutez-le à ce groupe depuis sa section Groupe.",
        goToReports: "Voir les signalements",
        removeReport: "Retirer du groupe",
        suggested: "Signalements suggérés",
        suggestedHint:
          "Signalements hors de tout groupe dont les erreurs récentes portent une empreinte liée.",
        add: "Ajouter au groupe",
        fingerprints: "Empreintes d'erreur",
        fingerprintsHint:
          "Une empreinte est un code de 16 caractères qui désigne un type d'erreur de l'application : le champ fingerprint d'une ligne de journal client.error. Liez-en une, et les signalements suivants portant cette erreur sont suggérés ici. Une empreinte appartient à un seul groupe.",
        fingerprintsNone: "Aucune empreinte liée.",
        fingerprintLabel: "Empreinte",
        link: "Lier",
        unlink: "Détacher {{fingerprint}}",
        gitlab: "Ticket GitLab",
        gitlabHint:
          "Le ticket contient votre titre et votre résumé, le type, les builds, routes, empreintes et décomptes. Jamais le texte d'un signalement, une organisation ni une personne.",
        notConfigured: "GitLab n'est pas configuré",
        notConfiguredHint:
          "Aucun jeton GitLab n'est encore enregistré pour cette instance. Les tickets pourront être créés ensuite (procédure infra).",
        unavailable: "Le jeton GitLab est illisible pour le moment. Réessayez plus tard.",
        previewChecks:
          "L'aperçu montre exactement ce qui sera envoyé, et refuse une adresse e-mail, un identifiant, une citation de plus de 80 caractères, des mots copiés d'un signalement ou une @mention dans votre titre ou résumé.",
        preview: "Aperçu du ticket",
        previewTitle: "Créer un ticket GitLab",
        previewLead: "Voici exactement ce qui sera envoyé à GitLab.",
        previewIssueTitle: "Titre",
        labels: "Libellés",
        body: "Contenu",
        findingsTitle: "Modifiez le titre ou le résumé avant de créer le ticket",
        findings: {
          email: "Une adresse e-mail : {{excerpt}}",
          uuid: "Un identifiant : {{excerpt}}",
          quote: "Une citation de plus de 80 caractères : {{excerpt}}",
          copied: "Des mots copiés d'un signalement : {{excerpt}}",
          mention: "Une mention, qui notifierait quelqu'un : {{excerpt}}",
          tenant: "Le nom d'un tenant : {{excerpt}}",
        },
        fields: { title: "Titre", summary: "Résumé", reason: "Raison" },
        confirm: "Créer le ticket",
        trackerFailed:
          "GitLab n'a pas créé le ticket{{status}}. Rien n'a été enregistré. Recréez-le quand GitLab répond.",
        openIssue: "Ouvrir le ticket n° {{number}} dans GitLab",
        group: "Groupe",
        notGrouped: "Dans aucun groupe",
        notGroupedHint:
          "Ajoutez ce signalement au groupe du même bug ou de la même idée : il prendra le statut et le ticket GitLab de ce groupe.",
        inGroup: "Dans le groupe",
        suggestion: "Même erreur que {{title}}",
        reportFingerprints: "Empreintes d'erreur",
        linkedHere: "Liée à ce groupe",
        linkedTo: "Liée à",
        notLinked: "Liée à aucun groupe",
        linkHere: "Lier à ce groupe",
        linkHereLabel: "Lier {{fingerprint}} à ce groupe",
        addTo: "Ajouter à un groupe",
        moveToOther: "Déplacer vers un autre groupe",
        chooseGroup: "Choisir un groupe ouvert",
        noOpenGroup: "Aucun groupe ouvert",
        noOtherOpenGroup: "Aucun autre groupe ouvert",
        addShort: "Ajouter",
        move: "Déplacer",
        ungroup: "Retirer du groupe",
        removeHint: "Le retirer remet le statut de son auteur à « Nouveau ».",
      },
    },
    reports: {
      title: "Utilisation",
      lead: "L'usage et le coût de chaque organisation sur les 30 derniers jours. Jamais une entité, un projet ni une personne.",
      tenants: "Organisations",
      tenant: "Organisation",
      questions: "Questions",
      conversations: "Conversations",
      cost: "Coût IA estimé",
      mcpCost: "Coût MCP",
      monthSpend: "Dépense ce mois-ci",
      storage: "Stockage",
      unsupported: "Citations non étayées",
      failures: "Échecs d'ingestion",
      costByTenant: "Coût IA estimé par organisation",
      costSummary: "{{total}} pour l'instance sur la période.",
      syntheticTenant: "{{name}} (inclut des données de démonstration synthétiques)",
      perTenant: "Par organisation",
      instanceCosts: "Coûts de l'instance",
      instanceLine: "Poste",
      today: "Aujourd'hui",
      thisMonth: "Ce mois-ci",
      last30Days: "30 derniers jours",
      feedbackTriage: "Tri des retours (ESI)",
    },
    sessions: {
      lead: "Les navigateurs d'opérateurs connectés. En fermer un le déconnecte à sa prochaine requête.",
      current: "Ce navigateur",
      unknownDevice: "Appareil inconnu",
      unknownOperator: "inconnu",
      meta: "Opérateur {{operator}} · vu le {{lastSeen}}",
      revoke: "Déconnecter",
    },
    refused: {
      seatLimit: "L'organisation n'a plus de place disponible.",
    },
  },
  plan: {
    title: "Offre et usage",
    hint: "Les limites de votre offre et l'usage de ce mois-ci.",
    lead: "Votre organisation dispose de l'offre {{name}}. Les opérateurs d'ESI en fixent les limites.",
    adminsOnly: "Seuls les administrateurs de l'organisation et des entités voient l'offre.",
    thisMonth: "Usage et limites ({{month}})",
    seats: "Utilisateurs actifs",
    storage: "Documents stockés",
    ocrPages: "Pages OCR ce mois-ci",
    modelSpend: "Dépense IA estimée ce mois-ci",
    unlimited: "Illimité",
    warningTitle: "Proche d'une limite",
    warningBody: "80 % ou plus utilisés : {{limits}}.",
    reachedTitle: "Limite atteinte",
    reachedBody:
      "Toute nouvelle activité de ce type est refusée jusqu'à ce que l'usage baisse ou que la limite soit relevée.",
    hardSpend:
      "Les nouvelles questions sont refusées et les nouveaux fichiers attendent (en pause) jusqu'au mois prochain ou jusqu'au relèvement de la limite. Le travail en pause reprend ensuite de lui-même.",
    modeSoft:
      "La limite de dépense IA ne fait qu'avertir : les questions continuent de fonctionner au-delà.",
    modeHard:
      "Au-delà de la limite de dépense IA, les nouvelles questions sont refusées et le traitement des fichiers est mis en pause jusqu'au mois prochain.",
    unknownCost_one: "{{count}} appel au coût inconnu ce mois-ci est compté à part.",
    unknownCost_other: "{{count}} appels au coût inconnu ce mois-ci sont comptés à part.",
    synthetic: "Inclut des données de démonstration synthétiques",
    syntheticSpend:
      "{{amount}} de la dépense IA de ce mois-ci sont des données de démonstration synthétiques. Elles sont affichées ici, mais seule la dépense réelle peut refuser des questions ou mettre du travail en pause.",
  },
  supportAccess: {
    title: "Accès support",
    hint: "Approuver ou refuser les demandes du support d'ESI ; voir leur utilisation.",
    lead: "Le support d'ESI ne peut consulter votre administration que si un administrateur de l'organisation l'approuve, pour une durée limitée. Les projets restent fermés sauf si vous les ouvrez. Chacune de ses requêtes figure dans votre journal d'audit.",
    adminsOnly: "Seuls les administrateurs de l'organisation décident des accès support.",
    pending: "Demandes en attente",
    nonePending: "Aucune demande en attente.",
    active: "Accès en cours",
    noneActive: "Aucun accès en cours.",
    history: "Historique",
    noHistory: "Aucun accès passé.",
    operator: "Demandé par",
    operatorRef: "Support d'ESI ({{ref}})",
    reason: "Motif",
    requested: "Demandé",
    requestedFor_one: "{{when}}, pour {{count}} heure",
    requestedFor_other: "{{when}}, pour {{count}} heures",
    metadataOnly:
      "Approuver donne un accès en lecture seule à votre administration (utilisateurs, entités, paramètres, annuaire, audit), pas au contenu des projets.",
    approveFor: "Approuver pour",
    hours_one: "{{count}} heure",
    hours_other: "{{count}} heures",
    approve: "Approuver",
    refuse: "Refuser",
    until: "Jusqu'au",
    content: "Projets ouverts",
    noContent: "Aucun : administration seulement",
    uses: "Utilisation",
    usesNone: "Pas encore utilisé",
    usesLast_one: "{{count}} requête, dernière le {{when}}",
    usesLast_other: "{{count}} requêtes, dernière le {{when}}",
    editContent: "Choisir des projets",
    revoke: "Révoquer maintenant",
    contentTitle: "Ouvrir des projets au support",
    contentBody:
      "Le support consulte les projets choisis en tant que lecteur, jusqu'à la fin de l'accès. Les documents réservés à des lecteurs nommés restent masqués.",
    contentNote: "Décochez tous les projets pour refermer le contenu.",
    noProjects: "Aucun projet d'entité à ouvrir.",
    statusLabel: "Statut",
    status: {
      requested: "En attente",
      active: "En cours",
      refused: "Refusé",
      revoked: "Révoqué",
      expired: "Expiré",
    },
  },
  totp: {
    title: "Validation en deux étapes",
    lead: "Demander un code d'une application d'authentification après votre mot de passe.",
    required: "Votre organisation exige la validation en deux étapes pour votre compte.",
    on: "Activée",
    off: "Désactivée",
    setUp: "Configurer",
    remove: "Désactiver",
    step1:
      "Scannez le code QR avec une application d'authentification, ou saisissez la clé de configuration à la main.",
    step2: "Saisissez le code à 6 chiffres affiché par l'application pour confirmer.",
    qrLabel: "Code QR pour votre application d'authentification",
    key: "Clé de configuration",
    showKey: "Afficher la clé de configuration",
    copy: "Copier la clé",
    copied: "Copiée",
    code: "Code à 6 chiffres",
    confirm: "Confirmer",
    confirming: "Confirmation…",
    enabled: "La validation en deux étapes est activée.",
    removed: "La validation en deux étapes est désactivée.",
    lastFactor:
      "Votre organisation exige une seconde étape : ajoutez une clé d'accès avant de la désactiver.",
  },
  password: {
    title: "Mot de passe",
    current: "Mot de passe actuel",
    new: "Nouveau mot de passe",
    rules: {
      length: "Au moins {{min}} caractères",
      upper: "Une majuscule",
      lower: "Une minuscule",
      digit: "Un chiffre",
      symbol: "Un symbole",
    },
    tooShort: "Le mot de passe est trop court.",
    weak: "Ce mot de passe ne respecte pas les exigences. Utilisez majuscules et minuscules, un chiffre et un symbole.",
    wrongCurrent: "Le mot de passe actuel ou le code est incorrect.",
    set: "Définir le mot de passe",
    change: "Changer le mot de passe",
    changed_one: "Mot de passe changé. {{count}} autre session a été déconnectée.",
    changed_other: "Mot de passe changé. {{count}} autres sessions ont été déconnectées.",
  },
  connect: {
    documentTitle: "Autoriser l'accès",
    loading: "Chargement…",
    title: "Autoriser {{client}} à utiliser ESI ?",
    lead: "Une application demande à agir en votre nom. Vérifiez chaque ligne avant d'autoriser.",
    client: "Application",
    firstParty: "Outil en ligne de commande ESI",
    hostHint: "Publiée par {{host}}",
    redirect: "Vous renvoie vers",
    resource: "Accès",
    resources: {
      api: "API ESI",
      mcp: "Serveur MCP ESI",
    },
    resourceHints: {
      api: "Sans les limites de la politique MCP de votre organisation",
    },
    tenant: "Organisation",
    user: "Connecté en tant que",
    permissions: {
      label: "Elle pourra",
      read: "Lire les connaissances de vos projets, en votre nom",
      everything: "Tout ce que vous pouvez faire dans ESI, en votre nom",
      write:
        "Créer et modifier dans vos projets en votre nom ; jamais supprimer, partager, publier ni changer les membres",
      writeHint:
        "Notes, fichiers, dossiers et propositions de wiki. Les fichiers enregistrés sont marqués comme contenu externe ; les modifications du wiki restent des brouillons tant que personne ne les a relues et publiées dans ESI.",
    },
    warning:
      "N'autorisez que si vous avez lancé cette connexion vous-même, depuis cette application.",
    allow: "Autoriser",
    allowing: "Autorisation…",
    deny: "Refuser",
    errors: {
      title: "Cette demande ne peut pas continuer",
      expired:
        "Cette demande de connexion a expiré ou a déjà servi. Recommencez depuis l'application.",
      otherSession:
        "Cette demande de connexion appartient à une autre session. Recommencez depuis l'application.",
    },
  },
  mcpServer: {
    lead: "Utilisez les connaissances de vos projets depuis Claude Code, VS Code et d'autres clients MCP : cherchez-y, lisez-les, ou demandez à l'agent d'un projet une réponse sourcée. Ils agissent en votre nom, avec vos droits, et ne reçoivent que le contenu que votre organisation autorise à sortir d'ESI.",
    url: "URL du serveur",
    copy: "Copier",
    copied: "Copiée.",
    claudeCode: "Claude Code",
    vscode: "VS Code",
    vscodeHint: "Ajoutez un serveur MCP HTTP avec cette URL (MCP: Add Server).",
    signIn:
      "Le client ouvre la connexion ESI la première fois. Il apparaît ensuite dans Applications connectées.",
    disabled: "Votre organisation a désactivé l'accès depuis les clients MCP.",
  },
  connectedApps: {
    title: "Applications connectées",
    lead: "Les applications de bureau, en ligne de commande et MCP que vous avez autorisées à utiliser ESI en votre nom. En révoquer une la déconnecte aussitôt.",
    empty: "Aucune application connectée.",
    emptyOf: "{{name}} n'a aucune application connectée.",
    connected: "Connectée le {{date}}",
    lastUsed: "Utilisée le {{date}}",
    canWrite: "Peut enregistrer des notes et proposer des brouillons de wiki",
    revoke: "Révoquer",
    revokeNamed: "Révoquer {{client}}",
    revoked: "Accès révoqué.",
  },
  device: {
    documentTitle: "Connecter un appareil",
    title: "Connecter un appareil",
    lead: "Saisissez le code affiché par l'application à laquelle vous vous connectez.",
    codeLabel: "Code",
    continue: "Continuer",
    checking: "Vérification…",
    confirmLead:
      "Une application sur un autre appareil demande à agir en votre nom. Vérifiez chaque ligne avant d'approuver.",
    code: "Code",
    origin: "Demandé depuis",
    unknownCountry: "Lieu inconnu",
    startedAt: "Commencé le {{time}}",
    warning: "Ne continuez que si vous venez de lancer cette connexion vous-même.",
    approve: "Approuver",
    approvedTitle: "Appareil connecté",
    approved: "{{client}} peut désormais utiliser ESI en votre nom.",
    deniedTitle: "Demande refusée",
    denied: "{{client}} n'a pas obtenu l'accès.",
    closeHint: "Vous pouvez fermer cette page et revenir à l'application.",
    errors: {
      format: "Saisissez les 8 lettres du code, telles que l'application les affiche.",
      invalid:
        "Ce code n'est pas valide ou a expiré. Vérifiez-le, ou recommencez depuis l'application.",
      throttled: "Trop de tentatives. Attendez quelques minutes avant de réessayer.",
      otherSession: "Ce code est utilisé dans une autre session. Recommencez depuis l'application.",
      deviceGrantDisabled:
        "Votre organisation n'autorise pas la connexion avec un code d'appareil.",
      clientNotAllowed: "Votre organisation n'autorise pas cette application.",
      signIn: "Reconnectez-vous pour continuer.",
    },
  },
  link: {
    invalid: "Ce lien n'est plus valide.",
    passwordSet:
      "Ce compte a déjà un mot de passe. Connectez-vous, ou réinitialisez votre mot de passe.",
    expiredTitle: "Ce lien n'est plus valide",
    expiredLead:
      "Les liens ne servent qu'une fois et pour une durée limitée. Saisissez votre adresse : nous en enverrons un nouveau si un compte existe.",
    send: "Envoyer un nouveau lien",
    sent: "Si un compte existe pour cette adresse, nous avons envoyé un lien. Consultez votre messagerie.",
    sentTitle: "Consultez votre messagerie",
    sendAgain: "Envoyer un autre lien",
    backToSignIn: "Retour à la connexion",
    invitation: {
      title: "Bienvenue sur ESI",
      lead: "Choisissez un mot de passe pour terminer la création de votre compte.",
      joinTitle: "Rejoindre {{tenant}}",
      invitedBy:
        "Invitation de {{name}}. Choisissez un mot de passe pour terminer la création de votre compte.",
      requestTitle: "Recevoir un nouveau lien d'invitation",
      requestLead:
        "Saisissez votre adresse professionnelle. Si une invitation l'attend, nous enverrons un nouveau lien.",
    },
    reset: {
      title: "Choisissez un nouveau mot de passe",
      lead: "Votre nouveau mot de passe vous déconnecte partout ailleurs.",
      requestTitle: "Réinitialiser votre mot de passe",
      requestLead:
        "Saisissez votre adresse professionnelle. Si un compte existe, nous enverrons un lien pour le réinitialiser.",
    },
  },
  esiCredential: {
    title: "Connexion ESI",
    on: "Activée",
    refusedBadge: "Non autorisée",
    off: "Désactivée",
    addLead:
      "Vous vous connectez avec la connexion de votre organisation. Vous pouvez aussi ajouter un mot de passe ou une clé d'accès ESI, pour entrer quand elle est indisponible.",
    heldLead:
      "Vous pouvez vous connecter avec votre mot de passe ou clé d'accès ESI, en plus de la connexion de votre organisation.",
    notAllowed:
      "Le mode de connexion de votre domaine n'autorise plus la connexion ESI. Votre mot de passe et vos clés d'accès sont conservés mais refusés jusqu'à ce qu'un administrateur l'autorise de nouveau.",
    add: "Ajouter un mot de passe ou une clé d'accès",
    sending: "Envoi…",
    sent: "Nous avons envoyé un lien à {{email}}. Ouvrez-le sous 72 heures pour choisir votre mot de passe ; vous pourrez ensuite ajouter une clé d'accès.",
    removeEllipsis: "Retirer la connexion ESI…",
    removeTitle: "Retirer votre mot de passe et vos clés d'accès ESI ?",
    removeBody: "Vous continuez à vous connecter avec la connexion de votre organisation.",
    removeSignsOut: "Cette session les a utilisés : elle prend fin aussi.",
    remove: "Retirer la connexion ESI",
    removing: "Retrait…",
    removed: "La connexion ESI est retirée.",
    refused: {
      throttled: "Trop de liens de connexion ont été envoyés. Réessayez plus tard.",
      notAllowed: "Le mode de connexion de votre domaine n'autorise pas la connexion ESI.",
      conflict:
        "Un lien est déjà en route, ou un autre compte de connexion utilise cette adresse. Vérifiez votre boîte de réception, ou contactez un administrateur.",
    },
  },
  stepUp: {
    title: "Confirmez votre identité",
    lead: "Par sécurité, saisissez à nouveau votre mot de passe avant cette modification.",
    orPassword: "ou votre mot de passe et le code d'authentification",
    confirm: "Confirmer",
    federated:
      "La connexion de votre organisation vérifie votre identité. Reconnectez-vous avec elle, puis revenez à cette modification.",
    signInAgain: "Se reconnecter",
  },
  memory: {
    proposal: {
      saveForMe: "Garder pour moi",
      suggestProject: "Proposer au projet",
      suggesting: "Envoi…",
      suggestedProject: "Envoyé à {{project}} pour validation.",
      addedProject: "Ajouté à la mémoire de {{project}}.",
      viewProject: "Voir la mémoire du projet",
      region: "Suggestion de mémoire",
      suggested: "Retenir ceci ?",
      requested: "Enregistrer en mémoire ?",
      preference: "Préférence, s'applique à toutes vos conversations",
      fact: "Fait, utilisé seulement dans {{project}}",
      factStandalone: "Fait, utilisé dans vos conversations",
      thisProject: "ce projet",
      sources_one: "S'appuie sur {{count}} source du projet",
      sources_other: "S'appuie sur {{count}} sources du projet",
      unverified: "Non vérifié : issu de résultats externes",
      textLabel: "Texte de la mémoire",
      save: "Enregistrer",
      saving: "Enregistrement…",
      edit: "Modifier",
      dismiss: "Ignorer",
      cancel: "Annuler",
      saved: "Enregistré en mémoire.",
      manage: "Gérer la mémoire",
      dismissed: "Non enregistré. Cette suggestion ne sera plus proposée.",
      gone: "Cette suggestion n'est plus disponible.",
      invalid:
        "Écrivez une seule phrase simple de 300 caractères au plus, sans lien ni mise en forme.",
      remaining: "{{count}} caractères restants",
    },
    project: {
      notAllowed: "Votre rôle dans ce projet n'inclut pas sa mémoire.",
      lead: "Les faits sur lesquels l'équipe du projet s'est accordée. L'assistant les utilise dans les conversations de chaque membre ici, comme des informations, jamais comme des instructions.",
      leadManager:
        "Les faits sur lesquels l'équipe du projet s'est accordée. L'assistant les utilise dans les conversations de chaque membre ici. Les propositions des contributeurs attendent votre validation ou celle d'un autre gestionnaire.",
      off: "La mémoire du projet est désactivée pour ce projet. Les faits enregistrés ne sont pas utilisés ; vous pouvez toujours les consulter et les supprimer.",
      filter: "Afficher",
      agreed: "Validés",
      queue: "En attente de validation",
      mine: "Mes propositions",
      search: "Rechercher dans la mémoire du projet",
      empty:
        "Aucun fait validé pour l'instant. Les membres les proposent depuis les réponses de ce projet.",
      emptyQueue: "Rien en attente de validation.",
      emptyMine: "Vous n'avez aucune proposition en attente.",
      noMatch: "Aucune mémoire du projet ne correspond à cette recherche.",
      hidden: "Masqué : s'appuie sur des sources que vous ne pouvez pas lire",
      someone: "un ancien membre",
      addedBy: "Ajouté par {{author}}",
      suggestedApproved: "Proposé par {{author}}, validé par {{decider}}",
      suggestedBy: "Proposé par {{author}}",
      yourSuggestion: "Votre proposition",
      expires: "Expire le {{date}}",
      restricted_one: "{{count}} membre inscrit ne peut pas lire ses sources",
      restricted_other: "{{count}} membres inscrits ne peuvent pas lire ses sources",
      approve: "Valider",
      editApprove: "Modifier et valider",
      reject: "Refuser",
      rejectHidden: "Refuser la proposition masquée",
      withdraw: "Retirer",
      approved: "Validé. Les membres en bénéficient dès leur prochain message.",
      rejected: "Proposition refusée.",
      withdrawn: "Proposition retirée.",
      deleted: "Supprimé de la mémoire du projet.",
      deleteHidden: "Supprimer la mémoire du projet masquée",
      deleteTitle: "Supprimer ce fait pour tout le monde ?",
      deleteEffects: {
        everyone:
          "L'assistant cesse de l'utiliser dès le prochain message de chaque membre dans ce projet.",
        audited: "La suppression est enregistrée dans l'activité du projet, sans son texte.",
      },
      confirmDelete: "Supprimer pour tout le monde",
      gone: "Cette mémoire n'est plus disponible.",
      tabPending_one: "{{count}} en attente de validation",
      tabPending_other: "{{count}} en attente de validation",
    },
    panel: {
      export: "Exporter",
      exporting: "Préparation du fichier…",
      exported: "{{file}} téléchargé.",
      exportFailed: "Impossible d'exporter votre mémoire. Réessayez.",
      suggest: "Proposer au projet",
      suggestedProject: "Envoyé au projet pour validation.",
      queueFull:
        "Ce projet a déjà trop de propositions en attente de validation. Réessayez plus tard.",
      duplicate: "Le projet a déjà cette mémoire, validée ou en attente de validation.",
      lead: "ESI ne retient que ce que vous enregistrez. Les préférences s'appliquent à toutes vos conversations ; les faits restent dans le projet d'où ils viennent et suivent ses sources.",
      locked:
        "La mémoire est désactivée par votre organisation. Vous pouvez toujours consulter et supprimer ce que vous avez enregistré.",
      filter: "Afficher",
      preferences: "Préférences",
      facts: "Faits",
      search: "Rechercher dans la mémoire",
      add: "Ajouter",
      addTitle: "Ajouter une mémoire",
      addDescription:
        "Écrivez une phrase simple sur vous ou votre travail. ESI la transmet à l'assistant comme une information, jamais comme une instruction.",
      kind: "Type",
      text: "Texte",
      preferenceHint: "Sur vous : rôle, langue, ton, format des réponses. Utilisée partout.",
      factHint: "Sur votre travail. Ajouté ici, il est utilisé dans toutes vos conversations.",
      emptyPreferences:
        "Aucune préférence. Écrivez « retiens … » dans une conversation, ou ajoutez-en une ici.",
      emptyFacts: "Aucun fait. ESI en propose sous les réponses ; rien n'est enregistré sans vous.",
      noMatch: "Aucune mémoire ne correspond à cette recherche.",
      noProject: "Sans projet",
      unknownProject: "Un projet que vous ne pouvez pas ouvrir",
      lastUsed: "Utilisé le {{date}}",
      neverUsed: "Pas encore utilisé",
      expiring: "Supprimé le {{date}} s'il n'est pas utilisé",
      unverified: "Non vérifié",
      hidden: "Masqué : s'appuie sur des sources que vous ne pouvez pas lire",
      actions: "Actions pour cette mémoire",
      edit: "Modifier",
      delete: "Supprimer",
      deleteHidden: "Supprimer la mémoire masquée",
      deleteAll: "Supprimer toute la mémoire",
      deleteAllTitle: "Supprimer toute votre mémoire ?",
      deleteAllEffects: {
        everything:
          "Toutes les préférences et tous les faits enregistrés sont supprimés, y compris ceux masqués.",
        nextTurn: "L'assistant cesse de les utiliser dès votre prochain message.",
        kept: "Vos conversations et vos fichiers ne sont pas modifiés.",
      },
      confirmDeleteAll: "Tout supprimer",
      deleting: "Suppression…",
      saved: "Mémoire enregistrée.",
      deleted: "Mémoire supprimée.",
      allDeleted_one: "{{count}} mémoire supprimée.",
      allDeleted_other: "{{count}} mémoires supprimées.",
      kindLocked:
        "Ce fait vient d'un projet ou de ses sources : il ne peut pas devenir une préférence.",
    },
  },
  accountSettings: {
    title: "Paramètres du compte",
    lead: "Votre profil, vos préférences, votre mémoire, la sécurité de connexion et vos sessions actives.",
    myFeedback: "Mes retours",
    suggestFeedback: "Suggérer des retours",
    suggestFeedbackHelp:
      "Après une panne dans une conversation, l'assistant peut proposer un signalement rédigé. Rien n'est envoyé sans un clic sur Envoyer. Lui demander de signaler un problème fonctionne toujours.",
    sections: {
      profile: "Profil",
      preferences: "Préférences",
      security: "Connexion et sécurité",
      sessions: "Sessions actives",
      memory: "Mémoire",
      connectedApps: "Applications connectées",
      mcp: "Serveur MCP",
    },
    federatedSecurity:
      "La connexion de votre organisation gère votre mot de passe et la validation en deux étapes.",
    federatedSecurityBeside:
      "La connexion de votre organisation gère son propre mot de passe et sa validation en deux étapes. La connexion ESI, ci-dessous, est distincte.",
    profile: {
      name: "Nom affiché",
      email: "Adresse e-mail",
      fromIdp: "Fourni par la connexion de votre organisation",
    },
    saved: "Enregistré.",
    preferencesNotSaved:
      "Appliqué ici, mais pas enregistré dans votre compte. Réessayez plus tard.",
  },
  passkeys: {
    title: "Clés d'accès",
    lead: "Connectez-vous avec l'empreinte, le visage ou le verrouillage de votre appareil au lieu d'un mot de passe.",
    unsupported: "Ce navigateur ne prend pas en charge les clés d'accès.",
    reveal: "Confirmez votre identité pour voir vos clés d'accès",
    none: "Aucune clé d'accès pour l'instant.",
    unnamed: "Clé d'accès",
    created: "Ajoutée le {{date}}",
    removeNamed: "Supprimer {{name}}",
    add: "Ajouter une clé d'accès",
    added: "Clé d'accès ajoutée.",
    removed: "{{name}} supprimée.",
    lastFactor:
      "Votre organisation exige une seconde étape : configurez une application d'authentification avant de supprimer votre dernière clé d'accès.",
    cancelled: "La clé d'accès n'a pas été ajoutée. Réessayez.",
    removeTitle: "Supprimer {{name}} ?",
    removeLead:
      "Vous ne pourrez plus vous connecter avec cette clé d'accès. Vos autres moyens de connexion restent valables.",
    removeConfirm: "Supprimer la clé d'accès",
    addConfirmed: "Ajouter la clé d'accès",
    addReady: "Votre identité est confirmée. Ajoutez la clé d'accès pour terminer.",
  },
  sessions: {
    lead: "Les navigateurs où vous êtes connecté. Fermer une session n'affecte pas les clients de bureau ni MCP.",
    unknownDevice: "Appareil inconnu",
    current: "Cette session",
    network: "Réseau {{network}}",
    signedIn: "Connecté le {{date}}",
    lastSeen: "Actif le {{date}}",
    signOut: "Déconnecter",
    signOutNamed: "Déconnecter {{device}}",
    signOutOthers: "Déconnecter les autres sessions",
    revoked_one: "{{count}} session déconnectée.",
    revoked_other: "{{count}} sessions déconnectées.",
  },
  projects: {
    title: "Projets",
    lead: "Chaque projet a ses propres connaissances, membres et conversations.",
    empty: "Vous ne faites encore partie d'aucun projet. Créez-en un pour ajouter des fichiers.",
    create: "Nouveau projet",
    createLead: "Un projet réunit des fichiers, un wiki et les personnes qui travaillent avec.",
    name: "Nom",
    owner: "Propriétaire",
    ownerLabel: { personal: "Personnel", entity: "Votre entité" },
    ownerHint: {
      personal: "Seuls les membres que vous ajoutez peuvent le voir.",
      entity: "Appartient à votre entité ; ses administrateurs le gèrent avec vous.",
    },
    visibilityLabel: "Qui peut le voir",
    createSubmit: "Créer le projet",
    creating: "Création…",
    list: "Vos projets",
  },
  project: {
    breadcrumb: "Fil d'Ariane",
    backToProjects: "Retour aux projets",
    personal: "Projet personnel",
    entityOwned: "Appartient à {{entity}}",
    visibility: {
      private: "Membres uniquement",
      entity: "Membres et entité propriétaire",
      entity_tree: "Membres, entité propriétaire et ses sous-entités",
    },
    yourRole: "Votre rôle : {{role}}",
    frozen:
      "Ce projet est en lecture seule. Les sources et les membres sont consultables mais pas modifiables.",
    frozenShort: "Lecture seule",
    conversation: "Conversation",
    knowledge: "Connaissances",
    openKnowledge: "Connaissances",
    members: "Membres",
    tabs: { files: "Sources", wiki: "Wiki" },
    residencySource: {
      instance: "Défini pour cette instance.",
      tenant: "Défini par votre organisation.",
      entity: "Défini par l'entité propriétaire.",
      project: "Défini pour ce projet.",
    },
  },
  settings: {
    accessRequests: {
      lead: "Des administrateurs qui gouvernent ce projet demandent des changements qui élargiraient qui le lit. Rien ne change tant que vous n’avez pas approuvé.",
      requestedBy: "Demandé par {{name}} · {{date}}",
      expires: "Expire le {{date}}",
      group: "{{approved}} projets sur {{size}} ont approuvé",
      reason: "Motif : {{reason}}",
      approve: "Approuver",
      reject: "Refuser",
      approveLabel: "Approuver : {{change}}, demandé par {{name}}",
      rejectLabel: "Refuser : {{change}}, demandé par {{name}}",
      own: "Votre propre demande : un autre propriétaire ou gestionnaire décide.",
      approved: "Approuvée. Le changement s’applique quand chaque projet concerné a approuvé.",
      applied: "Approuvée et appliquée.",
      rejected: "Refusée. Rien n’a changé.",
    },
    entry: "Paramètres",
    title: "Paramètres",
    lead: "Comment ce projet est configuré et qui peut l'utiliser.",
    pages: {
      navLabel: "Pages des paramètres",
      search: {
        label: "Rechercher un paramètre",
        placeholder: "Rechercher, par ex. recherche web ou OCR",
      },
      pendingCount_one: "{{count}} demande en attente",
      pendingCount_other: "{{count}} demandes en attente",
      setHereCount_one: "{{count}} défini pour ce projet",
      setHereCount_other: "{{count}} définis pour ce projet",
      summary: {
        setHere: "Définis pour ce projet :",
        lockedAbove: "Verrouillés au-dessus :",
        allInherited: "Tous les paramètres de cette page sont hérités",
      },
      empty: "Rien sur cette page ne peut être réglé sur ce déploiement.",
      general: {
        label: "Général",
        purpose: "Le nom du projet, sa description et qui peut l'ouvrir.",
      },
      members: {
        label: "Membres et accès",
        purpose:
          "Qui travaille dans ce projet et avec quel rôle, et les changements d'accès qui attendent son approbation.",
      },
      assistant: {
        label: "Assistant",
        purpose:
          "Comment l'assistant répond ici, quels modèles il utilise et ce qu'il retient. Les autres paramètres sont fixés pour votre organisation.",
      },
      knowledge: {
        label: "Connaissances",
        purpose:
          "Comment les fichiers du projet sont lus et classifiés, comment les brouillons du wiki sont rédigés, et ce que les clients MCP peuvent utiliser.",
      },
      web: {
        label: "Web",
        purpose: "Ce que l'assistant peut chercher sur le web pour ce projet.",
      },
      danger: {
        label: "Zone de danger",
        purpose: "Supprimer ce projet et tout ce qu'il contient.",
      },
    },
    sections: {
      general: "Général",
      members: "Membres et groupes",
      behaviour: "Comportement",
      requests: "Demandes d’accès",
    },
    readOnly: "Seuls le propriétaire et les responsables de projet peuvent modifier ceci.",
    save: "Enregistrer",
    saving: "Enregistrement…",
    saved: "Enregistré.",
    cancel: "Annuler",
    general: {
      name: "Nom",
      description: "Description",
      noDescription: "Aucune description.",
      visibility: "Qui peut l'ouvrir",
      personalOnly: "Un projet personnel est toujours privé.",
      visibilityHint: {
        private: "Seules les personnes ajoutées au projet, ou via un groupe, peuvent l'ouvrir.",
        entity: "Les membres de {{entity}} peuvent aussi l'ouvrir, en lecture.",
        entity_tree:
          "Les membres de {{entity}} et de ses sous-entités peuvent aussi l'ouvrir, en lecture.",
      },
      ownerFallback: "l'entité propriétaire",
      widenTitle: "Ouvrir le projet à davantage de personnes ?",
      widen: {
        entity: "Les membres de {{entity}} pourront ouvrir le projet en lecture.",
        entity_tree:
          "Les membres de {{entity}} et de ses sous-entités pourront ouvrir le projet en lecture.",
      },
      widenConfirm: "Changer qui peut l'ouvrir",
    },
    members: {
      add: "Ajouter",
      addTitle: "Ajouter au projet",
      addLead: "Donnez un rôle dans ce projet à une personne, un groupe ou une règle d'attributs.",
      addKind: "Quoi ajouter",
      filter: "Afficher",
      kinds: { all: "Tous", person: "Personnes", group: "Groupes", rule: "Règles" },
      email: "E-mail",
      emailHint: "La personne doit avoir un compte actif dans votre organisation.",
      person: {
        label: "Personne",
        placeholder: "Nom ou adresse e-mail",
        hint: "Saisissez au moins 2 lettres de son nom ou de son adresse e-mail.",
        searching: "Recherche…",
        results: "Personnes",
        found_one: "{{count}} personne trouvée. Choisissez avec les flèches.",
        found_other: "{{count}} personnes trouvées. Choisissez avec les flèches.",
        member: "Déjà membre",
        none: "Personne ne correspond. Vérifiez l'orthographe, ou saisissez son adresse e-mail complète.",
        noneEmail: "Personne ne correspond. Ajouter utilisera cette adresse e-mail telle quelle.",
        throttled:
          "Trop de recherches. Patientez un instant, ou saisissez son adresse e-mail complète.",
        failed:
          "La recherche de personnes a échoué. Vous pouvez saisir son adresse e-mail complète.",
        clear: "Choisir quelqu'un d'autre",
      },
      search: { group: "Rechercher un groupe", rule: "Rechercher une règle" },
      pick: { group: "Groupes", rule: "Règles" },
      noMatch: { group: "Aucun groupe ne correspond.", rule: "Aucune règle ne correspond." },
      noneLeft: {
        group: "Tous les groupes ont déjà un rôle ici.",
        rule: "Toutes les règles ont déjà un rôle ici.",
      },
      role: "Rôle",
      addSubmit: "Ajouter",
      adding: "Ajout…",
      memberSince: "Membre depuis le {{date}}",
      breakGlass: "Accès d'urgence",
      rule: "Règle d'attributs d'annuaire",
      empty: {
        all: "Personne n'a encore de rôle dans ce projet.",
        person: "Aucune personne n'a de rôle dans ce projet.",
        group: "Aucun groupe n'a de rôle dans ce projet.",
        rule: "Aucune règle d'attributs n'a de rôle dans ce projet.",
      },
      roleOf: "Rôle de {{name}}",
      removeTitle: "Retirer {{name}} du projet ?",
      removeBody: {
        person:
          "Cette personne perd l'accès au projet, sauf si sa visibilité ou l'un de ses groupes le lui donne.",
        group:
          "Les membres du groupe perdent le rôle qu'il leur donne, sauf si un autre accès le leur donne.",
        rule: "Les personnes qui correspondent à la règle perdent le rôle qu'elle leur donne, sauf si un autre accès le leur donne.",
      },
      removeConfirm: {
        person: "Retirer le membre",
        group: "Retirer le groupe",
        rule: "Retirer la règle",
      },
      actionsOf: "Actions pour {{name}}",
      remove: "Retirer du projet",
      ownerOnly: "Seul le propriétaire peut transférer la propriété.",
      guestLifted:
        "Si cette personne appartient à l'entité propriétaire, la visibilité du projet lui permet de l'ouvrir comme {{role}}.",
      transfer: "Transférer la propriété",
      transferTitle: "Transférer la propriété à {{name}} ?",
      transferBody: "{{name}} devient propriétaire et vous devenez responsable de projet.",
      transferPoints: {
        owner:
          "Seul le propriétaire peut supprimer le projet ou transférer à nouveau la propriété.",
        you: "Vous continuez à gérer les membres, les paramètres et le wiki.",
      },
      transferConfirm: "Transférer la propriété",
      transferring: "Transfert…",
      refused: {
        ownerSelf: "Transférez d'abord la propriété à un autre membre.",
        ownerProtected:
          "Seul le propriétaire peut changer son propre rôle, en transférant la propriété.",
        ceiling: "Seul un transfert de propriété fait de quelqu'un le propriétaire.",
        personalTransfer:
          "Un projet personnel reste à la personne qui le possède. Rattachez-le à une entité pour le transférer.",
        notDirect: "La propriété ne peut aller qu'à une personne ajoutée directement au projet.",
        breakGlassOwner: "La propriété ne peut pas aller à un accès d'urgence.",
        outsideEntity:
          "Les paramètres de votre organisation n'autorisent que les membres de l'entité propriétaire dans ce projet.",
        breakGlass: "L'accès d'urgence est toujours en lecture seule (Lecteur).",
        notFound: "Aucune personne active avec cet e-mail dans votre organisation.",
        already: "Cette personne est déjà membre.",
        disabled: "Le compte de cette personne est désactivé.",
        frozen: "Ce projet est désormais en lecture seule.",
      },
    },
    behaviour: {
      lead: "Les paramètres que ce projet peut ajuster. Les autres sont définis pour votre organisation dans l'administration.",
      /** Under a tool group's switch: the disclosure listing its tools by name. */
      mcpGroupTools: "Outils de ce groupe",
      modelsLink:
        "Laboratoires, familles, versions figées et état des modèles pour l'entité de ce projet",
      groups: {
        answers: "Réponses",
        models: "Modèles",
        knowledge: "Fichiers et classification",
        wiki: "Wiki",
        mcp: "Clients externes (MCP)",
        web: "Accès au web",
      },
      source: {
        project: "Défini pour ce projet",
        tenant: "Hérité de votre organisation",
        entity: "Hérité de {{entity}}",
        entityUnknown: "Hérité d'une entité parente",
        instance: "Par défaut",
      },
      locked: {
        tenant: "Verrouillé par votre organisation",
        entity: "Verrouillé par {{entity}}",
        entityUnknown: "Verrouillé par une entité parente",
        instance: "Fixé pour ce déploiement",
      },
      reset: "Revenir à la valeur héritée",
      change: "Modifier",
      on: "Activé",
      off: "Désactivé",
      narrowOnly: "Ne peut être que plus strict que la valeur héritée.",
      models: { locked: "{{effort}} uniquement (effort figé)" },
      keys: {
        idpGroupsMaxAge: {
          label: "Validité des groupes du fournisseur d'identité",
          help: "En heures, de 1 à 168. L'accès donné par les groupes que transmet votre fournisseur d'identité expire ce délai après la dernière connexion de la personne par ce fournisseur. Les groupes venant de SCIM ou d'autres sources d'annuaire n'expirent pas.",
        },
        nativeClients: {
          label: "Applications connectées",
          help: "Les applications de bureau, en ligne de commande et MCP que chacun peut connecter à son compte. Chaque connexion est journalisée et révocable.",
        },
        mcpEnabled: {
          label: "Accès depuis les clients MCP",
          help: "Permet d'utiliser les connaissances des projets depuis des clients MCP comme Claude Code ou VS Code, en son nom et avec ses propres droits. Une entité ou un projet peut seulement le désactiver.",
        },
        mcpToolsKnowledge: {
          label: "Outils de connaissances pour les clients MCP",
          help: "Permet aux clients MCP de chercher dans les connaissances, de lire des documents et de travailler sur les notes, les fichiers et les dossiers.",
        },
        mcpToolsWiki: {
          label: "Outils de wiki pour les clients MCP",
          help: "Permet aux clients MCP de lire les pages du wiki et de proposer des brouillons.",
        },
        mcpToolsConversations: {
          label: "Outils de conversation pour les clients MCP",
          help: "Permet aux clients MCP d'interroger l'assistant d'un projet.",
        },
        mcpToolsTasks: {
          label: "Outils de tâches pour les clients MCP",
          help: "Permettra aux clients MCP de travailler sur les tâches, quand ces outils existeront.",
        },
        mcpToolsProjects: {
          label: "Outils de projets pour les clients MCP",
          help: "Permet aux clients MCP de lire la synthèse d’un projet : ses chiffres en un coup d’œil.",
        },
        mcpWriteTools: {
          label: "Enregistrement depuis les clients MCP",
          help: "Activé par défaut. Permet aux clients MCP qu'une personne a autorisés à écrire d'enregistrer des notes, de déposer et modifier des fichiers, d'organiser les dossiers (en ne supprimant que les dossiers vides) et de proposer des brouillons de wiki dans les projets, en son nom et dans les limites de son rôle. Ce qu'ils enregistrent est marqué comme contenu externe ; les modifications du wiki restent des brouillons jusqu'à leur publication dans ESI. Il ne s'applique que s'il est activé pour toute l'organisation ; une entité ou un projet peut alors le désactiver.",
        },
        mcpSelfPublish: {
          label: "Publier ses propres modifications de wiki MCP",
          help: "Désactivé par défaut : une modification de wiki proposée par le client MCP d\u{2019}une personne est publiée par une autre, qui la lit d\u{2019}abord. Activez-le pour un projet n\u{2019}ayant qu\u{2019}un contributeur ; chaque publication de ce type est enregistrée dans le journal d\u{2019}activité.",
        },
        mcpCeiling: {
          label: "Contenu le plus sensible transmis aux clients MCP",
          help: "Un contenu classé au-dessus n'est jamais recherché ni transmis à un client MCP, même si la personne peut le lire dans ESI. Une entité ou un projet peut seulement l'abaisser.",
        },
        assistedTriage: {
          label: "Assistant de tri pour vos retours",
          help: "L'assistant de tri de l'équipe ESI peut lire les signalements de votre organisation pour suggérer comment les regrouper, sur les modèles propres d'ESI dans l'UE. Désactivé : vos signalements sont triés uniquement à la main.",
        },
        feedbackEnabled: {
          label: "Retours à l'équipe ESI",
          help: "Chacun peut signaler un bug ou proposer une idée à l'équipe ESI depuis l'application. La personne voit exactement ce qui est envoyé ; rien de ses projets ne l'accompagne. Chaque envoi apparaît dans le journal d'audit, sans son texte.",
        },
        feedbackAgentSuggestions: {
          label: "Suggestions de retours par l'assistant",
          help: "Après une panne dans une conversation (un outil en échec, une fonction absente d'ESI), l'assistant peut proposer un signalement rédigé. Rien n'est envoyé sans un clic sur Envoyer, et rien de la conversation ne l'accompagne. Désactivé : l'assistant rédige toujours un signalement quand on le lui demande.",
        },
        clientTelemetry: {
          label: "Rapports de santé de l'application",
          help: "Les navigateurs signalent à ESI les erreurs de l'application, les requêtes en échec et la vitesse des pages, pour corriger les pannes plus vite. Jamais le contenu des pages, de noms, d'adresses ni d'identifiants. Rien ne sort d'ESI.",
        },
        jitProvisioning: {
          label: "Créer les comptes à la première connexion",
          help: "Activé : toute personne que votre fournisseur d'identité connecte depuis un domaine vérifié obtient un compte à sa première connexion. Désactivé : seules les personnes invitées ou créées par une synchronisation d'annuaire peuvent se connecter ; les autres voient l'échec de connexion habituel.",
        },
        deviceGrant: {
          label: "Connexion avec un code d'appareil",
          help: "Permet de connecter des applications sans navigateur (par SSH, dans des conteneurs) en approuvant un code sur un autre appareil.",
        },
        mfa: {
          label: "Second facteur",
          help: "Les comptes natifs se connectent aussi avec une application d'authentification ou une clé d'accès, de même que les mots de passe ESI sur les domaines SSO (leur mode de connexion se règle sur la page Connexion). Les connexions par un fournisseur d'identité en dépendent.",
        },
        webSearch: {
          label: "Recherche sur le web",
          help: "Activé par défaut. L'assistant peut chercher sur le web public.",
        },
        modelsAllowed: {
          label: "Niveaux d'effort",
          help: "L'effort que peuvent demander les réponses : Rapide, Équilibré ou Approfondi. Un seul niveau le fige.",
        },
        defaultEffort: {
          label: "Effort par défaut",
          help: "L'effort de départ des nouvelles conversations. Il doit faire partie des efforts autorisés pour le projet.",
        },
        advancedPicker: {
          label: "Permettre de choisir une autre famille",
          help: "Les utilisateurs peuvent faire tourner un effort sur une autre famille de modèles de vos laboratoires, jamais sur une version précise.",
        },
        turnMaxUsd: {
          label: "Coût maximal d'une réponse",
          help: "Coût IA estimé, en USD, au-delà duquel une réponse s'arrête. Il borne chaque réponse, pas le total mensuel de chaque personne.",
        },
        sharePressureThresholdPct: {
          label: "Seuil de pression du budget",
          help: "Pourcentage du budget IA mensuel utilisé à partir duquel les parts individuelles s’appliquent. Le budget restant au premier franchissement est partagé à parts égales entre les personnes ayant utilisé l’IA ce mois-ci. Chaque nouvelle personne active réduit la part de chacun. Les administrateurs reçoivent un événement d’audit pour l’organisation une fois par mois.",
        },
        allowedModels: {
          label: "Modèles autorisés",
          help: "Seuls ces modèles peuvent servir les réponses et les tâches de fond de ce projet.",
        },
        allowPersonal: {
          label: "Projets personnels",
          help: "Chacun peut créer ses propres projets.",
        },
        shareOutsideEntity: {
          label: "Membres d'autres entités",
          help: "Les projets peuvent ajouter des membres hors de l'entité propriétaire et de ses sous-entités.",
        },
        maxUploadMb: {
          label: "Taille maximale d'un fichier",
          help: "Le plus gros fichier qu'un projet accepte.",
        },
        classificationScheme: {
          label: "Niveaux de classification",
          help: "Du moins au plus sensible, pour toute l'organisation.",
        },
        ocrMonthlyPages: {
          label: "Budget mensuel de numérisation",
          help: "Pages lues par OCR chaque mois dans l'organisation, au plus le budget de l'opérateur.",
        },
        sourcesOnly: {
          label: "Répondre uniquement à partir des sources du projet",
          help: "L'assistant refuse plutôt que d'utiliser ses connaissances générales.",
        },
        memoryRetention: {
          label: "Conservation de la mémoire",
          help: "Nombre de jours de conservation d'un souvenir qu'aucune réponse n'utilise et que personne ne modifie. S'applique à la mémoire personnelle et de projet, pour toute l'organisation.",
        },
        tasks: {
          label: "Tâches",
          help: "Les membres peuvent créer et gérer les tâches du projet. Les désactiver laisse les tâches existantes lisibles et permet aux propriétaires et gestionnaires de les supprimer.",
        },
        memoryPersonal: {
          label: "Mémoire personnelle",
          help: "L'assistant peut proposer des mémoires sous les réponses et utiliser celles que chacun a enregistrées. Rien n'est enregistré sans la personne ; chacun ne voit que les siennes.",
        },
        memoryProject: {
          label: "Mémoire du projet",
          help: "Les membres peuvent proposer des faits pour tout le projet ; les propriétaires et gestionnaires les approuvent avant que l'assistant les utilise dans les conversations du projet. Désactivée, les mémoires enregistrées restent gérables.",
        },
        generatedSources: {
          label: "Sources générées dans les réponses",
          help: "Les réponses peuvent s'appuyer sur les fichiers rédigés par l'assistant et ajoutés aux connaissances, et sur les réponses enregistrées à partir d'eux. Désactivé : ces fichiers restent listés et téléchargeables, mais l'assistant ne les lit ni ne les cite plus, n'en rédige plus de résumé automatique, et ne propose ni n'utilise de mémoires tirées de ce qu'il a lui-même écrit.",
        },
        attachments: {
          label: "Pièces jointes des conversations",
          help: "Les personnes peuvent joindre des fichiers de travail à une conversation. Désactiver les envois conserve les fichiers lisibles existants et les téléchargements autorisés.",
        },
        artefacts: {
          label: "Artefacts",
          help: "L'assistant peut créer et réviser des documents, du code, des diagrammes et des tableaux conservés à côté de la conversation. Le désactiver laisse les artefacts existants lisibles.",
        },
        thinkingSummary: {
          label: "Résumés de réflexion",
          help: "Chaque étape à laquelle l'assistant réfléchit reçoit un résumé d'une ligne dans les étapes de sa réponse, rédigé par ESI et visible seulement des personnes qui peuvent lire la réponse. Le raisonnement du modèle n'est jamais conservé ni affiché. Désactivé : l'étape n'affiche que sa durée.",
        },
        standalone: {
          label: "Conversations hors projet",
          help: "Chacun peut lancer des conversations sans projet ni connaissance de projet, privées. Les réglages suivent l'entité actuelle de la personne ; l'usage reste à l'entité d'origine.",
        },
        residency: {
          label: "Où les modèles s'exécutent",
          help: "Mondial : modèles mondiaux, avec repli dans l'UE. Les données sont toujours stockées dans l'UE.",
        },
        defaultClassification: {
          label: "Classification par défaut",
          help: "Attribuée aux nouveaux fichiers, sauf choix contraire de la personne qui dépose.",
        },
        ocr: {
          label: "Lire les fichiers numérisés",
          help: "Les PDF numérisés et les images sont lus par un modèle de vision.",
        },
        imageCaptions: {
          label: "Décrire les images",
          help: "Les images déposées reçoivent une courte description que les réponses peuvent citer.",
        },
        ocrMaxPages: {
          label: "Nombre maximal de pages lues par numérisation",
          help: "Les numérisations plus longues ne sont pas lues automatiquement.",
        },
        declassification: {
          label: "Propriétaire et responsables peuvent déclassifier",
          help: "Le propriétaire et les responsables de projet peuvent publier un contenu dérivé à des lecteurs que ses sources excluraient.",
        },
        guidelines: {
          label: "Consignes de rédaction du wiki",
          help: "Comment les brouillons sont rédigés : langue, types de page et structure.",
        },
        reviewAfterDays: {
          label: "Relire les pages du wiki après",
          help: "Une page indique « Peut-être obsolète » une fois ce nombre de jours écoulé depuis la dernière confirmation de son exactitude, ou depuis sa publication. Désactivé par défaut.",
        },
        placement: {
          label: "Emplacement des nouvelles pages",
          help: "Le dossier du wiki où commence chaque type de page proposée par l’assistant, un résumé de fichier ou un client MCP. Un dossier absent renvoie à la racine du wiki ; la relecture peut toujours choisir un autre dossier.",
        },
        autoDraftSummaries: {
          label: "Rédiger un résumé de chaque nouveau fichier",
          help: "Chaque fichier prêt reçoit un brouillon de résumé dans la file de relecture du wiki.",
        },
        wikiSuggestions: {
          label: "Suggestions wiki",
          help: "L'assistant peut suggérer une page wiki sous une réponse sourcée. Rien n'est rédigé avant que l'utilisateur choisisse « En faire un brouillon », et une personne relit le brouillon avant sa publication. L'enregistrement d'une réponse dans le wiki n'est pas concerné.",
        },
        wikiTrust: {
          label: "Qui peut publier les modifications du wiki",
          help: "Qui peut approuver et publier un ensemble de modifications : la personne pour qui il a été fait, un autre contributeur ou un responsable. Un projet peut seulement le rendre plus strict. Qui peut lire chaque page ne change pas.",
        },
        webDomains: {
          label: "Domaines de la recherche web",
          help: "Résultats uniquement issus des domaines autorisés, jamais des domaines bloqués. Un domaine couvre ses sous-domaines.",
        },
        webMaxAge: {
          label: "Ancienneté maximale des résultats web",
          help: "Les résultats publiés avant sont écartés, de même que ceux sans date.",
        },
        webProvider: {
          label: "Service de recherche web",
          help: "Le service qui effectue les recherches web de l'assistant.",
        },
        webFetch: {
          label: "Lecture de pages web",
          help: "Activé par défaut. L'assistant peut lire une page web publique : une page donnée par l'utilisateur, un résultat de recherche ou un lien d'une page lue. Une adresse qu'il compose lui-même est d'abord soumise à autorisation.",
        },
        webFetchApproval: {
          label: "Autorisation avant de lire une page",
          help: "Les adresses composées par l'assistant demandent toujours une autorisation. Choisissez si chaque page en demande une aussi.",
        },
        webFetchDomains: {
          label: "Domaines des pages web",
          help: "Pages uniquement issues des domaines autorisés, jamais des domaines bloqués, redirections comprises. Un domaine couvre ses sous-domaines.",
        },
        egress: {
          label: "Appels sortants après lecture de contenu classifié",
          help: "Ce que deviennent les appels de l'assistant à des outils externes une fois qu'une conversation a lu des fichiers de chaque audience.",
        },
        webFetchProvider: {
          label: "Service de lecture de pages",
          help: "Le service qui lit les pages web pour l'assistant. Il n'a aucun accès aux données de votre organisation.",
        },
        codeExecution: {
          label: "Exécution de code",
          help: "Activée par défaut. L'assistant peut exécuter de courts programmes de calcul et d'analyse de données dans un bac à sable isolé, dans la région de vos données, sans réseau ni accès aux systèmes de votre organisation. Chaque réponse a son propre bac à sable, supprimé à la fin de la réponse.",
        },
        codeExecutionProvider: {
          label: "Service de bac à sable",
          help: "Le service qui exécute les programmes de l'assistant. Il n'a pas de réseau ni d'accès aux données de votre organisation au-delà du code qu'il reçoit.",
        },
        codeEgress: {
          label: "Exécution de code après lecture de contenu classifié",
          help: "Ce que deviennent les exécutions de code de l'assistant une fois qu'une conversation a lu des fichiers de chaque audience. Rien ne sort du bac à sable : la règle peut donc être plus souple que pour les appels sortants.",
        },
      },
      wikiTrust: {
        requester: "La personne pour qui il a été fait, ou tout contributeur",
        peer: "Une autre personne que son auteur",
        manager: "Un responsable ou le propriétaire",
        fewPublishers:
          "Moins de deux membres sont nommés dans ce projet avec un rôle qui peut publier (groupes et règles non comptés) : à ce niveau, personne ne pourrait publier ses propres modifications.",
      },
      webFetchApproval: {
        provenance: "Seulement pour les adresses composées par l'assistant",
        always: "Pour chaque page",
      },
      residency: { eu: "UE uniquement", global: "Modèles mondiaux, avec repli dans l'UE" },
      web: {
        include: "Uniquement ces domaines",
        includeHint: "Vide : tous les domaines.",
        exclude: "Jamais ces domaines",
        add: "Ajouter",
        addTo: "Ajouter à « {{list}} »",
        remove: "Retirer {{domain}}",
        placeholder: "exemple.fr",
        none: "Aucun",
        anyDomain: "Tous les domaines",
        only: "Uniquement {{domains}}",
        never: "jamais {{domains}}",
        neverStart: "Jamais {{domains}}",
        andMore: "{{list}} et {{count}} de plus",
        invalid:
          "« {{domain}} » n'est pas un domaine. Saisissez un nom d'hôte comme exemple.fr, sans https:// ni chemin.",
        duplicate: "{{domain}} figure déjà dans la liste.",
        tooMany: "{{max}} domaines au plus par liste.",
        inherited: "Hérité : {{value}}",
        widens:
          "Ce choix autoriserait plus que ce dont le projet hérite. Gardez chaque blocage hérité, et des domaines autorisés compris dans ceux hérités.",
        noLimit: "Aucune limite",
        days_one: "{{count}} jour",
        days_other: "{{count}} jours",
      },
      refused: {
        invalidDomain:
          "« {{domain}} » n'est pas un domaine valide : saisissez un nom d'hôte comme exemple.fr.",
        narrowOnly: "Ce projet peut seulement rendre ce réglage plus strict que la valeur héritée.",
        locked: "Ce réglage est verrouillé à un niveau supérieur.",
      },
      pages: "{{count}} pages",
      placement: {
        entry: "{{type}} : {{path}}",
        none: "Racine du wiki pour tous les types",
        root: "Racine du wiki",
        missing: "{{path}} (absent de ce projet)",
        hint: "Seuls les dossiers existants sont utilisés : rien n’est créé.",
        pathPlaceholder: "Sources / Résumés",
        pathsHint:
          "Noms des dossiers depuis la racine du wiki, séparés par /. Un projet sans ce dossier utilise la racine de son wiki.",
      },
      guidelines: {
        language: "Langue",
        languageHint: "Vide : la langue des sources.",
        sourcesLanguage: "la langue des sources",
        pageTypes: "Types de page",
        structure: "Structure",
        structureHint: "Sections, ton ou tout ce que les brouillons doivent suivre.",
        summary: "Langue : {{language}} · {{types}}",
        withStructure: "structure définie",
      },
    },
    danger: {
      lead: "La suppression du projet ne peut pas être annulée.",
      delete: "Supprimer le projet",
      title: "Supprimer « {{name}} » ?",
      body: "Le projet, ses conversations, ses fichiers et son wiki deviennent inaccessibles immédiatement et sont purgés sous 24 heures. Cette action ne peut pas être annulée.",
      typeName: "Saisissez « {{name}} » pour confirmer",
      confirm: "Supprimer le projet",
      deleting: "Suppression…",
      deleted: "« {{name}} » a été supprimé.",
    },
  },
  settingsControls: {
    saving: "Enregistrement…",
    saved: "Enregistré",
    on: "Activé",
    off: "Désactivé",
    units: { hours: "heures", days: "jours", mb: "Mo", pages: "pages", usd: "USD", percent: "%" },
    range: "Saisissez un nombre entier de {{min}} à {{max}}.",
    rangeFrom: "Saisissez un nombre entier d'au moins {{min}}.",
    rangeHint: "De {{min}} à {{max}} {{unit}}",
    rangeHintFrom: "Au moins {{min}} {{unit}}",
    segments: {
      optional: "Facultatif",
      required: "Obligatoire",
      euOnly: "UE uniquement",
      global: "Mondial",
      fast: "Rapide",
      balanced: "Équilibré",
      deep: "Approfondi",
      composedOnly: "Composées seulement",
      everyPage: "Chaque page",
      allowed: "Autorisé",
      asksFirst: "Sur confirmation",
      blocked: "Bloqué",
    },
    nullable: {
      limit: "Limite",
      ownBudget: "Budget propre",
      off: "Désactivé",
      noLimit: "Aucune limite",
      operator: "Budget de l'opérateur",
    },
    lock: {
      lock: "Verrouiller en dessous",
      unlock: "Déverrouiller",
      lockName: "Verrouiller pour les niveaux inférieurs : {{label}}",
      unlockName: "Déverrouiller : {{label}}",
    },
    lockEveryTenant: {
      lock: "Verrouiller pour toutes les organisations",
      unlock: "Déverrouiller",
      lockName: "Verrouiller pour toutes les organisations : {{label}}",
      unlockName: "Déverrouiller pour toutes les organisations : {{label}}",
    },
    editors: {
      levels: "Modifier les niveaux",
      domains: "Modifier les domaines",
      guidelines: "Modifier les consignes",
      placement: "Modifier l'emplacement",
      applications: "Modifier les applications",
      models: "Modifier les modèles",
    },
    editorName: "{{action}} : {{label}}",
    confirm: {
      group: "Confirmer : {{label}}",
      turnOn: "Activer",
      keepOff: "Laisser désactivé",
      allow: "Autoriser",
      keep: "Garder {{value}}",
      save: "Enregistrer",
      keepEditing: "Continuer à modifier",
    },
    projectLoosen: {
      residency:
        "Autoriser les modèles mondiaux permet de nouveau de produire les réponses de ce projet hors de l'UE. Les données restent stockées dans l'UE.",
      tasks:
        "L'activer permet de nouveau aux membres de créer et de gérer des tâches dans ce projet.",
      memoryPersonal:
        "L'activer permet de nouveau à l'assistant de suggérer et d'utiliser des mémoires personnelles dans ce projet.",
      memoryProject:
        "L'activer permet de nouveau aux membres de suggérer des faits pour tout le projet.",
      attachments:
        "L'activer permet de nouveau de joindre des fichiers aux conversations de ce projet.",
      artefacts:
        "L'activer permet de nouveau à l'assistant de créer des documents, du code et des tableaux à côté des conversations de ce projet.",
      thinkingSummary:
        "L'activer affiche de nouveau un court résumé de chaque étape de réflexion dans les réponses de ce projet.",
      modelsAllowed: "Ajouter un effort permet de nouveau aux réponses de ce projet de l'utiliser.",
      advancedPicker:
        "L'activer permet de nouveau de choisir une autre famille de modèles dans ce projet.",
      allowedModels:
        "Ajouter des modèles leur permet de nouveau de produire les réponses de ce projet.",
      ocrMaxPages:
        "Davantage de pages permettent à l'OCR de lire des numérisations plus longues dans ce projet.",
      declassification:
        "L'activer permet de nouveau d'abaisser la classification des fichiers de ce projet.",
      generatedSources:
        "L'activer permet de nouveau aux réponses de ce projet de s'appuyer sur des fichiers écrits par l'assistant.",
      wikiSuggestions:
        "L'activer permet de nouveau à l'assistant de suggérer des pages du wiki sous les réponses de ce projet.",
      wikiTrust:
        "Un niveau plus bas permet à davantage de personnes d'approuver et de publier les modifications du wiki de ce projet.",
      mcpWriteTools:
        "L'activer permet de nouveau aux clients MCP de créer et de modifier du contenu dans ce projet, au nom de la personne.",
      mcpSelfPublish:
        "L'activer permet aux clients MCP de publier des pages du wiki de ce projet sans relecture par une autre personne.",
      mcpTools: "L'activer redonne aux clients MCP les outils de ce groupe dans ce projet.",
      webSearch:
        "L'activer permet de nouveau à l'assistant de chercher sur le web dans les conversations de ce projet.",
      webFetch:
        "L'activer permet de nouveau à l'assistant de lire des pages web publiques dans les conversations de ce projet.",
      webFetchApproval:
        "Seules les adresses composées par l'assistant demanderont une autorisation : il lit les autres pages de ce projet sans approbation.",
      webMaxAge:
        "Des résultats plus anciens permettent à l'assistant de citer des pages périmées dans ce projet.",
      webDomains:
        "Ces listes permettent à la recherche web d'atteindre des domaines que ce projet lui fermait.",
      webFetchDomains:
        "Ces listes permettent à l'assistant de lire des pages sur des domaines que ce projet lui fermait.",
      codeExecution:
        "L'activer permet de nouveau à l'assistant d'exécuter du code dans un bac à sable dans les conversations de ce projet.",
    },
    inheritedFrom: {
      tenant: "Hérité de votre organisation : {{value}}.",
      entity: "Hérité de {{entity}} : {{value}}.",
      entityUnknown: "Hérité d'une entité parente : {{value}}.",
      instance: "Par défaut : {{value}}.",
    },
    loosenGeneric:
      "Ce changement élargit ce que les personnes, les clients ou l'assistant peuvent faire des données.",
    // `tools.egress` par audience de lecteurs (D10) : la valeur en mots et son assouplissement.
    egress: {
      named: "Lecteurs nommés",
      allow: "autorisés",
      confirm: "sur confirmation",
      block: "bloqués",
      help: {
        project: "Fichiers que toute personne pouvant lire le projet peut lire",
        entity: "Fichiers limités à l'entité propriétaire",
        named: "Fichiers que seuls les personnes et groupes désignés peuvent lire",
      },
      files: {
        project: "fichiers que toute personne pouvant lire le projet peut lire",
        entity: "fichiers limités à l'entité propriétaire",
        named: "fichiers que seules les personnes désignées peuvent lire",
      },
      loosen: {
        blockToConfirm:
          "Après la lecture de {{files}} dans une conversation, la recherche web et la lecture de pages web demanderont d'abord au lieu d'être bloquées.",
        blockToAllow:
          "Après la lecture de {{files}} dans une conversation, la recherche web et la lecture de pages web s'exécuteront sans demander au lieu d'être bloquées.",
        confirmToAllow:
          "Après la lecture de {{files}} dans une conversation, la recherche web et la lecture de pages web s'exécuteront sans demander au lieu de demander d'abord.",
      },
      to: { allow: "Autoriser", confirm: "Demander confirmation" },
      keep: { block: "Garder bloqué", confirm: "Garder la confirmation" },
      keepAll: "Garder cette règle",
    },
    loosen: {
      webSearch:
        "L'activer permet à l'assistant de chercher sur le web depuis les conversations de ce niveau et des niveaux inférieurs.",
      webFetch:
        "L'activer permet à l'assistant de lire des pages web publiques depuis les conversations de ce niveau et des niveaux inférieurs.",
      mcpEnabled:
        "L'activer permet de connecter des clients MCP, qui lisent alors ce que la personne peut lire dans ESI.",
      mcpWriteTools:
        "L'activer permet aux clients MCP de créer et de modifier du contenu, au nom de la personne.",
      mcpSelfPublish:
        "L'activer permet aux clients MCP de publier des pages du wiki sans relecture par une autre personne.",
      mcpTools: "L'activer redonne aux clients MCP les outils de ce groupe.",
      declassification: "L'activer permet d'abaisser la classification des fichiers.",
      deviceGrant:
        "L'activer permet aux outils en ligne de commande et aux appareils de se connecter avec un code affiché sur un autre écran.",
      shareOutsideEntity: "L'activer permet d'ajouter aux projets des membres d'autres entités.",
      nativeClients:
        "Autoriser toutes les applications permet à tout client natif ou MCP de se connecter, pas seulement à ceux de la liste.",
      providers:
        "Activer un fournisseur permet aux connexions de l'instance, ou à celles des organisations, d'appeler des modèles par lui.",
      labs: "Ajouter un laboratoire permet à ses modèles de répondre ici, une fois les vérifications de chaque effort passées.",
      mfa: "Rendre le second facteur facultatif permet aux comptes natifs de se connecter avec un mot de passe seul.",
      residency:
        "Autoriser les modèles mondiaux permet de produire des réponses hors de l'UE. Les données restent stockées dans l'UE.",
      wikiTrust:
        "Un niveau plus bas permet à davantage de personnes d'approuver et de publier les modifications du wiki.",
      webFetchApproval:
        "Seules les adresses composées par l'assistant demanderont une autorisation : il lit les autres pages sans approbation.",
      mcpCeiling:
        "Un plafond plus élevé permet aux clients MCP de recevoir des fichiers plus sensibles.",
      webMaxAge:
        "Des résultats plus anciens, ou aucune limite, permettent à l'assistant de citer des pages périmées.",
      webDomains:
        "Ces listes permettent à la recherche web d'atteindre des domaines qu'elle ne pouvait pas atteindre.",
      webFetchDomains:
        "Ces listes permettent à l'assistant de lire des pages sur des domaines qu'il ne pouvait pas lire.",
      egress:
        "Les outils sortants pourront envoyer davantage après la lecture de fichiers classifiés dans une conversation.",
      codeExecution:
        "L'activer permet à l'assistant d'exécuter du code dans un bac à sable depuis les conversations de ce niveau et des niveaux inférieurs.",
      codeEgress:
        "L'assistant pourra exécuter du code avec moins d'approbations après la lecture de fichiers classifiés dans une conversation.",
    },
  },
  residencyNote: {
    eu: "Stocké et traité dans l'UE",
    global: "Stocké dans l'UE · modèles possiblement hors UE",
    euLong: "Les données sont stockées dans l'UE et les modèles s'exécutent dans l'UE.",
    globalLong:
      "Les données restent stockées dans l'UE. Les requêtes, l'historique de conversation et des passages de fichiers peuvent être traités par des modèles hors de l'UE.",
    label: "Résidence des données",
    // Où s'exécute le code de l'assistant (agent-turn D14), quand l'exécution de code est active.
    codeEu:
      "Le code exécuté par l'assistant reste dans un bac à sable isolé dans l'UE, sans réseau.",
    codeGlobal:
      "Le code exécuté par l'assistant reste dans un bac à sable isolé dans la région de ce déploiement, sans réseau.",
  },
  members: {
    you: "Vous",
    groupMembers_one: "{{count}} membre",
    groupMembers_other: "{{count}} membres",
    excluded_one:
      "{{count}} exclu par la règle d'entité : ce membre est hors de l'entité propriétaire.",
    excluded_other:
      "{{count}} exclus par la règle d'entité : ces membres sont hors de l'entité propriétaire.",
    rulesHint:
      "Toute personne dont les attributs d'annuaire correspondent à la règle agit avec son rôle.",
    audienceNote: {
      entity: "Les membres de l'entité propriétaire peuvent aussi lire ce projet, comme lecteurs.",
      entity_tree:
        "Les membres de l'entité propriétaire et de ses sous-entités peuvent aussi lire ce projet, comme lecteurs.",
    },
  },
  knowledgePanel: {
    title: "Connaissances",
    tree: "Connaissances",
    section: {
      files: "Sources",
      wiki: "Wiki",
      connected: "Connectées",
      attachments: "Cette conversation",
      artefacts: "Artefacts",
    },
    type: {
      file: "Fichier",
      wiki: "Page wiki",
      connected: "Élément connecté",
      artefact: "Artefact",
    },
    search: "Rechercher fichiers et pages",
    searchPages: "Rechercher des pages",
    items_one: "{{count}} élément",
    items_other: "{{count}} éléments",
    matches_one: "{{count}} résultat",
    matches_other: "{{count}} résultats",
    actionsFor: "Actions pour {{name}}",
    actionsHint: "Actions : Maj+F10",
    openPage: "Ouvrir la page",
    updated: "Mise à jour le {{date}}",
    pendingPage: "Une modification en attente touche cette page",
    pendingFolder: "contient des pages avec des modifications en attente",
    breadcrumb: "Chemin du dossier",
    emptyFolder: "Ce dossier est vide.",
    back: "Retour à {{name}}",
    empty: {
      title: "Aucune source pour l'instant",
      body: "Les pages wiki et les sources de ce projet que vous pouvez lire apparaîtront ici.",
      upload:
        "Déposez un fichier, ou enregistrez une réponse, pour donner à cette conversation sa première source.",
    },
    noMatch: {
      title: "Aucun résultat",
      body: "Aucun nom de fichier, de page ou de dossier ne contient « {{query}} ».",
      clear: "Effacer la recherche",
    },
  },
  folders: {
    new: "Nouveau dossier",
    newIn: "Nouveau dossier dans {{path}}",
    rename: "Renommer",
    moveTo: "Déplacer vers…",
    uploadHere: "Déposer ici",
    field: "Dossier",
    nameLabel: "Nom du dossier",
    visibleNote: "Les noms de dossiers sont visibles par tous les membres de ce projet.",
    nameProblem: {
      empty: "Saisissez un nom.",
      too_long: "Utilisez au plus {{max}} caractères.",
      invalid:
        "Un nom ne peut contenir ni / ni \\, ni caractères de contrôle, et ne peut être . ou ..",
    },
    createTitle: "Nouveau dossier",
    createIn: "Dans {{path}}",
    create: "Créer le dossier",
    renameTitle: "Renommer « {{name}} »",
    save: "Enregistrer le nom",
    moveTitle: "Déplacer « {{name}} »",
    moveLead: "Les dossiers ne font qu'organiser : déplacer ne change l'accès de personne.",
    destination: "Destination : {{path}}",
    moveHere: "Déplacer ici",
    picker: {
      label: "Dossier de destination",
      search: "Rechercher un dossier",
      root: "{{section}} (premier niveau)",
      noMatch: "Aucun nom de dossier ne correspond.",
      change: "Changer",
      done: "Terminé",
      here: "Déjà ici",
      inside: "Dans le dossier déplacé",
      tooDeep: "Trop profond : au plus {{max}} niveaux de dossiers",
    },
    delete: {
      action: "Supprimer le dossier",
      title: "Supprimer « {{name}} » ?",
      lead: "Seul le dossier disparaît. Son contenu reste dans le projet.",
      moves: "Son contenu va dans « {{parent}} » : {{what}}.",
      movesBoth: "Son contenu va dans « {{parent}} » : {{items}} et {{folders}}.",
      empty: "Il est vide. Rien ne va dans « {{parent}} ».",
      files_one: "{{count}} fichier",
      files_other: "{{count}} fichiers",
      pages_one: "{{count}} page",
      pages_other: "{{count}} pages",
      folders_one: "{{count}} dossier",
      folders_other: "{{count}} dossiers",
      nothing:
        "Rien n'est supprimé. Les éléments que vous ne voyez pas sont aussi déplacés et gardent leurs lecteurs.",
      confirm: "Supprimer le dossier",
      done: "Dossier supprimé. Son contenu est remonté d'un niveau.",
      doneRenamed:
        "Dossier supprimé. Son contenu est remonté ; renommés pour rester uniques : {{names}}.",
    },
    error: {
      nameTaken: "Un dossier porte déjà ce nom ici (sans tenir compte de la casse).",
      limit: "Cette section contient déjà le nombre maximal de dossiers d'un projet.",
      tooDeep: "Au plus {{max}} niveaux de dossiers : choisissez un dossier plus haut.",
      cycle: "Un dossier ne peut aller dans lui-même ni dans l'un de ses sous-dossiers.",
      wrongSection:
        "Pages wiki et sources ont des dossiers distincts : choisissez-en un de cette section.",
      frozen: "Ce projet est en lecture seule : les dossiers ne peuvent pas changer.",
    },
  },
  artefactKnowledge: {
    cannotUpdate:
      "Une version antérieure est dans les connaissances. Seuls la personne qui l'a ajoutée, un responsable ou le propriétaire peuvent la mettre à jour.",
    addEllipsis: "Ajouter aux connaissances…",
    addTitle: "Ajouter aux connaissances",
    updateTitle: "Mettre à jour dans les connaissances",
    add: "Ajouter aux connaissances",
    update: "Mettre à jour dans les connaissances",
    addNamed: "Ajouter aux connaissances : {{title}}, version {{version}}",
    updateNamed: "Mettre à jour dans les connaissances : {{title}}, version {{version}}",
    inKnowledge: "Dans les connaissances · version {{version}}",
    showInFiles: "Voir dans Sources",
    projectAudience_one:
      "Membres du projet qui peuvent lire ses sources · {{count}} personne aujourd'hui",
    projectAudience_other:
      "Membres du projet qui peuvent lire ses sources · {{count}} personnes aujourd'hui",
    readersExplain:
      "Ses sources décident qui peut le lire : seules les personnes qui peuvent toutes les lire, aujourd'hui comme demain. Ni le dossier ni le rôle de quiconque n'élargit ce cercle.",
    generatedExplain:
      "Les réponses qui le citent indiqueront que l'assistant l'a rédigé, et le projet peut exclure ces fichiers des réponses.",
    withWeb: "tiré de résultats web",
    folderExplain: "Le dossier ne fait que ranger le fichier. Il ne change pas qui peut le lire.",
    updateExplain:
      "La version {{version}} remplace la version {{current}} dans les connaissances. Elle reste dans {{folder}}, avec les mêmes lecteurs sauf si ses sources les restreignent.",
    alreadyAdded:
      "La version {{version}} de cet artefact est déjà dans les connaissances du projet.",
    addSubmit: "Ajouter aux connaissances",
    updateSubmit: "Mettre à jour",
    adding: "Ajout…",
    done: "{{title}} est dans les connaissances · version {{version}}.",
    refusal: {
      alreadyAdded:
        "Cet artefact est déjà dans les connaissances du projet. Mettez-le à jour depuis une version plus récente.",
      notNewer: "Les connaissances contiennent déjà cette version ou une plus récente.",
      storageLimit:
        "Le stockage de votre organisation est plein. Supprimez des fichiers ou demandez plus d'espace à un administrateur.",
      frozen: "Ce projet est gelé : rien ne peut y être ajouté.",
      forbidden: "Vous ne pouvez pas ajouter de fichiers à ce projet.",
    },
  },
  generated: {
    mark: "Généré par l'agent",
    fromNamedPrefix: "Depuis ",
    fromConversation: "Depuis une conversation",
    fromDeleted: "Depuis une conversation supprimée",
    version: "version {{version}}",
    previewNote:
      "Rédigé par l'assistant dans une conversation. Vérifiez les faits importants dans les sources d'origine.",
    usesGenerated: "Utilise des sources générées",
  },
  files: {
    statusFilter: {
      failed: "Seuls les fichiers en échec sont affichés",
      processing: "Seuls les fichiers en traitement sont affichés",
      clear: "Afficher tous les fichiers",
      mine: {
        failed: "Seuls vos envois en échec sont affichés",
        processing: "Seuls vos envois en traitement sont affichés",
      },
      none: {
        failed: "Aucun fichier que vous pouvez lire n’est en échec.",
        processing: "Aucun fichier que vous pouvez lire n’est en traitement.",
      },
    },
    title: "Sources",
    caption: "Sources du projet",
    upload: "Déposer un fichier",
    empty:
      "Aucun fichier pour l'instant. Déposez des PDF, Word, PowerPoint, Excel, CSV, Markdown, HTML, fichiers texte ou images PNG et JPEG ; les réponses de ce projet les citent.",
    name: "Nom",
    status: "État",
    classification: "Classification",
    origin: "Origine",
    readers: "Lecteurs",
    size: "Taille",
    actions: "Actions",
    statusLabel: {
      uploading: "Dépôt incomplet",
      processing: "En traitement",
      ready: "Prêt",
      failed: "Échec",
    },
    noReason: "Aucune raison n'a été enregistrée.",
    paused: "En pause",
    pausedReason: "Budget IA mensuel atteint. Le traitement reprendra de lui-même.",
    failure: {
      upload_mismatch:
        "Le fichier envoyé ne correspond pas à la taille ou au type déclaré. Déposez-le à nouveau.",
      unknown: "Raison d'échec inconnue.",
      ocr_page_cap: "Trop de pages pour une lecture automatique.",
      ocr_page_cap_limit: "Trop de pages pour une lecture automatique (limite : {{limit}}).",
      ocr_budget_reached:
        "Le quota mensuel de pages numérisées est épuisé. Réessayez le mois prochain ou demandez à un administrateur.",
      ocr_failed: "La numérisation n'a pas pu être lue automatiquement.",
      no_text_layer:
        "Aucun texte trouvé, et la lecture des numérisations est désactivée pour ce projet.",
      unsupported_format: "Ce type de fichier ne peut pas être lu.",
      corrupt: "Le fichier est endommagé ou protégé et ne peut pas être lu.",
      empty: "Aucun texte n'a été trouvé dans ce fichier.",
      too_large: "Le fichier est trop volumineux pour être lu.",
      missing_object: "Le dépôt n'a pas atteint le stockage. Déposez-le à nouveau.",
      model_rejected: "Le contenu du fichier n'a pas pu être indexé.",
      unavailable: "Le traitement est momentanément indisponible. Réessayez plus tard.",
    },
    originDerived: "Déduite de ses sources : elle ne se modifie pas à la main.",
    originLabel: {
      internal: "Source interne",
      external: "Source externe",
      generated: "Généré par l'agent",
    },
    open: "Ouvrir",
    download: "Télécharger",
    citeOnly: "Citable seulement, sans téléchargement",
    retry: "Relancer",
    retrying: "Relance…",
    remove: "Retirer",
    removing: "Retrait…",
    actionFailed: "{{title}} : {{reason}}",
    restricted_one: "{{count}} autre fichier de ce projet est réservé à d'autres lecteurs.",
    restricted_other: "{{count}} autres fichiers de ce projet sont réservés à d'autres lecteurs.",
    actionsFor: "Actions pour {{title}}",
    editAccess: "Modifier l'accès",
    delete: "Supprimer",
    deleteTitle: "Supprimer « {{title}} » ?",
    deleteLead: "La suppression prend effet immédiatement et ne peut pas être annulée :",
    deleteEffects: {
      retrieval: "Le fichier ne peut plus être retrouvé, cité ni téléchargé.",
      citations: "Les réponses qui le citaient afficheront « source supprimée ».",
      wiki: "Les pages du wiki construites à partir de lui recevront un élément à revoir.",
      purge: "Son contenu est purgé sous 24 heures.",
    },
    deleteConfirm: "Supprimer le fichier",
    deleting: "Suppression…",
    deleted: "« {{title}} » a été supprimé.",
    cancel: "Annuler",
    access: {
      title: "Accès à « {{title}} »",
      lead: "Qui peut le lire, sa classification et son origine.",
      save: "Enregistrer",
      saving: "Enregistrement…",
      uploaderIncluded: "{{name}} (l'a déposé, toujours inclus)",
      noChange: "Rien n'a changé.",
      widenTitle: "Davantage de personnes pourront le lire",
      widenLead: "Après cette modification, « {{title}} » pourra être lu par :",
      gains: {
        project_one: "Toute personne qui peut lire ce projet ({{count}} membre)",
        project_other: "Toute personne qui peut lire ce projet ({{count}} membres)",
        projectUnknown: "Toute personne qui peut lire ce projet",
        entity: "Les membres de {{entity}}",
        entityTree: "Les membres de {{entity}} et de ses sous-entités",
        ownerFallback: "l'entité propriétaire",
        group: "Le groupe {{name}}",
        rule: "Les personnes correspondant à {{name}}",
        unnamed: "Un lecteur que vous ne pouvez pas voir nommément",
      },
      downloadUnlocked: "Les lecteurs pourront télécharger le fichier.",
      floorWidened: "« {{level}} » permet de le partager plus largement qu'avant.",
      confirmWiden: "Donner l'accès",
      back: "Retour",
      narrowedOut: "Vous n'avez plus accès à « {{title}} ».",
      updated: "L'accès à « {{title}} » a été mis à jour.",
      refused: {
        derived:
          "Les lecteurs d'un fichier enregistré doivent pouvoir lire chacune des sources dont il provient.",
        belowSource:
          "Un fichier enregistré ne peut pas être classé en dessous d'une source dont il provient.",
        declassified:
          "Un contenu déclassifié ne peut être rouvert que par une nouvelle déclassification.",
        frozen:
          "Ce projet est désormais en lecture seule : ses fichiers ne peuvent plus être modifiés.",
      },
    },
  },
  readers: {
    project: "Audience du projet",
    entity: "Entité propriétaire",
    entityRef: "Entité {{ref}}",
    entityTree: "Entité propriétaire et sous-entités",
    entityTreeRef: "Entité {{ref}} et sous-entités",
    onlyYou: "Vous seul",
    people_one: "{{count}} personne",
    people_other: "{{count}} personnes",
    groups_one: "{{count}} groupe",
    groups_other: "{{count}} groupes",
    rules_one: "{{count}} règle d'attribut",
    rules_other: "{{count}} règles d'attribut",
    none: "Personne que vous puissiez nommer",
  },
  audience: {
    restricted: "Restreint",
    label: "Qui peut le lire",
    onlyYou: "Vous seul",
    youAndShared_one: "Vous et {{count}} personne",
    youAndShared_other: "Vous et {{count}} personnes",
    youAndProject: "Vous et le projet",
    youProjectAndShared_one: "Vous, le projet et {{count}} personne",
    youProjectAndShared_other: "Vous, le projet et {{count}} personnes",
    projectSharedBy: "Partagée avec le projet par {{name}}",
    ownerAndShared_one: "{{owner}} et {{count}} personne",
    ownerAndShared_other: "{{owner}} et {{count}} personnes",
    sharedBy: "Partagé avec vous par {{name}}",
    hiddenSources_one: "{{count}} source que vous ne pouvez pas voir",
    hiddenSources_other: "{{count}} sources que vous ne pouvez pas voir",
    webResults: "Inclut des résultats web",
    ranCode: "Code exécuté",
    generalAnswer: "Réponse générale, aucune source utilisée",
  },
  models: {
    answer: {
      label: "Répondu par",
      fallback: "Modèle de repli utilisé",
      policy: "Effort ou modèle choisi non disponible",
      reasons: {
        older_version: "La version la plus récente n'était pas disponible",
        neighbour_family: "Le modèle habituel n'était pas disponible",
        pin_unhonoured: "La version définie par votre organisation n'était pas disponible",
        unavailable: "Modèle de repli utilisé",
        policy: "Effort ou modèle choisi non disponible",
        provider_withdrawn: "Le fournisseur choisi pour cette conversation n'est plus proposé",
      },
    },
    picker: {
      title: "Effort",
      trigger: "Effort : {{effort}}",
      triggerFamily: "Effort : {{effort}}, sur {{family}}",
      triggerLocked: "Effort : {{effort}}, défini par {{level}}",
      lockedBy: "Défini par {{level}}",
      triggerOnly: "Effort : {{effort}}, le seul disponible ici",
      onlyEffort: "Le seul effort disponible ici",
      levels: {
        instance: "cette instance",
        tenant: "votre organisation",
        entity: "votre entité",
        project: "ce projet",
      },
      efforts: {
        fast: { name: "Rapide", hint: "Réponses rapides aux questions simples" },
        balanced: { name: "Équilibré", hint: "L'effort adapté à la plupart des tâches" },
        deep: { name: "Approfondi", hint: "Raisonne plus longtemps avant de répondre" },
      },
      unavailableEffort:
        "{{effort}} n'est pas disponible ici. Les réponses utilisent {{fallback}}.",
      unavailableFamily:
        "La famille de modèles choisie n'est pas disponible ici. Les réponses utilisent la famille de {{effort}}.",
      otherFamilies: "Autres familles pour {{effort}}",
      effortFamily: "Famille de {{effort}}",
      automatic: "Automatique",
      clearFamily: "Ne plus la demander",
      useFallback: "Utiliser {{effort}}",
      pinned: "Version définie par votre organisation :",
      pinUnhonoured:
        "La version définie par votre organisation pour {{effort}} n'est pas disponible : {{model}} répond.",
      through: "Via",
      throughLab: "{{lab}} via",
      providers: { esi: "ESI", tenantBedrock: "Votre AWS Bedrock" },
      advanced: "Avancé",
      reasoning: {
        title: "Raisonnement pour cette conversation",
        hint: "S'applique à cette conversation uniquement.",
        preset: "Préréglage {{effort}} ({{level}})",
        levels: { default: "Par défaut du modèle", low: "Faible", medium: "Moyen", high: "Élevé" },
        line: "{{model}} · {{level}}",
        lineLevels: {
          low: "raisonnement faible",
          medium: "raisonnement moyen",
          high: "raisonnement élevé",
        },
      },
      retention: {
        default: "Conservation standard",
        aws_review: "Revue possible par AWS (30 jours)",
      },
      processed: { eu: "Traité dans l'UE", global: "Traité au niveau mondial" },
      providerNever: "Le fournisseur ne voit jamais les données",
      providerReceives: "Le fournisseur reçoit les données",
      cost: {
        lowest: "Coût le plus bas",
        moderate: "Coût modéré",
        higher: "Coût plus élevé",
        highest: "Coût le plus élevé",
      },
      speed: { slower: "Plus lent", moderate: "Vitesse moyenne", fastest: "Le plus rapide" },
    },
    sections: {
      tabs: {
        label: "Sections des modèles",
        labs: "Laboratoires",
        efforts: "Efforts",
        pins: "Versions figées",
        health: "État et historique",
        access: "Modèles et consentement",
      },
      purpose: {
        labs: "Les laboratoires dont ce niveau propose les modèles. Le premier est le laboratoire principal : ses familles répondent à chaque effort, sauf si vous en associez une autre.",
        efforts:
          "Les efforts autorisés, la possibilité de choisir une autre famille, et la famille et le niveau de raisonnement de chaque effort.",
        pins: "Garder une famille sur une version plutôt que sur sa plus récente en bon état. Une version figée ne s'applique que tant qu'elle répond.",
        health:
          "Ce que les versions de chaque famille ont répondu aux derniers contrôles, et quand les modèles de ce niveau ont changé de version.",
        access:
          "Les conditions de chaque modèle en clair, le consentement de l'organisation et les versions exactes autorisées à ce niveau.",
      },
      setHere: "Défini à ce niveau",
      inheritedFrom: "Hérité de {{source}}",
      lockedHere: "Verrouillé pour les niveaux en dessous",
      labs: {
        offeredList: "Laboratoires proposés ici",
        primary: "Laboratoire principal",
        tested: "Testé par ESI · {{date}}, catalogue {{version}}",
        testedNoDate: "Testé par ESI",
        untested: "Non testé par ESI",
        notInEu: "Non disponible dans l'UE",
        offer: "Proposer",
        drop: "Ne plus proposer",
        moveUp: "Monter {{lab}}",
        moveDown: "Descendre {{lab}}",
        warning:
          "Non testé par ESI : la qualité des réponses, l'usage des outils et les citations peuvent être moins fiables",
        unacknowledged:
          "Confirmez l'avertissement sur les laboratoires non testés par ESI avant de les proposer.",
        dialog: {
          title_one: "Proposer {{labs}} ?",
          title_other: "Proposer {{labs}} ?",
          lead: "ESI n'a encore aucune évaluation enregistrée pour ce laboratoire.",
          scope:
            "Ses modèles deviennent disponibles pour les réponses de votre organisation lorsque la résidence, les modèles autorisés et les limites de dépense le permettent. Ce choix est enregistré dans le journal d'audit.",
          confirm: "Je comprends et souhaite le proposer à mon organisation",
          submit_one: "Proposer le laboratoire",
          submit_other: "Proposer les laboratoires",
        },
      },
      efforts: {
        familyHeading: "Famille par effort",
        family: "Famille",
        reasoning: "Raisonnement",
        reasoningLevels: {
          default: "Par défaut du modèle",
          low: "Faible",
          medium: "Moyen",
          high: "Élevé",
        },
        notSupported: "non pris en charge par {{model}}",
        lockFamilies: "Verrouiller les familles pour les niveaux en dessous",
        lockReasoning: "Verrouiller les niveaux de raisonnement (aussi par conversation)",
        default: "Famille du laboratoire principal (par défaut)",
        notUsable: "inutilisable pour {{effort}}",
        notUsableTools:
          "Inutilisable pour {{effort}} : pas d'usage d'outils. Sa famille voisine répond à la place.",
        resolves: "Actuellement {{model}}, via {{provider}}",
        noVersion: "Aucune version disponible ici",
        noneKept:
          "Aucune version disponible ici : la dernière version qui fonctionnait est conservée",
      },
      pins: {
        latest: "La plus récente (en bon état)",
        notAvailableOn: "non disponible chez {{provider}}",
        unhonoured: "{{pin}} n'est pas disponible : {{model}} répond à la place.",
      },
      health: {
        refresh: "Actualiser les modèles",
        refreshing: "Actualisation : les résultats s'affichent ici d'ici quelques minutes.",
        waitMinutes_one: "Actualisé récemment : de nouveau possible dans {{count}} minute.",
        waitMinutes_other: "Actualisé récemment : de nouveau possible dans {{count}} minutes.",
        interval: { hourly: "Contrôlé toutes les heures.", daily: "Contrôlé chaque jour." },
        notProbed: "Pas encore contrôlé",
        notListed: "Non proposé par {{provider}}",
        probedAt: "contrôlé le {{date}}",
        inUse: "Utilisée",
        status: {
          ok: "Répond",
          throttled: "Répond, limité",
          denied: "Accès refusé",
          unavailable: "Indisponible",
          error: "En échec",
          unknown: "Inconnu",
          retired: "Retiré par le fournisseur",
        },
        history: "Historique des versions",
        noHistory: "Aucun changement de version enregistré à ce niveau pour l'instant.",
        switch: "{{from}} → {{to}}",
        noVersion: "aucune version disponible",
        reasons: {
          new_version_healthy: "Une version plus récente a passé ses contrôles",
          version_unhealthy: "La version a cessé de répondre",
          pin_unhealthy: "La version figée a cessé de répondre",
          pin_restored: "La version figée répond de nouveau",
          admin_pin: "Un administrateur a figé une version",
          admin_unpin: "Un administrateur a retiré une version figée",
          provider_changed: "Le fournisseur a changé",
          policy_changed: "La politique a changé",
        },
      },
      olderOnly:
        "Les modèles autorisés ici ne citent que des versions anciennes ({{models}}) : les versions plus récentes ne répondent jamais. Choisissez plutôt les familles ou les versions actuelles.",
      projectLink: "Les modèles sont définis par votre organisation et ses entités.",
    },
    title: "Modèles",
    intro:
      "Les conditions de chaque modèle sont dites en clair. Votre organisation choisit les modèles qui peuvent servir ses réponses et ses tâches de fond ; un modèle aux conditions plus strictes sur les données n'est jamais utilisé tant qu'un administrateur du tenant ne les a pas acceptées.",
    instance: "Cette instance : traitement {{geographies}}, {{mode}}.",
    retentionMode: {
      default: "conservation Bedrock standard",
      aws_review: "conservation Bedrock autorisant l'examen par AWS",
    },
    geographies: { eu: "UE", global: "mondial" },
    level: "Règles pour",
    organisation: "Toute l'organisation",
    catalogue: "Catalogue",
    implications: {
      retentionDefault: "Aucune conservation au-delà des contrôles d'abus standard de Bedrock",
      retentionReview:
        "AWS peut examiner les requêtes signalées et les conserver jusqu'à 30 jours dans la région de traitement",
      geographiesEu: "Traité dans l'UE ou, avec une résidence mondiale, partout",
      geographiesGlobal: "Traité uniquement à l'échelle mondiale (pas dans l'UE seule)",
      provider: "Le fournisseur du modèle ne reçoit jamais vos données",
      costSpeed: "Coût relatif {{cost}} sur 4, vitesse {{speed}} sur 3",
    },
    unavailable: {
      retention: "Non proposé : le mode de conservation de cette instance ne le permet pas",
      geography: "Non proposé : sa zone géographique n'est pas accordée ici",
      not_routed: "Non proposé : cette instance ne l'achemine pas",
    },
    consent: {
      not_required: "Aucun consentement requis",
      none: "Requiert le consentement d'un administrateur du tenant",
      accepted: "Autorisé par {{name}} le {{date}}",
      acceptedNoName: "Autorisé le {{date}}",
      outdated: "Ses conditions ont changé : consentement à renouveler",
      revoked: "Consentement retiré le {{date}}",
    },
    allow: "Examiner et autoriser",
    revoke: "Retirer",
    dialog: {
      title: "Autoriser {{model}}",
      lead: "Utiliser ce modèle pour votre organisation implique :",
      scope:
        "Il devient disponible pour les périmètres en résidence mondiale qui l'autorisent, dès le prochain appel. Chaque acceptation est tracée dans l'audit.",
      confirm: "J'accepte ces conditions pour toute l'organisation",
      submit: "Autoriser {{model}}",
      changed: "Ces conditions ont changé pendant votre lecture. Examinez-les à nouveau.",
    },
    revokeDialog: {
      title: "Retirer le consentement à {{model}}",
      lead: "Dès le prochain appel, aucune réponse ni tâche de fond ne l'utilise. Les rôles sans modèle échouent comme indisponibles jusqu'à ce qu'un autre modèle soit autorisé.",
      submit: "Retirer le consentement",
    },
    policy: "Règles",
    efforts: "Niveaux d'effort",
    effortsHelp:
      "Rapide, Équilibré et Approfondi. Un seul niveau le fige pour les niveaux en dessous.",
    advancedPicker: "Permettre de choisir une autre famille",
    advancedPickerHelp:
      "Les utilisateurs peuvent faire tourner un effort sur une autre famille de modèles de vos laboratoires, jamais sur une version précise.",
    on: "Activé",
    off: "Désactivé",
    lock: "Verrouiller pour les niveaux en dessous",
    save: "Enregistrer",
    saving: "Enregistrement…",
    advanced: "Avancé : modèles autorisés",
    advancedHelp:
      "Seuls les modèles cochés peuvent servir les réponses et les tâches de fond de ce niveau. Par défaut, tous ceux que la résidence et le consentement permettent.",
    notUsable: "Inutilisable à ce niveau",
    excludedAbove: "Non autorisé par le niveau au-dessus",
    uncovered:
      "Sans modèle utilisable pour : {{roles}}. Ces appels échouent comme indisponibles tant qu'aucun modèle n'est autorisé.",
    blocked: "Enregistrer ne laisserait aucun modèle utilisable pour : {{roles}}.",
    source: {
      instance: "Valeur par défaut de l'instance",
      tenant: "Défini pour l'organisation",
      entity: "Défini pour une entité",
      project: "Défini pour le projet",
    },
    lockedAbove: "Verrouillé au-dessus",
    roles: {
      chat: "les réponses Équilibré",
      reasoning: "les réponses Approfondi et les brouillons de wiki",
      fast: "les tâches rapides (titres, vérifications, résumés)",
      vision: "la lecture des scans et images",
    },
    readOnly:
      "Les administrateurs d'entité voient le consentement ; ceux du tenant l'accordent et le retirent.",
  },
  upload: {
    title: "Déposer des fichiers",
    fileHint:
      "PDF, Office, CSV, Markdown, HTML ou texte, jusqu'à {{mb}} Mo ; images PNG ou JPEG jusqu'à {{imageMb}} Mo.",
    unsupportedType:
      "Ce type de fichier n'est pas pris en charge. Utilisez PDF, Word, PowerPoint, Excel, CSV, Markdown, HTML, texte brut, PNG ou JPEG.",
    emptyFile: "Ce fichier est vide.",
    tooLarge: "Ce fichier dépasse la limite de {{mb}} Mo de ce projet.",
    imageTooLarge: "Les images sont limitées à {{mb}} Mo.",
    classification: "Classification",
    levelNamed: "Seules des personnes nommées peuvent le lire.",
    levelEntity: "Ne peut pas être partagé avec toute l'audience du projet.",
    levelNoDownload: "Ne peut pas être téléchargé ; les réponses peuvent quand même le citer.",
    levelAny: "Tous les choix de lecteurs ci-dessous sont permis.",
    readers: "Qui peut le lire",
    audience: {
      project: "Toutes les personnes qui peuvent lire ce projet",
      entity: "Les membres de l'entité propriétaire",
      entity_tree: "Les membres de l'entité propriétaire et de ses sous-entités",
      named: "Seulement les personnes que je choisis",
      groups: "Des groupes ou règles d'attribut que je choisis",
    },
    groupsHint:
      "Les membres d'un groupe, ou les personnes dont les attributs d'annuaire correspondent à une règle, qui peuvent aussi lire ce projet. Vous restez lecteur.",
    groupsLabel: "Groupes",
    rulesLabel: "Règles d'attribut",
    noPrincipals: "Votre organisation n'a encore ni groupe ni règle d'attribut.",
    pickOne: "Choisissez au moins un groupe ou une règle.",
    audienceNotAllowed: "Non autorisé pour {{level}}.",
    you: "Vous (toujours inclus)",
    noOtherMembers: "Aucun autre membre pour l'instant.",
    membersUnavailable:
      "La liste des membres n'a pas pu être chargée : vous seul pouvez être choisi pour l'instant.",
    origin: "Origine",
    originLabel: { internal: "Interne", external: "Externe" },
    originHint: {
      internal: "Rédigé au sein de votre organisation.",
      external:
        "Fourni par un tiers, comme un client ou un fournisseur. Ce qui en est dérivé est marqué externe.",
    },
    storageUnavailable:
      "Le stockage de fichiers n'est pas configuré sur ce serveur : aucun fichier ne peut être déposé.",
    putFailed: "Le fichier n'a pas atteint le stockage. Réessayez.",
    expired: "Le lien de dépôt a expiré. Réessayez pour lancer un nouveau dépôt.",
    mismatch: "{{detail}} Réessayez.",
    frozen: "Ce projet est passé en lecture seule : le dépôt ne peut pas être terminé.",
    storageFull:
      "L'espace de stockage de votre organisation est plein : ce fichier n'a pas été conservé.",
    cancel: "Annuler",
    batch: {
      files: "Fichiers",
      choose: "Choisir des fichiers",
      dropHint: "ou déposez-les ici",
      dropping: "Relâchez pour ajouter les fichiers",
      list: "Fichiers à déposer",
      limit_one: "Un lot contient au plus {{max}} fichiers : 1 fichier n'a pas été ajouté.",
      limit_other:
        "Un lot contient au plus {{max}} fichiers : {{count}} fichiers n'ont pas été ajoutés.",
      folder:
        "Un dossier déposé n'est pas envoyé. Ouvrez-le et choisissez plutôt les fichiers qu'il contient.",
      duplicateOf: "Même contenu que {{name}} : ce fichier est écarté.",
      sameNameInProject: "Un fichier de ce nom figure déjà dans le projet.",
      sameNameInBatch: "Un autre fichier de ce lot porte ce nom.",
      accessMissing: "Choisissez qui peut le lire avant de le déposer.",
      sharedAccess:
        "Ces choix s'appliquent à chaque fichier, sauf si vous modifiez l'accès d'un fichier.",
      status: {
        ready: "Prêt",
        skipped: "Ne sera pas déposé",
        queued: "En attente",
        intent: "Préparation…",
        put: "Envoi… {{percent}} %",
        confirm: "Vérification…",
        done: "Déposé",
        failed: "Échec",
        cancelled: "Annulé",
        not_sent: "Non envoyé",
      },
      progressOf: "Avancement du dépôt de {{name}}",
      removeFile: "Retirer {{name}}",
      cancelFile: "Annuler {{name}}",
      retry: "Réessayer",
      retryFile: "Réessayer {{name}}",
      changeAccess: "Modifier l'accès",
      changeAccessFile: "Modifier l'accès de {{name}}",
      customAccess: "Accès personnalisé",
      accessTitle: "Accès à {{name}}",
      accessLead: "Qui peut lire ce fichier, sa classification et son origine.",
      accessSave: "Enregistrer",
      accessReset: "Reprendre l'accès du lot",
      submit_one: "Déposer",
      submit_other: "Déposer {{count}} fichiers",
      retryAll_one: "Réessayer",
      retryAll_other: "Réessayer {{count}} fichiers",
      cancelAll: "Tout annuler",
      close: "Fermer",
      summary_one: "{{done}} fichier déposé sur {{count}}",
      summary_other: "{{done}} fichiers déposés sur {{count}}",
      failedCount_one: "{{count}} en échec",
      failedCount_other: "{{count}} en échec",
      notSentCount_one: "{{count}} non envoyé",
      notSentCount_other: "{{count}} non envoyés",
      cancelledCount_one: "{{count}} annulé",
      cancelledCount_other: "{{count}} annulés",
      announce: {
        done: "{{name}} déposé.",
        failed: "{{name}} en échec : {{reason}}",
        cancelled: "{{name}} annulé.",
        not_sent: "{{name}} non envoyé.",
      },
      closeTitle: "Arrêter le dépôt ?",
      closeLead_one:
        "{{count}} fichier n'est pas encore déposé. Arrêter l'annule ; les fichiers déjà déposés restent dans le projet.",
      closeLead_other:
        "{{count}} fichiers ne sont pas encore déposés. Arrêter les annule ; les fichiers déjà déposés restent dans le projet.",
      keepUploading: "Continuer le dépôt",
      stopAndClose: "Arrêter et fermer",
    },
  },
  progress: {
    done: "Terminé",
    failed: "Échec",
    stepOf: "Étape {{current}} sur {{total}} : {{name}}",
  },
  citations: {
    page: "p. {{page}}",
    slide: "Diapositive {{slide}}",
    image: "Image",
    fromOcr: "issu de l'OCR",
    checkOriginal: "issu de l'OCR, vérifiez l'original",
    caption: "décrit par une IA",
    supportedByTranscription: "Étayé par la transcription",
    start: "Section d'ouverture",
    sources: "Sources",
    sourcesCount_one: "{{count}} source",
    sourcesCount_other: "{{count}} sources",
    projectSources: "Sources du projet",
    verdicts: {
      unverified_one: "{{count}} non vérifiée",
      unverified_other: "{{count}} non vérifiées",
      unavailable_one: "{{count}} indisponible",
      unavailable_other: "{{count}} indisponibles",
    },
    showAll: "Tout afficher",
    collapse: "Réduire",
    more_one: "+{{count}}",
    more_other: "+{{count}}",
    moreLabel_one: "Afficher {{count}} source de plus",
    moreLabel_other: "Afficher {{count}} sources de plus",
    showing_one: "{{count}} source affichée",
    showing_other: "{{count}} sources affichées",
    showingAll_one: "Toutes les sources affichées ({{count}})",
    showingAll_other: "Toutes les sources affichées ({{count}})",
    sourceChip: "Source {{handle}} : {{label}}",
    showInSources: "Voir dans les sources",
    classification: "Classification : {{label}}",
    classificationLock: "Classé {{label}}",
    group_one: "{{count}} source",
    group_other: "{{count}} sources",
    groupLabel_one: "{{count}} source citée",
    groupLabel_other: "{{count}} sources citées",
    checking: "Vérification des citations…",
    open: "Ouvrir",
    openSource: "Ouvrir la source",
    working: "En cours…",
    unsourcedSpan: "ne provient pas des sources du projet",
    stoppedUnavailable:
      "Réponse interrompue : elle n'a pas pu continuer. Réessayez dans un instant.",
    stoppedBySpend:
      "Réponse interrompue : le budget IA mensuel de votre organisation est épuisé. Un administrateur peut l'augmenter.",
    stoppedByTime:
      "Réponse interrompue : elle a atteint sa limite de temps pour le client externe. Reposez la question ici pour continuer.",
    stoppedByTurnSpend:
      "Réponse interrompue : elle a atteint le coût IA maximal d'une réponse. Posez une question plus ciblée, ou demandez à un administrateur de relever la limite.",
    stoppedByUserShare:
      "Réponse interrompue : vous avez utilisé votre part du budget IA mensuel de votre organisation. Vous pourrez reposer la question le mois prochain, ou demander à un administrateur de relever votre part.",
    unsourcedBadge: "Hors sources",
    unverifiedSpan: "non vérifié",
    hiddenNotice: "Masqué : s'appuie sur des sources que vous ne pouvez pas lire",
    hiddenExplained:
      "Le reste de la conversation reste visible. Si vous avez besoin de cette réponse, demandez à un responsable de projet.",
    speaker: { user: "Vous", assistant: "Réponse" },
    state: {
      pending: "citation en attente",
      checking: "Vérification…",
      supported: "Étayée",
      unsupported: "Non vérifiée",
      unchecked: "Non contrôlée",
      deleted: "Source supprimée",
      unavailable: "Source indisponible",
    },
    preview: {
      folder: "Dossier :",
      loading: "Chargement du passage…",
      versionChanged:
        "La source a été mise à jour depuis cette réponse. Le passage cité n'est plus affiché.",
      noExcerpt: "Aucun texte de passage à afficher.",
      unavailable: "Source indisponible",
      unavailableBody: "Vous ne pouvez plus lire cette source.",
      label: "Citation {{handle}}",
    },
    web: {
      badge: "web",
      span: "issu du web",
      sources: "Résultats web",
      open: "Ouvrir la page",
      noLink: "Aucun lien à ouvrir",
      noSnippet: "Aucun extrait à afficher.",
      retrieved: "Récupéré le {{date}}",
    },
    help: {
      badge: "Aide ESI",
      chip: "Aide ESI : {{title}}",
      sources: "Aide ESI",
      open: "Ouvrir cet écran",
      adminOnly:
        "C’est une tâche d’administrateur. Demandez à un administrateur de votre organisation.",
      noProject: "Cet écran se trouve dans un projet : ouvrez-le depuis le projet concerné.",
      earlier: "Décrit une version antérieure d’ESI.",
      gone: "Cette page d’aide n’existe plus dans ESI.",
      noExcerpt: "Aucun passage à afficher.",
    },
  },
  conversation: {
    pick: "Conversation",
    switcher: "Changer de conversation",
    newOption: "Nouvelle conversation",
    untitled: "Conversation sans titre",
    sharedBy: "partagée par {{name}}",
    share: "Partager",
    thread: "Messages",
    empty:
      "Posez une question sur les fichiers de ce projet. Les réponses citent les passages sur lesquels elles s'appuient.",
    ask: "Posez une question sur ce projet…",
    send: "Envoyer",
    thinking: "Recherche dans les sources du projet…",
    saveAsFile: "Enregistrer comme fichier",
    frozen: "Ce projet est en lecture seule : aucun message ne peut être envoyé.",
    sharedReadOnly:
      "Partagée avec vous par {{name}}. Vous pouvez la lire ; seule cette personne peut la poursuivre.",
    projectSharedReadOnly:
      "Partagée avec le projet par {{name}}. Vous pouvez la lire ; seule cette personne peut la poursuivre.",
    userShareRefused:
      "Vous avez utilisé votre part du budget IA mensuel de votre organisation : aucune nouvelle réponse ne peut commencer avant le mois prochain. Votre message est de retour dans la zone de saisie. Un administrateur peut relever votre part.",
    budgetRefused:
      "Le budget IA mensuel de votre organisation est épuisé : aucune nouvelle réponse ne peut commencer. Votre message est de retour dans la zone de saisie. Un administrateur peut relever le budget.",
    turnFailed:
      "La réponse n'a pas pu être terminée. Votre message est de retour dans la zone de saisie : renvoyez-le.",
    status: {
      submitted: "Recherche dans les sources du projet…",
      streaming: "Rédaction de la réponse…",
      done: "Réponse prête.",
    },
    question: {
      title: "L'assistant demande",
      forYou: "Question pour vous",
      youPicked: "Vous avez choisi {{choice}}",
      picked: "Réponse : {{choice}}",
      choices: "Choix",
      ownWords: "Répondre avec vos mots",
    },
    timeline: {
      label: "Étapes de cette réponse",
      progress: "Avancement de la réponse",
      details: "Afficher les détails",
      hideDetails: "Masquer les détails",
      more: "+{{count}}",
      done: {
        readTasks_one: "A lu les tâches ({{count}})",
        readTasks_other: "A lu les tâches ({{count}})",
        proposedTask_zero: "N'a proposé aucune tâche",
        proposedTask_one: "A proposé une tâche",
        proposedTask_other: "A proposé {{count}} tâches",
        recalled_one: "A rappelé une mémoire",
        recalled_other: "A rappelé {{count}} mémoires",
        proposedMemory_one: "A proposé une mémoire",
        proposedMemory_other: "A proposé une mémoire",
        thinking: "A réfléchi",
        search: "A cherché dans les sources",
        read_one: "A lu {{count}} document",
        read_other: "A lu {{count}} documents",
        readNone: "A lu des sources",
        web: "A cherché sur le web",
        wiki_write: "A modifié le wiki",
        artefact: "A rédigé un artefact",
        code: "A exécuté du code",
        task: "A travaillé sur les tâches",
        memory: "A utilisé la mémoire",
        question: "Vous a posé une question",
        people: "A cherché qui sait",
        answer: "A rédigé la réponse",
        other: "A utilisé un outil",
        help: "A cherché dans l'aide d'ESI",
        feedback: "A rédigé un signalement",
      },
      running: {
        readTasks: "Lecture des tâches…",
        proposedTask: "Proposition d'une tâche…",
        recalled: "Rappel des mémoires…",
        proposedMemory: "Proposition d'une mémoire…",
        thinking: "Réflexion…",
        search: "Recherche dans les sources…",
        read: "Lecture…",
        web: "Recherche sur le web…",
        wiki_write: "Modification du wiki…",
        artefact: "Rédaction d'un artefact…",
        code: "Exécution du code…",
        task: "Travail sur les tâches…",
        memory: "Utilisation de la mémoire…",
        question: "Question en cours…",
        people: "Recherche de qui sait…",
        answer: "Rédaction de la réponse…",
        other: "En cours…",
        checking: "Vérification des sources…",
        starting: "En cours…",
      },
      thoughtFor: "A réfléchi pendant {{duration}}",
      thoughtFallback: "A réfléchi à l'étape suivante",
      underSecond: "< 1 s",
      seconds: "{{count}} s",
      minutes: "{{minutes}} min {{seconds}} s",
    },
    yours: "Les vôtres",
    sharedWithYou: "Partagées avec vous",
  },
  tools: {
    label: {
      web_search: "Recherche web",
      web_fetch: "Lecture de page",
      run_code: "Exécuter du code",
      other: "Outil externe",
    },
    code: {
      program: "Programme",
      programIn: "Programme ({{language}})",
      output: "Sortie",
      errors: "Erreurs",
      showAll_one: "Tout afficher ({{count}} caractère de plus)",
      showAll_other: "Tout afficher ({{count}} caractères de plus)",
      showLess: "Afficher moins",
      exit: "Code de sortie {{code}}",
      filesMade_one: "{{count}} fichier",
      filesMade_other: "{{count}} fichiers",
      filesIn: "Fichiers donnés au programme",
      dropped: "Non conservés",
      dropReason: {
        type: "ce type de fichier n'est pas autorisé",
        size: "au-delà de la taille autorisée",
        count: "au-delà du nombre de fichiers conservés par exécution",
        limit: "la conversation contient déjà le maximum d'artefacts",
        writes: "la réponse a déjà écrit le maximum d'artefacts",
        artefacts_off: "les artefacts sont désactivés ici",
        failed: "il n'a pas pu être enregistré",
      },
      declined: "Vous avez refusé : le programme n'a pas été exécuté.",
      failed: "Le programme n'a pas pu s'exécuter : le bac à sable est indisponible ou l'a arrêté.",
      blocked: "Le programme n'a pas été exécuté.",
      downloadNamed: "Télécharger {{name}}",
      approval: {
        title: "Exécuter ce programme ?",
        explained:
          "Cette réponse a lu du contenu restreint. Le programme s'exécute dans un bac à sable isolé, sans réseau, et seulement si vous l'autorisez.",
        files: "Avec ces fichiers",
        allow: "L'exécuter",
      },
    },
    outcome: {
      running: "En cours…",
      pending: "En attente de confirmation",
      done: "Terminé",
      blocked: "Bloqué",
      declined: "Refusé",
      expired: "Expiré",
      failed: "Échec",
    },
    arguments: {
      sent: "Envoyé hors d'ESI",
      proposed: "Serait envoyé hors d'ESI",
      notSent: "Non envoyé",
    },
    blockedBecause: "Rien n'est sorti d'ESI.",
    reason: {
      classification:
        "Cette réponse s'appuie sur des informations dont la classification interdit l'envoi hors de l'organisation.",
      verbatim: "La requête reprenait le texte d'un passage lu pour cette réponse.",
      verbatim_confidential:
        "La requête reprenait le texte d'un passage confidentiel lu pour cette réponse.",
      handle: "La requête mentionnait une source de ce projet.",
      residency:
        "Le fournisseur de cet outil est hors de la zone de résidence des données autorisée ici.",
      turn_limit: "Cette réponse a déjà fait autant de requêtes externes que permis.",
      model_budget: "Le budget IA mensuel de votre organisation est épuisé.",
      user_budget: "Vous avez utilisé votre part du budget IA mensuel de votre organisation.",
      no_confirmation_channel:
        "Cette requête demande votre confirmation, que seul ESI peut vous demander.",
      url_provenance: "L'assistant a composé cette adresse lui-même.",
      fetch_policy: "Votre organisation exige une autorisation pour chaque page.",
      hidden_characters: "La requête contenait des caractères invisibles pour vous.",
      file_unavailable: "Un fichier donné au programme ne vous est plus accessible.",
      invalid_url: "L'adresse n'est pas une adresse web valide.",
      host_denied: "L'adresse n'est pas une page web publique.",
      address_denied: "L'adresse mène à un réseau privé ou réservé.",
      domain_denied: "Votre organisation n'autorise pas ce site.",
      redirect_limit: "La page a redirigé trop de fois.",
      redirect_denied: "La page redirigeait vers une adresse non autorisée.",
      timeout: "La page a mis trop de temps à répondre.",
      too_large: "La page était trop volumineuse.",
      unsupported_type: "Ce type de contenu n'est pas pris en charge.",
      upstream: "Le site n'a pas répondu.",
    },
    declined: "Vous avez refusé cette requête : rien n'est sorti d'ESI.",
    expired:
      "Cette requête n'a pas été confirmée à temps : rien n'est sorti d'ESI. Reposez la question pour continuer.",
    alreadyDecided: "Cette requête a déjà reçu une réponse. Reposez la question pour continuer.",
    budget:
      "Le budget IA mensuel de votre organisation est épuisé : rien n'a été envoyé. Rechargez pour répondre à nouveau une fois qu'il est relevé.",
    userBudget:
      "Vous avez utilisé votre part du budget IA mensuel de votre organisation : rien n'a été envoyé. Rechargez pour répondre à nouveau une fois qu'elle est relevée.",
    unavailable:
      "Cette requête ne peut plus recevoir de réponse. Reposez la question pour continuer.",
    failed: "Le service externe n'a pas répondu.",
    refused: "La requête dépassait ce que cet outil accepte : rien n'est sorti d'ESI.",
    results_one: "{{count}} résultat",
    results_other: "{{count}} résultats",
    resultsNote: "Contenu extérieur à l'organisation : vérifiez-le avant de vous y fier.",
    wiki: {
      written_one: "Wiki : {{count}} modification écrite",
      written_other: "Wiki : {{count}} modifications écrites",
      writing: "Écriture dans le wiki…",
    },
    help: {
      searched: "Recherche dans l’aide ESI",
      searching: "Recherche dans l’aide ESI…",
      pages_one: "{{count}} page d’aide",
      pages_other: "{{count}} pages d’aide",
    },
    web: {
      searched: "Recherche sur le web",
      searching: "Recherche sur le web en cours…",
      showResults_one: "Afficher le résultat",
      showResults_other: "Afficher les {{count}} résultats",
      approval: "ESI enverra cette requête au service de recherche web :",
      blocked: {
        classification:
          "La recherche web n'est pas disponible dans cette conversation en raison des sources qu'elle a utilisées.",
        residency:
          "La recherche web n'est pas disponible avec la résidence des données de ce projet.",
        other: "Cette recherche a été bloquée : rien n'a été envoyé.",
      },
      declined: "Vous avez refusé cette recherche : rien n'a été envoyé.",
      expired:
        "Cette recherche n'a pas été confirmée à temps : rien n'a été envoyé. Reposez la question pour continuer.",
      failed: "Le service de recherche web n'a pas répondu.",
      refused: "Cette recherche dépassait ce que la recherche web accepte : rien n'a été envoyé.",
    },
    fetch: {
      read: "Page lue",
      reading: "Lecture de la page…",
      address: "Adresse de la page",
      truncated: "Page longue : seul son début a été lu.",
      notAllowed: "Non autorisé",
      unsupported: "Contenu non pris en charge",
      approval: {
        title: "Autoriser ESI à lire cette page ?",
        url_provenance:
          "L'assistant a composé cette adresse lui-même : vérifiez-la avant d'autoriser.",
        fetch_policy: "Votre organisation exige une autorisation pour chaque page.",
        classification: "Cette conversation a lu des sources confidentielles.",
        other: "ESI demandera exactement cette adresse :",
      },
      blocked: {
        classification:
          "La lecture de pages n'est pas disponible dans cette conversation en raison des sources qu'elle a utilisées.",
        residency:
          "La lecture de pages n'est pas disponible avec la résidence des données de ce projet.",
        other: "Cette page n'a pas été lue : rien n'a été envoyé.",
      },
      declined: "Vous avez refusé : la page n'a pas été lue.",
      expired:
        "Cette page n'a pas été autorisée à temps : elle n'a pas été lue. Reposez la question pour continuer.",
      failed: "La page n'a pas pu être lue.",
      refused: {
        domain_denied: "Votre organisation n'autorise pas la lecture des pages de ce site.",
        redirect_denied: "La page redirigeait vers une adresse non autorisée : rien n'a été lu.",
        not_public: "Cette adresse n'est pas une page web publique : rien n'a été envoyé.",
        unsupported_type:
          "Ce contenu ne peut pas être lu : seuls les pages web, le texte brut, le Markdown et le JSON le peuvent.",
        other: "Cette adresse ne peut pas être lue : rien n'a été envoyé.",
      },
    },
    approval: {
      title: "Autoriser cette requête à sortir d'ESI ?",
      explained: "{{tool}} enverra exactement ceci à un service externe :",
      allow: "Autoriser",
      decline: "Refuser",
    },
  },
  projectShare: {
    withProject: "avec le projet",
    heading: "Partager avec le projet",
    everyone: "Tout le projet",
    shared: "Partagée avec le projet",
    share: "Partager",
    stop: "Arrêter le partage",
    working: "Enregistrement…",
    nobodyYet: "Aucun autre membre pour l'instant. Les personnes qui rejoindront pourront la lire.",
    allWillRead_one: "L'autre membre pourra la lire",
    allWillRead_other: "Les {{count}} autres membres pourront la lire",
    allRead_one: "L'autre membre peut la lire",
    allRead_other: "Les {{count}} autres membres peuvent la lire",
    someExcluded_one: "{{count}} membre sur {{total}} ne peut pas lire certaines de ses sources",
    someExcluded_other:
      "{{count}} membres sur {{total}} ne peuvent pas lire certaines de ses sources",
    guests:
      "Atteint les membres, y compris ceux qui rejoindront le projet, qui peuvent lire toutes ses sources. Les invités ne sont pas inclus : nommez-les ci-dessous.",
    frozen: "Ce projet est en lecture seule : ses partages ne peuvent pas changer.",
    adminStop: "Arrêter le partage avec le projet",
    adminStopTitle: "Arrêter de partager cette conversation avec le projet\u{202F}?",
    adminStopBody:
      "Toutes les personnes qui la lisent par le projet en perdent l\u{2019}accès, y compris vous, sauf si votre nom y figure. Les personnes désignées nommément la gardent.",
    adminStopConfirm: "Arrêter le partage",
  },
  share: {
    refusedPerson: "{{name}} : {{reason}}",
    title: "Partager cette conversation",
    explain:
      "Les personnes avec qui vous partagez peuvent lire la conversation, pas la poursuivre. Chaque personne est contrôlée sur les sources utilisées ; quiconque ne peut pas toutes les lire est refusé.",
    sharedWith: "Partagée avec",
    nobody: "Aucun destinataire nommé pour l'instant.",
    stop: "Arrêter le partage",
    add: "Partager avec",
    noCandidates: "Toutes les autres personnes qui peuvent lire ce projet l'ont déjà.",
    noMatch: "Aucune personne pouvant lire ce projet ne correspond.",
    find: "Rechercher des personnes",
    findHint:
      "Seules les 200 premières personnes sont listées : saisissez un nom ou une adresse e-mail.",
    onlyYou: "Personne d'autre ne peut encore lire ce projet.",
    addPeople: "Ajouter des personnes au projet",
    addPeopleHint: "Un responsable du projet peut y ajouter des personnes.",
    membersUnavailable: "La liste des membres n'a pas pu être chargée.",
    sharedNow: "Partagée avec {{names}}.",
    refused_one: "{{count}} personne a été refusée :",
    refused_other: "{{count}} personnes ont été refusées :",
    sharing: "Partage…",
    submit: "Partager",
    done: "Terminé",
    explainStandalone:
      "Les personnes avec qui vous partagez peuvent lire la conversation, pas la poursuivre. Indiquez leur adresse e-mail ; toute adresse qui ne peut pas la recevoir est refusée.",
    someone: "Quelqu'un",
    emails: "Adresses e-mail",
    emailsPlaceholder: "nom@exemple.com",
    emailsHint: "Séparez les adresses par des virgules ou des retours à la ligne (20 au plus).",
    cannotReceive: "{{email}} ne peut pas recevoir cette conversation.",
  },
  save: {
    title: "Enregistrer la réponse comme fichier",
    readers_one: "{{count}} personne pourra le lire",
    readers_other: "{{count}} personnes pourront le lire",
    readersExplain:
      "Seules les personnes qui peuvent lire toutes les sources de cette réponse. Si une source est restreinte plus tard, le fichier l'est aussi.",
    webExplain:
      "Une partie de cette réponse provient de pages web publiques : le fichier est marqué comme externe.",
    classification: "Classification",
    origin: "Origine",
    fileTitle: "Nom du fichier",
    fileTitlePlaceholder: "Laisser vide pour un nom tiré de la réponse",
    suggesting: "Proposition d’un nom…",
    saved: "Enregistré sous « {{title}} ». Il apparaît dans Sources une fois traité.",
    saving: "Enregistrement…",
    submit: "Enregistrer le fichier",
    cancel: "Annuler",
    done: "Terminé",
    target: "Projet",
    targetHint: "Le fichier est enregistré dans ce projet, pour ses lecteurs.",
    noTarget: "Aucun de vos projets n'accepte vos réponses enregistrées.",
    residencyNarrower:
      "Cette conversation a reçu des réponses de modèles hors UE : elle ne peut pas aller dans ce projet limité à l'UE.",
  },
  residency: {
    title: "Résidence des données",
    intro:
      "Où les modèles peuvent traiter les requêtes et les sources de cette organisation. UE garde chaque appel de modèle dans l'UE ; Mondial autorise aussi des modèles mondiaux, avec des modèles UE en secours. Les fichiers et les conversations restent stockés dans l'UE dans tous les cas.",
    ceilingEuOnly: "Cette instance n'autorise que des modèles UE : la résidence est UE partout.",
    value: { eu: "UE uniquement", global: "Mondial" },
    source: {
      instance: "Instance",
      tenant: "Organisation",
      entity: "Entité",
      project: "Projet",
    },
    locked: "Verrouillé",
    lockedAbove: "Verrouillé au-dessus",
    organisation: "Organisation",
    entities: "Organisation et entités",
    projects: "Projets",
    name: "Nom",
    residency: "Résidence",
    origin: "Provient de",
    lock: "Verrouiller pour les niveaux inférieurs",
    save: "Enregistrer",
    saving: "Enregistrement…",
    personalProject: "Projet personnel",
    globalCount_zero: "Aucun projet n'utilise de modèles mondiaux.",
    globalCount_one: "{{count}} projet peut utiliser des modèles mondiaux.",
    globalCount_other: "{{count}} projets peuvent utiliser des modèles mondiaux.",
    choose: "Résidence de {{name}}",
  },
  signin: {
    title: "Connexion",
    lead: "Comment les personnes de votre organisation se connectent : leur fournisseur d'identité, et les domaines qui les y envoient.",
    adminsOnly: "Seuls les administrateurs de l'organisation gèrent la connexion.",
    providers: "Fournisseurs d'identité",
    providersCaption: "Fournisseurs d'identité",
    noProviders:
      "Aucun fournisseur d'identité. Les personnes se connectent avec des comptes ESI jusqu'à ce que vous en ajoutiez un et y associiez un domaine.",
    addGoogle: "Ajouter Google Workspace",
    protocol: { oidc: "OpenID Connect", saml: "SAML" },
    preset: { google_workspace: "Google Workspace" },
    status: { active: "Actif", disabled: "Désactivé" },
    googleRule:
      "N'admet que les comptes de ses domaines associés dont le Google Workspace correspond. Les comptes Google personnels sont refusés.",
    redirectUri: "URI de redirection",
    redirectUriHelp:
      "Collez-la comme URI de redirection autorisée du client du fournisseur d'identité.",
    copy: "Copier",
    copied: "Copié",
    google: {
      title: "Ajouter Google Workspace",
      description:
        "Utilise un client OAuth de votre propre projet Google Cloud. L'émetteur et les portées de Google sont fixés par ESI.",
      steps:
        "Dans Google Cloud : créez un client OAuth de type Application Web, réglez l'écran de consentement sur Interne et ajoutez l'URI de redirection ci-dessous.",
      restrict:
        "Facultatif : dans la console d'administration Google (Sécurité, Commandes des API, Contrôle des accès des applications), n'autorisez ce client que pour le groupe ou l'unité organisationnelle qui doit utiliser ESI, pour que personne d'autre ne puisse même y accéder.",
      displayName: "Nom affiché à la connexion",
      clientId: "ID client",
      clientSecret: "Code secret du client",
      secretHelp: "Transmis une fois au service de connexion. ESI ne le conserve pas.",
      submit: "Ajouter Google Workspace",
      adding: "Ajout…",
      added:
        "{{name}} a été ajouté. Associez-lui un domaine vérifié pour que ses personnes puissent se connecter.",
      defaultName: "Google Workspace",
    },
    domains: "Domaines",
    domainsCaption: "Domaines",
    noDomains:
      "Aucun domaine. Revendiquez le domaine de messagerie de votre organisation pour orienter ses connexions.",
    claim: "Revendiquer un domaine",
    claimTitle: "Revendiquer un domaine",
    claimDescription:
      "Choisissez où vont ses connexions. Vous prouvez la propriété par un enregistrement DNS TXT avant qu'il n'oriente qui que ce soit.",
    domain: "Domaine",
    routesTo: "Les connexions vont vers",
    nativeAccounts: "Comptes ESI",
    routesToLine: "Les connexions vont vers {{name}}",
    nativeLine: "Les connexions utilisent des comptes ESI",
    bindingFixed:
      "La destination est figée une fois le domaine vérifié. Choisissez-la maintenant : déplacer un domaine vérifié n'est pas encore possible.",
    claimSubmit: "Revendiquer",
    claiming: "Revendication…",
    verified: "Vérifié",
    pending: "En attente de vérification DNS",
    txtName: "Nom de l'enregistrement TXT",
    txtValue: "Valeur de l'enregistrement TXT",
    verify: "Vérifier",
    verifying: "Vérification…",
    verifyFor: "Vérifier {{domain}}",
    verifiedNotice: "{{domain}} est vérifié.",
    mode: {
      labels: {
        sso_or_esi: "SSO ou mot de passe ESI",
        sso_admins_esi: "SSO uniquement",
      },
      help: {
        sso_or_esi:
          "Tout le monde se connecte avec {{name}}, et peut aussi créer un mot de passe ou une clé d'accès ESI. Le réglage par défaut.",
        sso_admins_esi:
          "Tout le monde se connecte avec {{name}}. Les administrateurs gardent un mot de passe ou une clé d'accès ESI pour entrer si {{name}} est indisponible ; chaque usage est journalisé et signalé par e-mail aux autres administrateurs.",
      },
      chipPrefix: "Mode de connexion :",
      change: "Modifier",
      changeFor: "Modifier la connexion de {{domain}}",
      title: "Connexion pour {{domain}}",
      lead: "Les personnes de ce domaine peuvent toujours se connecter avec {{name}}. Choisissez qui peut aussi utiliser un mot de passe ou une clé d'accès ESI.",
      choose: "Mode de connexion de {{domain}}",
      current: "(actuel)",
      mfaOptional:
        "La connexion ESI suit le réglage de vérification en deux étapes de votre organisation, actuellement facultative : un mot de passe seul suffit.",
      mfaRequired:
        "La connexion ESI suit le réglage de vérification en deux étapes de votre organisation, actuellement obligatoire : une application d'authentification ou une clé d'accès en plus.",
      mfaUnknown:
        "La connexion ESI suit le réglage de vérification en deux étapes de votre organisation.",
      mfaLink: "Le modifier dans Sécurité",
      people_one: "{{count}} personne perd la connexion ESI",
      people_other: "{{count}} personnes perdent la connexion ESI",
      impact:
        "{{people}} : {{sessions}} et {{grants}} prennent fin. Leur SSO continue de fonctionner.",
      refused: {
        last_sign_in_method: {
          title: "Aucun administrateur ne pourrait se connecter après ce changement.",
          body: "Aucun administrateur ne peut encore se connecter : aucun ne s'est connecté avec {{name}} ni n'a de mot de passe ou de clé d'accès ESI. Faites d'abord connecter un administrateur avec {{name}}.",
        },
      },
      save: "Enregistrer",
      saving: "Enregistrement…",
      saved: "{{domain}} utilise désormais « {{mode}} ».",
      savedSessions_one: "{{count}} session de connexion ESI a pris fin.",
      savedSessions_other: "{{count}} sessions de connexion ESI ont pris fin.",
    },
    removal: {
      actionsFor: "Actions pour {{name}}",
      removeIdpItem: "Supprimer le fournisseur…",
      releaseDomainItem: "Libérer le domaine…",
      cancelClaimItem: "Annuler la revendication",
      idpTitle: "Supprimer {{name}} ?",
      idpLead: "Plus personne ne pourra se connecter via {{name}}. Cette action est irréversible.",
      domainTitle: "Libérer {{domain}} ?",
      domainLead:
        "{{domain}} n'oriente plus les connexions, et une autre organisation pourra le vérifier. Cette action est irréversible.",
      claimTitle: "Annuler la revendication de {{domain}} ?",
      claimLead:
        "Il n'est pas encore vérifié : il n'oriente personne. Son jeton DNS cesse de fonctionner.",
      impact: "Ce que cela arrête",
      nobodyLoses: "Personne ne perd l'accès à la connexion.",
      people_one: "{{count}} personne perd l'accès à la connexion.",
      people_other: "{{count}} personnes perdent l'accès à la connexion.",
      createdAtFirstSignIn_one: "{{count}} d'entre elles a été créée à sa première connexion.",
      createdAtFirstSignIn_other:
        "{{count}} d'entre elles ont été créées à leur première connexion.",
      accountsStayIdp:
        "Leurs comptes et leurs contenus restent ; elles pourront se reconnecter dès qu'un nouveau fournisseur sera configuré pour leur domaine.",
      accountsStayDomain:
        "Leurs comptes et leurs contenus restent ; elles pourront se reconnecter dès que le domaine sera de nouveau vérifié et associé à un fournisseur.",
      signInContinues_one:
        "{{count}} personne est déconnectée mais peut encore se connecter via {{name}}, qui peut se porter garant d'adresses hors de ses domaines vérifiés.",
      signInContinues_other:
        "{{count}} personnes sont déconnectées mais peuvent encore se connecter via {{name}}, qui peut se porter garant d'adresses hors de ses domaines vérifiés.",
      sessions_one: "{{count}} session active",
      sessions_other: "{{count}} sessions actives",
      grants_one: "{{count}} application connectée",
      grants_other: "{{count}} applications connectées",
      sessionsEnd: "{{sessions}} et {{grants}} prennent fin maintenant.",
      domains_one:
        "Le domaine {{domains}} est libéré. Revendiquez-le et vérifiez-le de nouveau pour l'utiliser avec un autre fournisseur.",
      domains_other:
        "Les domaines {{domains}} sont libérés. Revendiquez-les et vérifiez-les de nouveau pour les utiliser avec un autre fournisseur.",
      source:
        "La source d'annuaire {{name}} est supprimée, avec {{groups}} et {{memberships}}. Les accès accordés par ces groupes prennent fin.",
      groups_one: "{{count}} groupe",
      groups_other: "{{count}} groupes",
      memberships_one: "{{count}} appartenance",
      memberships_other: "{{count}} appartenances",
      signsOutActorIdp: "Votre compte est lié à {{name}} : vous serez déconnecté vous aussi.",
      signsOutActorDomain:
        "Vous faites partie des personnes déconnectées : votre session prend fin aussi.",
      refusedIdpTitle: "Vous ne pouvez pas supprimer {{name}}.",
      refusedIdp:
        "Aucun administrateur de l'organisation ne garderait de moyen de se connecter. Pour le remplacer, associez votre domaine au nouveau fournisseur et faites-y se connecter un administrateur, puis supprimez {{name}}.",
      refusedDomainTitle: "Vous ne pouvez pas libérer {{domain}}.",
      refusedDomain:
        "Aucun administrateur de l'organisation ne garderait de moyen de se connecter. Faites d'abord se connecter un administrateur via un autre fournisseur, ou avec un compte ESI.",
      typeToConfirm: "Saisissez {{name}} pour confirmer",
      recentSignIn:
        "Une connexion de moins de 5 minutes est requise : il pourra vous être demandé de vous reconnecter.",
      removeIdp: "Supprimer le fournisseur",
      releaseDomain: "Libérer le domaine",
      removing: "Suppression…",
      releasing: "Libération…",
      cancelClaim: "Annuler la revendication",
      cancelling: "Annulation…",
      keep: "Garder la revendication",
      close: "Fermer",
      cleanupTitle: "Terminer la suppression de {{name}}",
      cleanupLeft:
        "{{name}} est supprimé, mais son nettoyage dans le service de connexion n'est pas terminé.",
      finishCleanup: "Terminer le nettoyage",
      claimVerified:
        "{{domain}} a été vérifié entre-temps. Fermez cette fenêtre et choisissez Libérer le domaine.",
      retryAccount: "Réessayer",
      cleanupStillPending:
        "Le nettoyage de {{name}} n'a pas abouti cette fois non plus. Réessayez plus tard ; personne ne peut se connecter via ce fournisseur entre-temps.",
      cleanupPending:
        "{{name}} est supprimé : plus personne ne peut se connecter via ce fournisseur. Son nettoyage dans le service de connexion n'est pas terminé. Réessayez pour le terminer ; il pourra vous être demandé de vous reconnecter.",
      retry: "Relancer le nettoyage",
      retrying: "Nouvel essai…",
      cleanupDone: "{{name}} est supprimé et son nettoyage est terminé.",
      idpRemoved: "{{name}} est supprimé.",
      domainReleased: "{{domain}} est libéré.",
      claimCancelled: "La revendication de {{domain}} est annulée.",
      keepTitle: "Conserver un domaine au lieu de le libérer",
      keepNone: "Ne conserver aucun domaine",
      keepDomain: "Conserver {{domain}} et passer ses utilisateurs à la connexion ESI",
      keepCounts: "{{kept}} ; {{links}}.",
      keepWithCredential_one: "{{count}} personne garde sa connexion ESI",
      keepWithCredential_other: "{{count}} personnes gardent leur connexion ESI",
      keepLinks_one: "{{count}} reçoit un lien pour définir un mot de passe, valable 72 heures",
      keepLinks_other:
        "{{count}} reçoivent un lien pour définir un mot de passe, valable 72 heures",
      keepMfaOptional:
        "Les personnes d'un domaine conservé suivent le réglage de vérification en deux étapes de votre organisation, actuellement facultative : un mot de passe seul suffit.",
      keepMfaRequired:
        "Les personnes d'un domaine conservé suivent le réglage de vérification en deux étapes de votre organisation, actuellement obligatoire : une application d'authentification ou une clé d'accès en plus.",
      linksSent_one: "{{count}} lien de définition du mot de passe envoyé.",
      linksSent_other: "{{count}} liens de définition du mot de passe envoyés.",
      linksFailed_one:
        "{{count}} n'a pas pu être envoyé : la personne peut en demander un nouveau sur la page « Recevoir un nouveau lien d'invitation » d'ESI.",
      linksFailed_other:
        "{{count}} n'ont pas pu être envoyés : ces personnes peuvent en demander un nouveau sur la page « Recevoir un nouveau lien d'invitation » d'ESI.",
    },
  },
  directory: {
    google: {
      add: "Ajouter une source Google Workspace",
      needsIdp:
        "Pour synchroniser les groupes Google Workspace, ajoutez d'abord le fournisseur d'identité Google Workspace dans Connexion.",
      openSignin: "Ouvrir Connexion",
      title: "Ajouter une source Google Workspace",
      description:
        "Lit les utilisateurs, groupes et appartenances avec un compte de service de votre projet Google Cloud. Rien n'est modifié dans Google.",
      name: "Nom",
      defaultName: "Google Workspace",
      identityProvider: "Fournisseur d'identité",
      key: "Clé du compte de service (JSON)",
      keyHelp:
        "Écrite une seule fois et conservée chiffrée hors de la base de données. ESI n'affiche ensuite que le compte et l'ID de la clé.",
      keyChosen: "{{email}} · clé {{keyId}}",
      keyInvalid: "Ce fichier n'est pas une clé de compte de service Google (JSON).",
      scope: "Qui reçoit un compte ESI",
      scopeHelp:
        "Les groupes et appartenances se synchronisent pour tous. Les comptes ne sont créés que pour les membres de ce groupe (imbriqués compris) ou les utilisateurs de cette unité organisationnelle.",
      scopeGroup: "Membres d'un groupe",
      scopeOrgUnit: "Utilisateurs d'une unité organisationnelle",
      groupEmail: "Adresse du groupe",
      orgUnitPath: "Chemin de l'unité organisationnelle",
      privileges: "Droits à accorder",
      privilegesHelp:
        "Dans la console d'administration Google, attribuez au compte de service un rôle d'administrateur personnalisé avec uniquement ces droits de lecture. Aucune délégation au niveau du domaine.",
      privilege: {
        users_read: "Utilisateurs : lecture",
        groups_read: "Groupes : lecture",
        org_units_read: "Unités organisationnelles : lecture",
      },
      residency:
        "Les données Workspace restent là où Google les conserve ; ESI stocke les groupes et comptes synchronisés dans sa propre région.",
      submit: "Ajouter la source",
      adding: "Ajout…",
      added: "{{name}} a été ajoutée, désactivée. Testez-la, puis activez-la.",
      account: "{{email}} · clé {{keyId}}",
      scopeLine: {
        group: "Comptes pour les membres de {{value}}",
        orgUnit: "Comptes pour les utilisateurs de {{value}}",
      },
      skipped: "Écartés à la dernière exécution : {{list}}",
      skip: {
        outside_scope: "{{count}} hors du périmètre",
        external_member: "{{count}} hors du Workspace",
        customer_member: "{{count}} membre « tous les utilisateurs »",
      },
      test: "Tester",
      testing: "Test…",
      testFor: "Tester {{name}}",
      enable: "Activer",
      enabling: "Activation…",
      replaceKey: "Remplacer la clé",
      replacing: "Remplacement…",
      keyReplaced: "La clé de {{name}} a été remplacée.",
      testPassed:
        "{{name}} : le test a réussi. Le compte de service peut lire ce dont la source a besoin.",
      testMissing:
        "{{name}} : droit manquant : {{list}}. Accordez-le dans la console d'administration Google, puis testez à nouveau.",
      testFailed: "{{name}} : le test a échoué. {{reason}}",
      testError: {
        no_adapter: "Cette source ne peut pas être testée.",
        credential_missing: "La source n'a pas de clé utilisable.",
        credential_refused:
          "La clé n'a pas pu être lue : elle est absente, ou appartient à une autre organisation.",
        token_refused: "Google a refusé la clé du compte de service.",
        scope_not_found: "Le groupe ou l'unité organisationnelle est introuvable.",
        throttled: "Google a limité les appels. Réessayez dans une minute.",
        unreachable: "Google n'a pas pu être joint.",
      },
    },
    unknownReason: "Raison inconnue",
    identityProvider: "Fournisseur d'identité",
    identityProviderFor: "Fournisseur d'identité de {{name}}",
    noIdentityProvider: "Aucun",
    identityProviderHelp:
      "Le fournisseur d'identité au nom duquel parle cette source. Une première connexion ne retrouve les identifiants qu'elle enregistre que par ce fournisseur ; sans fournisseur, elle n'en enregistre aucun.",
    unboundHint:
      "Aucun fournisseur d'identité : les identifiants de fournisseur qu'elle envoie sont ignorés.",
    lapsed_one: "{{count}} personne doit se reconnecter pour retrouver ses groupes",
    lapsed_other: "{{count}} personnes doivent se reconnecter pour retrouver leurs groupes",
    lapsedHint:
      "Leurs groupes venant de ce fournisseur d'identité ne donnent plus accès : le fournisseur ne les a pas confirmés dans le délai de validité de l'organisation.",
    title: "Annuaire",
    intro:
      "Là où votre fournisseur d'identité, SCIM et les flux RH maintiennent groupes, attributs et appartenances en phase avec ESI. Les accès suivent ces changements dès la requête suivante.",
    adminsOnly:
      "Seuls les administrateurs peuvent gérer l'annuaire. Demandez à un administrateur de l'organisation si vous avez besoin d'un changement.",
    sources: "Sources",
    caption: "Sources de l'annuaire",
    noSources:
      "Aucune source pour l'instant. Ajoutez une source SCIM pour votre fournisseur d'identité, ou une source push pour un flux RH.",
    name: "Nom",
    kind: "Type",
    kindLabel: { idp: "Groupes de connexion", scim: "SCIM", push: "API push", pull: "Pull" },
    status: "État",
    statusLabel: { active: "Active", disabled: "Désactivée" },
    lastSync: "Dernière synchronisation",
    lastFull: "Dernière réconciliation complète",
    never: "Jamais",
    stale: "En retard",
    staleHint: "Aucune synchronisation terminée dans son intervalle.",
    errors: "Échecs récents",
    noErrors: "Aucun",
    errorLine: "{{reason}} ({{item}})",
    moreErrors: "et {{count}} de plus",
    rotate: "Renouveler le jeton",
    rotating: "Renouvellement…",
    add: "Ajouter la source",
    adding: "Ajout…",
    newName: "Nom de la source",
    newKind: "Type",
    newKindLabel: { scim: "SCIM (Entra ID, Okta…)", push: "API push (flux RH ou ERP)" },
    tokenTitle: "Jeton de {{name}}",
    tokenOnce:
      "Copiez-le maintenant : vous ne le reverrez pas. Renouveler le jeton plus tard refusera celui-ci.",
    scimUrl: "URL de base SCIM",
    token: "Jeton",
    copy: "Copier",
    copied: "Copié",
    dismiss: "Je l'ai conservé",
    unmapped: "Codes d'organisation non associés",
    unmappedIntro:
      "Les personnes dont le code d'organisation n'est couvert par aucune association. Elles gardent leurs autres appartenances et ne reçoivent aucune entité de ce code tant qu'il n'est pas associé.",
    unmappedCaption: "Codes d'organisation non associés",
    noUnmapped: "Tous les codes d'organisation sont associés.",
    person: "Personne",
    code: "Code",
    source: "Source",
    since: "Depuis",
    mapTo: "Associer à",
    map: "Associer",
    mapping: "Association…",
    chooseEntity: "Entité pour {{code}}",
    mapped_one: "{{code}} associé. {{count}} personne a quitté la file.",
    mapped_other: "{{code}} associé. {{count}} personnes ont quitté la file.",
  },
  wiki: {
    docs: {
      noSources: "Aucun document source enregistré.",
      empty: {
        title: "Aucune page pour l'instant",
        body: "Les pages publiées que vous pouvez lire seront listées ici.",
      },
      pages: "Pages",
      details: "Détails de la page",
      about: "À propos de cette page",
      status: "État",
      version: "Version",
      trust: "Confiance",
      ready: "Prête",
      history: "Historique",
      allHistory: "Voir tout l’historique",
      contents: "Sur cette page",
      resize: "Redimensionner le panneau Pages",
      resizeValue: "{{width}} pixels de large",
      resizeHint:
        "Faites glisser ou utilisez les flèches pour redimensionner. Double-cliquez pour rétablir.",
      toolbar: "Barre du wiki",
      trustValues: {
        generated: "Généré",
        internal: "Interne",
        external: "Sources externes",
        mixed: "Sources mixtes",
      },
      writtenBy: "Rédigée par",
      written: {
        agent: "L’agent",
        agentRole: "L’agent ({{role}})",
        roles: { chat: "chat", reasoning: "raisonnement", fast: "rapide", vision: "vision" },
        answer: "Une réponse enregistrée",
        person: "Une personne",
        mcp: "Un client MCP",
        mcpClient: "Un client MCP ({{client}})",
        ingestion: "Résumé automatique",
        revert: "Une annulation",
      },
    },
    stewardship: {
      title: "Responsable et relecture",
      owner: "Responsable : {{name}}",
      noOwner: "Aucun responsable",
      someone: "Un ancien membre",
      verified: "Vérifiée par {{name}} le {{date}}",
      notVerified: "Non vérifiée",
      verify: "Vérifier cette version",
      withdraw: "Retirer la vérification",
      changeOwner: "Changer de responsable",
      membersFailed: "Les membres du projet n'ont pas pu être chargés. Réessayez.",
      stale: {
        label: "Peut-être obsolète",
        sourceChanged: "Une source a changé le {{date}}",
        lastVerified_one: "Dernière vérification il y a {{count}} jour",
        lastVerified_other: "Dernière vérification il y a {{count}} jours",
        published_one: "Publiée il y a {{count}} jour, jamais vérifiée depuis",
        published_other: "Publiée il y a {{count}} jours, jamais vérifiée depuis",
      },
      refused: {
        notFound:
          "Cette personne ne peut pas être responsable de la page : il faut un membre du projet qui peut la lire.",
        forbidden: "Votre rôle ne permet pas cette modification.",
        frozen: "Ce projet est gelé : son wiki ne peut pas changer.",
        newer: "Une version plus récente a été publiée. Ouvrez-la pour la vérifier.",
      },
      ownerDialog: {
        title: "Responsable de {{title}}",
        body: "Le responsable répond de la page. Cela ne crée aucune version et ne donne aucun accès.",
        field: "Responsable",
        nobody: "Aucun responsable",
        cancel: "Annuler",
        save: "Enregistrer",
        saving: "Enregistrement…",
      },
    },
    okfChecks: {
      warnings: "Avant de publier",
      errors: {
        title_empty: "La page doit avoir un titre.",
        title_too_long: "Le titre dépasse 200 caractères.",
        title_link_syntax: "Le titre ne peut pas contenir [[ ou ]].",
        title_line_break: "Le titre doit tenir sur une ligne.",
        front_matter:
          "La page ne peut pas commencer par un bloc de métadonnées (---). Supprimez-le ou ajoutez une ligne au-dessus.",
      },
      warning: {
        unresolved_link: "[[{{target}}]] n'ouvre aucune page que vous pouvez lire",
        heading_repeats_title: "Le premier titre répète le titre de la page",
        heading_level_skipped: "Un niveau de titre est sauté à « {{target}} »",
        summary_without_source: "Ce résumé n'a aucune source",
      },
    },
    folderIndex: {
      contents: "Contenu de {{folder}}",
      empty: {
        title: "Aucune page dans ce dossier",
        body: "Les pages publiées que vous pouvez lire dans ce dossier apparaîtront ici.",
      },
    },
    breadcrumb: {
      label: "Emplacement de la page",
      wiki: "Wiki",
      folder: "Pages de « {{folder}} »",
      empty: "Aucune page que vous pouvez lire ici",
    },
    neighbours: {
      label: "Pages de ce dossier",
      previous: "Précédente",
      next: "Suivante",
    },
    history: {
      passage: {
        title: "Historique de ce passage",
        action: "Historique",
        where: "Dans « {{section}} » · {{page}}",
        steps: "D’où vient ce passage",
        introduced: "Introduit dans la",
        writtenBy: "écrite par {{name}}",
        proposedBy: "Proposé par {{name}}",
        approvedBy: "approuvé par {{name}}",
        publishedBy: "publié par {{name}}",
        discussion_one: "{{count}} discussion sur ce passage pendant la revue",
        discussion_other: "{{count}} discussions sur ce passage pendant la revue",
        noThreads: "Personne n’a discuté ce passage pendant la revue.",
        open: "Toujours ouverte",
        onText: "Sur le texte d’alors :",
        none: "Aucun historique antérieur de ce passage ne vous est accessible.",
        member: "un membre",
      },
      review: {
        toggle: "Ce qui a changé depuis ma dernière revue",
        against: "Modifications par rapport au texte que vous avez revu",
        revised: "Révisée depuis votre revue",
        unchanged: "Inchangée depuis votre revue",
        new: "Nouvelle depuis votre revue",
        decision: {
          changes_requested: "vous avez demandé des modifications le {{at}}",
          approved: "vous l’avez approuvée le {{at}}",
          rejected: "vous l’avez rejetée le {{at}}",
        },
      },
      read: {
        line: "Cette page a changé depuis votre dernière lecture (version {{version}})",
        show: "Voir les modifications",
        hide: "Masquer les modifications",
        dismiss: "Masquer",
        unavailable: "Impossible de charger les modifications.",
      },
      current: "Actuelle",
      changes: "Modifications",
      changesOf: "Modifications de la version {{version}}",
      select: "Sélectionner la version {{version}} pour la comparer",
      compare: "Comparer",
      compareHint: "Sélectionnez deux versions pour les comparer.",
      restore: "Restaurer cette version",
      restoreVersion: "Restaurer la version {{version}}",
      actions: "Autres actions pour la version {{version}}",
      compareWithCurrent: "Comparer avec la version actuelle",
    },
    compare: {
      title: "Comparer des versions",
      range: "De la version {{from}} à la version {{to}}",
      titleChange: "Titre : {{from}} → {{to}}",
      typeChange: "Type : {{from}} → {{to}}",
      noChanges: "Ces deux versions ont le même contenu.",
      tooLarge:
        "Ces versions diffèrent trop pour être comparées ligne à ligne. Les deux sont affichées en entier.",
      notFound:
        "Ces versions ne peuvent pas être comparées avec vos accès, ou elles n'existent plus.",
      backToPage: "Retour à la page",
      openVersion: "Ouvrir la version {{version}}",
    },
    restore: {
      pageTitle: "Restaurer la version {{version}} de {{title}} ?",
      pageBody:
        "Cela crée un lot de modifications qui restaure la version {{version}}. La page ne change pas tant que le lot n'est pas relu et publié.",
      setTitle: "Annuler {{title}} ?",
      setBody_one:
        "Cela crée un lot de modifications qui restaure la page qu'il a modifiée. Rien ne change tant que le lot n'est pas relu et publié.",
      setBody_other:
        "Cela crée un lot de modifications qui restaure les {{count}} pages qu'il a modifiées. Rien ne change tant que le lot n'est pas relu et publié.",
      confirm: "Créer le lot de modifications",
      working: "Création…",
      cancel: "Annuler",
      revert: "Annuler ce lot de modifications",
      refused: {
        version: "Cette version ne peut pas être restaurée avec vos accès.",
        changeSet: "Ce lot de modifications ne peut pas être annulé avec vos accès.",
        current: "C'est déjà la version actuelle de la page.",
        frozen: "Ce projet est gelé. Rien ne peut être modifié tant qu'il n'est pas dégelé.",
        notPublished:
          "Ce lot de modifications n'est plus publié, ou la page a changé. Rechargez et réessayez.",
        forbidden: "Votre rôle ne permet pas de proposer des modifications du wiki dans ce projet.",
      },
    },
    // La carte Modifications du wiki et le panneau wiki de la conversation (wiki-collaboration).
    reading: {
      views: "Afficher la page",
      view: { marked: "Marqué", clean: "Net", source: "Source" },
      tooLarge:
        "Cette page est trop longue pour être marquée : elle s’ouvre sur ses lignes modifiées.",
      inserted: "ajouté",
      removed: "supprimé",
    },
    sizes: {
      created_one: "{{count}} nouvelle page",
      created_other: "{{count}} nouvelles pages",
      edits_one: "{{count}} modification",
      edits_other: "{{count}} modifications",
      renames_one: "{{count}} renommage",
      renames_other: "{{count}} renommages",
      moves_one: "{{count}} déplacement",
      moves_other: "{{count}} déplacements",
      deletions_one: "{{count}} suppression",
      deletions_other: "{{count}} suppressions",
      folders_one: "{{count}} dossier",
      folders_other: "{{count}} dossiers",
      lines: "+{{added}} −{{removed}}",
      newLines_one: "{{count}} ligne",
      newLines_other: "{{count}} lignes",
      none: "Aucune modification",
    },
    author: {
      title: "Brouillon du wiki",
      titleOnlyYou: "Brouillon du wiki · vous seul",
      kind: {
        created: "Nouvelle page",
        edits: "Modification",
        renames: "Renommage",
        moves: "Déplacement",
        deletions: "Suppression",
        folders: "Dossier",
      },
      structure: "Structure",
      more_one: "et {{count}} autre modification",
      more_other: "et {{count}} autres modifications",
      decides: {
        you: "Vous pouvez publier dans ce projet",
        requester: "Une personne autorisée à publier décide",
        peer: "Une autre personne que l’auteur décide",
        manager: "Un responsable ou le propriétaire décide",
        own_edits: "Quelqu’un d’autre doit décider : vous avez modifié une page",
        waiting: "En attente de votre réponse",
      },
      preview: "Aperçu",
      publish: "Publier",
      submit: "Soumettre à la relecture",
      submitInstead: "Soumettre à la relecture plutôt",
      approvePublish: "Approuver et publier",
      viewChanges: "Voir les modifications",
      updatedBelow: "Brouillon du wiki mis à jour plus bas",
      revised: "Révisé : les pages modifiées doivent être relues à nouveau",
      settled: {
        open: "Soumis à la relecture",
        changes_requested: "Modifications demandées",
        published: "Publié",
        discarded: "Abandonné",
      },
      previewTitle: "Aperçu · votre brouillon",
      openFull: "Ouvrir l’aperçu complet",
      onlyYou:
        "Vous seul voyez ce brouillon. Sélectionnez un passage pour donner une consigne à l’agent.",
      readOnly:
        "Soumis : vous le voyez comme les relecteurs. Sélectionnez un passage pour donner une consigne à l’agent.",
      changedPages: "Pages modifiées",
      publishSheet: {
        title_one: "Publier {{count}} modification dans le wiki ?",
        title_other: "Publier {{count}} modifications dans le wiki ?",
        explain: "Ce qui sera publié, et qui lira chaque page.",
        sameReaders: "Mêmes lecteurs",
        unchanged: "contenu inchangé",
        footnote:
          "Les lecteurs suivent les sources de chaque page. Ce projet vous permet de publier vos propres modifications ; c’est consigné dans l’Activité.",
        stale:
          "{{pages}} a changé entre-temps. Rien n’a été publié : ouvrez l’aperçu pour le mettre à jour.",
        declassifyHint:
          "Une page pourrait être publiée à plus de lecteurs que ses sources ne le permettent.",
        declassify: "Déclassifier dans la publication complète",
      },
      instruct: {
        action: "Donner une consigne à l’agent",
        hint: "Va à l’agent de cette conversation, pas à des personnes.",
        notSent: "pas encore envoyée",
        label: "Consigne pour l’agent",
        add: "Ajouter la consigne",
        cancel: "Annuler",
        tooLong: "1 000 caractères au plus.",
        full: "20 consignes au plus : envoyez d’abord celles-ci.",
      },
      tray: {
        title_one: "{{count}} consigne pour l’agent",
        title_other: "{{count}} consignes pour l’agent",
        onlyYou: "Vous seul les voyez",
        remove: "Retirer la consigne sur « {{quote}} »",
        clear: "Effacer",
        send: "Envoyer à l’agent",
        tooLong: "Trop long pour un seul message : retirez une consigne ou raccourcissez-en une.",
      },
      sent: {
        title_one: "Réviser le brouillon du wiki · {{count}} consigne",
        title_other: "Réviser le brouillon du wiki · {{count}} consignes",
        on: "Sur {{where}}",
      },
    },
    card: {
      title: "Modifications du wiki",
      untitled: "Lot sans titre",
      pages_one: "{{count}} page",
      pages_other: "{{count}} pages",
      folders_one: "{{count}} dossier",
      folders_other: "{{count}} dossiers",
      stale_one: "{{count}} obsolète",
      stale_other: "{{count}} obsolètes",
      workingNote: "Dans la file de relecture de personne d’autre avant votre soumission",
      review: "Relire",
      submit: "Soumettre",
      discard: "Abandonner",
      discardTitle: "Abandonner ces modifications du wiki ?",
      discardBody: "Le lot est fermé. Aucune page ne change.",
      settled: {
        open: "Soumis à la relecture",
        changes_requested: "Renvoyé pour modifications",
        published: "Publié",
        discarded: "Abandonné",
      },
      openReview: "Ouvrir la relecture",
      unavailable: "Ce lot de modifications ne vous est plus accessible.",
    },
    panel: {
      label: "Wiki : {{title}}",
      back: "Retour",
      close: "Fermer le panneau wiki",
      openInWiki: "Ouvrir la page complète",
      knowledge: "Connaissances",
      onThisPage: "Sur cette page",
      discussion_one: "{{count}} discussion ouverte · sur la page complète",
      discussion_other: "{{count}} discussions ouvertes · sur la page complète",
      discussionNone: "Discussion · sur la page complète",
      openReview: "Ouvrir la relecture",
      notFound: "Cette page n’existe pas ou vous ne pouvez pas la lire.",
      linkedFrom: "Pages qui y renvoient",
      history: "Versions",
      versionLine: "Version {{version}} · {{date}}",
      showCurrent: "Afficher la version actuelle",
      items: "Éléments",
    },
    changes: {
      notFound: "Cette modification ou cette page ne vous est pas accessible.",
      discardBody: "Cette modification passe dans Terminées. Aucune page ne change.",
      discardComment: "Commentaire (facultatif, affiché dans Terminées)",
      discardCommentDropped:
        "Abandonnée. Votre commentaire n’a pas été conservé : ce serveur n’accepte pas encore de commentaire à l’abandon.",
      discardedBy: "Abandonnée par {{name}} · {{at}}",
      discardedUnused: "Abandonnée sans usage · {{at}}",
      publishedPages: "Pages publiées",
      versionOf: "{{title}} · version {{version}}",
      edit: "Modifier la page",
      save: "Enregistrer",
      sources: "Sources",
      regenerate: "Régénérer",

      review: "Réviser",
      activity: "Activité",
      hiddenTitle: "Modification avec pages masquées",
      pages_one: "{{count}} page",
      pages_other: "{{count}} pages",
      hidden:
        "Certaines pages vous sont masquées. Une personne autorisée à les lire doit réviser cette modification avant publication.",
      hiddenActivity: "Certains détails de la modification vous sont masqués.",
      blockedStale: "Révisez ces pages en conflit avant de publier : {{pages}}",
      approveAll: "Tout approuver",
      approve: "Approuver",
      reject: "Rejeter",
      reopen: "Rouvrir",
      sendBack: "Renvoyer",
      comment: "Que faut-il modifier ?",
      changedPages: "Pages modifiées",
      allPages: "Toutes les pages",
      approvedBy: "Approuvé par {{name}}",
      rejectedBy: "Rejeté par {{name}}",
      publishedBy: "Publié par {{name}} · {{at}}",
      rebased: "Recalé depuis la version {{version}}. Révisez et approuvez à nouveau.",
      clearsApproval: "L’enregistrement annule l’approbation précédente.",
      reconcile: "Comparez cette proposition à la page actuelle avant d’enregistrer.",
      noSources: "Aucun document source. Les lecteurs sont vérifiés avant publication.",
      openPublished: "Ouvrir la version publiée",
      source: "Voir le Markdown",
      rendered: "Lire la page",
      root: "Racine du wiki",
      moveFrom: "Déplacer depuis {{folder}}.",
      moveTo: "Déplacer vers {{folder}}. Le contenu et la version restent identiques.",
      deleting: "Cette page sera supprimée. Ses versions antérieures sont conservées.",
      proposedTitle: "Titre proposé : {{title}}",
      before: "Version actuelle",
      after: "Version proposée",
      unsaved: "Abandonner les modifications non enregistrées ?",
      keepEditing: "Continuer à modifier",
      discardEdits: "Abandonner",
      publishExplain:
        "Vérifiez qui pourra lire chaque page avant de publier. Rien ne change avant votre confirmation.",
      includePending: "Inclure les pages en attente",
      approveFirst: "Approuvez une page ou incluez les pages en attente.",
      audienceUnchanged: "Cette opération conserve les lecteurs de la page.",
      noActivity: "Les modifications publiées et abandonnées apparaîtront ici.",
      older: "Voir les précédentes",
      // wiki-collaboration : le lot en cours, la soumission, les dossiers, la vérification.
      working:
        "En cours : l’agent peut encore modifier ce lot. Rien n’arrive aux autres relecteurs avant que vous le soumettiez.",
      workingOther: "En cours : son auteur ne l’a pas encore soumis à la relecture.",
      submit: "Soumettre à la relecture",
      submitted: "Soumis à la relecture.",
      submitExplain: "Ses pages sont vérifiées par rapport à leurs sources lors de la soumission.",
      publishNeedsRole:
        "Une personne autorisée à publier dans ce projet le relira une fois soumis.",
      mcpPublisher: {
        notYou:
          "Votre client MCP a proposé cette modification : une autre personne autorisée à publier dans ce projet la lit et la publie.",
        others:
          "Proposée via un client MCP : une personne autorisée à publier dans ce projet, autre que {{name}}, la publie.",
        anyone:
          "Proposée via un client MCP. Ce projet permet aussi à son auteur de la publier ; c\u{2019}est enregistré dans le journal d\u{2019}activité.",
      },
      notChecked: "Non vérifiée",
      notCheckedWorking: "Non vérifiée avant la soumission",
      notCheckedReason: {
        spend_limit: "Non vérifiée : le plafond de dépenses est atteint",
        check_failed: "Non vérifiée : la vérification a échoué",
        timed_out: "Non vérifiée : la vérification a expiré",
      },
      folderItem: "Dossier",
      folder: {
        folder_create: "Crée le dossier {{path}}. Un dossier existant du même nom est réutilisé.",
        folder_rename: "Renomme {{path}} en {{name}}.",
        folder_move: "Déplace {{path}} dans {{target}}.",
        folder_delete:
          "Supprime {{path}}. Ses pages et sous-dossiers remontent dans le dossier parent ; aucune page n’est supprimée.",
      },
      folderMissing: "Ce dossier n’existe plus. Rejetez cet élément pour publier le reste.",
      inFolder: "Dans {{path}}",
      // wiki-collaboration slice B : demandes de modification, niveaux de confiance.
      changesRequested: "Modifications demandées",
      approveBlocked: "Résolvez la demande de modification sur cette page avant de l’approuver.",
      publishBlocked: "Résolvez les demandes de modification ouvertes avant de publier.",
      skipped_one: "{{count}} page ignorée : modifications demandées",
      skipped_other: "{{count}} pages ignorées : modifications demandées",
      skippedOwn_one: "{{count}} page ignorée : vous l’avez modifiée",
      skippedOwn_other: "{{count}} pages ignorées : vous les avez modifiées",
      trustOwnEdits:
        "Vous avez modifié une page de cet ensemble : un autre contributeur l’approuve et publie l’ensemble.",
      trustRefused: {
        requester:
          "Vous ne pouvez pas approuver ni publier de modifications du wiki dans ce projet.",
        peer: "Ce projet demande qu’un autre contributeur approuve et publie ces modifications.",
        manager:
          "Ce projet demande qu’un responsable approuve et publie les modifications du wiki.",
      },
      state: {
        working: "En cours",
        open: "Ouverte",
        changes_requested: "Modifications demandées",
        published: "Publiée",
        discarded: "Abandonnée",
      },
      itemState: {
        pending: "En attente",
        approved: "Approuvée",
        rejected: "Rejetée",
        stale: "Obsolète",
        published: "Publiée",
      },
      op: {
        create: "Nouvelle page",
        edit: "Modification",
        rename: "Renommage",
        move: "Déplacement",
        delete: "Suppression",
        folder_create: "Nouveau dossier",
        folder_rename: "Renommage de dossier",
        folder_move: "Déplacement de dossier",
        folder_delete: "Suppression de dossier",
      },
      origin: {
        person: "Personne",
        agent: "Agent",
        answer: "Réponse enregistrée",
        mcp: "Client connecté",
        ingestion: "Synthèse de source",
        revert: "Rétablissement",
      },
      stale: {
        overlap: "La page a changé au même endroit. Modifiez ou régénérez la proposition.",
        too_large:
          "La modification est trop importante pour un recalage automatique. Comparez-la à la page actuelle.",
        deleted: "La page a été supprimée. Cette proposition ne peut pas être publiée.",
        missing: "La page est indisponible. Cette proposition ne peut pas être publiée.",
      },
    },
    threads: {
      item: "Discussion sur cette page",
      set: "Discussion sur l’ensemble des modifications",
      none: "Aucun commentaire pour l’instant.",
      kind: {
        comment: "Commentaire",
        question: "Question",
        change_request: "Modifications demandées",
      },
      kindLabel: "Type",
      kindOption: {
        comment: "Commentaire",
        question: "Question",
        change_request: "Demander des modifications",
      },
      forAgent: "Demander à l’agent de réviser",
      forAgentHint:
        "Son auteur reçoit une carte Réviser dans sa conversation ; rien ne s’exécute avant qu’il l’envoie.",
      body: "Commentaire",
      bodyItem: "Commenter cette page",
      bodySet: "Commenter l’ensemble de modifications",
      post: "Publier",
      reply: "Répondre",
      replyLabel: "Votre réponse",
      resolve: "Résoudre",
      reopen: "Rouvrir",
      resolvedBy: "Résolu par {{name}}",
      resolvedWithNote: "Résolu par {{name}} · {{note}}",
      note: {
        label: "Note de décision (facultative)",
        placeholder: "Accepté / refusé parce que …, concerne …",
        hint: "Une ligne, gardée avec la discussion pour tous ceux qui peuvent la lire.",
        confirm: "Résoudre",
        replyConfirm: "Répondre et résoudre",
        tooLong: "{{max}} caractères au plus.",
      },
      deleted: "Commentaire supprimé",
      deleteComment: "Supprimer le commentaire",
      deleteBody:
        "Son texte est retiré pour tout le monde. Une mention de sa suppression reste dans la discussion.",
      agentRevised: "Révisé par l’agent",
      askedAgent: "Révision demandée à l’agent",
      invalid: "Un commentaire contient de 1 à 2 000 caractères.",
      readOnly: "Cet ensemble de modifications est clos : sa discussion est en lecture seule.",
      someone: "Quelqu’un",
    },
    pending: {
      toggle: "Modifications en attente",
      shortcut: "Maj+P",
      banner_one: "{{count}} modification en attente touche cette page",
      banner_other: "{{count}} modifications en attente touchent cette page",
      view: "Voir",
      viewSet: "Voir « {{title}} »",
      section: "Une modification en attente touche cette section",
      viewUnnamed: "Voir la modification en attente",
      unnamed: "Un ensemble de modifications",
    },
    pageThreads: {
      title: "Discussion",
      open_one: "{{count}} ouverte",
      open_other: "{{count}} ouvertes",
      none: "Aucune discussion sur cette page pour l’instant.",
      comment: "Commenter la page",
      body: "Commentaire sur la page",
      bodyHeading: "Commentaire sur cette section",
      onPage: "Sur la page",
      onHeading: "Sur « {{heading}} »",
      earlier: "Sur une version antérieure",
      detached: "Détachée · sur la",
      onVersion: "version {{version}}",
      visibleTo_one: "Visible de la personne qui peut lire cette version",
      visibleTo_other: "Visible des {{count}} personnes qui peuvent lire cette version",
      visibleToReaders: "Visible des personnes qui peuvent lire cette version",
      whoResolves:
        "Son auteur, le responsable de la page ou une personne qui peut publier la résout.",
      paneLabel: "Discussion de la page",
      onPassage: "Sur un passage",
      ask: "Demander à l’agent",
      asked: "Demandé à l’agent · Ouvrir la conversation",
      commentOn: "Commenter « {{heading}} »",
      headingThreads_one: "{{count}} discussion ouverte sur « {{heading}} »",
      headingThreads_other: "{{count}} discussions ouvertes sur « {{heading}} »",
      dialogTitle: "Discussion sur « {{heading}} »",
      dialogDescription: "Visible des membres qui peuvent lire cette page. Texte brut uniquement.",
    },
    revision: {
      title: "Révision demandée",
      by: "{{name}} demande à l’agent de réviser « {{item}} »",
      revise: "Réviser",
      sending: "Envoi…",
      settled: "Révision de « {{item}} » envoyée à l’agent · demandée par {{name}}",
      openReview: "Ouvrir la relecture",
      someone: "Un relecteur",
      passage: "Sur le passage",
    },
    // Résoudre avec l’agent (wiki-resolve-with-agent D4, D6, D7, D10).
    resolve: {
      trigger: "Résoudre avec l’agent",
      triggerLabel_one: "Résoudre avec l’agent : {{count}} commentaire",
      triggerLabel_other: "Résoudre avec l’agent : {{count}} commentaires",
      triggerOf: "{{count}} sur {{of}}",
      triggerLabelOf: "Résoudre avec l’agent : {{count}} sur {{of}} commentaires, les plus anciens",
      triggerHint:
        "L’agent révise ce brouillon et rédige une réponse à chaque commentaire. C’est vous qui envoyez les réponses.",
      running: "L’agent traite les commentaires",
      sent_one: "Résolution de {{count}} commentaire sur « {{title}} » envoyée à l’agent",
      sent_other: "Résolution de {{count}} commentaires sur « {{title}} » envoyée à l’agent",
      sentUntitled_one: "Résolution de {{count}} commentaire envoyée à l’agent",
      sentUntitled_other: "Résolution de {{count}} commentaires envoyée à l’agent",
      revision: "Révision de l’agent {{revision}}",
      revisionPending: "Révision de l’agent",
      progress: "Avancement de la résolution",
      step: {
        read_one: "{{count}} commentaire lu",
        read_other: "{{count}} commentaires lus",
        revise: "Révision des pages",
        draft: "Rédaction des réponses",
        ready: "Prêt à relire",
      },
      live: {
        pages_one: "{{count}} page révisée",
        pages_other: "{{count}} pages révisées",
        replies_one: "{{count}} réponse rédigée",
        replies_other: "{{count}} réponses rédigées",
      },
      nothingSent: "Rien n’est envoyé aux relecteurs avant que vous n’envoyiez les réponses.",
      ready: "Réponses prêtes : {{counts}}",
      notSent_one: "{{count}} réponse rédigée, non envoyée",
      notSent_other: "{{count}} réponses rédigées, non envoyées",
      counts: {
        changed_one: "{{count}} modifiée",
        changed_other: "{{count}} modifiées",
        question_one: "{{count}} question",
        question_other: "{{count}} questions",
        noChange_one: "{{count}} sans changement",
        noChange_other: "{{count}} sans changement",
      },
      none: "aucune réponse rédigée",
      review: "Relire les réponses",
      sendAll: "Tout envoyer",
      settledSent: "réponses envoyées",
      settledUndone: "annulée",
      failed: "L’agent n’a pas pu terminer cette révision. Rien n’a été envoyé aux relecteurs.",
      unanswered_one: "{{count}} commentaire sans réponse de l’agent : répondez dans la relecture",
      unanswered_other:
        "{{count}} commentaires sans réponse de l’agent : répondez dans la relecture",
      panel: {
        hint: "Les réponses sont des brouillons que vous seul voyez. Modifiez, envoyez ou retirez chacune d’elles.",
        back: "Retour à l’aperçu",
        theme_one: "{{theme}} · {{count}} commentaire",
        theme_other: "{{theme}} · {{count}} commentaires",
        from: "{{name}} · {{kind}}",
        on: "sur « {{quote}} »",
        choices: "Choix : {{list}}",
        send: "Envoyer",
        edit: "Modifier",
        editLabel: "La réponse à {{name}}",
        choiceLabel: "Choix {{n}}",
        save: "Enregistrer",
        remove: "Retirer",
        sent: "Envoyée",
        removed: "Retirée",
        drafts_one: "{{count}} brouillon · chaque relecteur reçoit une seule notification",
        drafts_other: "{{count}} brouillons · chaque relecteur reçoit une seule notification",
        noDrafts: "Plus aucun brouillon à envoyer.",
        unanswered: "Sans réponse de l’agent",
        otherPages: "Autres pages modifiées par cette révision",
        unansweredItem: "{{page}} : répondez vous-même dans la relecture",
        theSet: "L’ensemble de modifications",
        undoPage: "Annuler les modifications de cette page",
        undone: "Modifications annulées",
        undoTitle: "Annuler les modifications de l’agent sur « {{title}} » ?",
        undoBody:
          "La page revient à son texte d’avant cette révision et ses réponses sont retirées. Les autres pages gardent la révision.",
        undoConfirm: "Annuler les modifications",
        unavailable: "Cette révision n’est plus disponible.",
      },
      reply: {
        label: "La réponse de l’agent",
        forName: "L’agent, pour {{name}}",
        forSomeone: "L’agent, pour l’auteur",
        draft: "Brouillon · vous seul le voyez",
        outcome: {
          changed: "Modifié",
          question: "Question pour {{name}}",
          questionYou: "Question pour vous",
          no_change: "Sans changement",
        },
        seeChange: "Voir la modification",
        choices: "Réponses à choisir",
        answer: "Répondre…",
        waiting: "En attente de {{name}}",
        waitingYou: "En attente de votre réponse · {{name}} relance l’agent avec elle",
        hidden: "L’agent a répondu à partir de sources que vous ne pouvez pas lire.",
        hiddenAsk:
          "L’agent a répondu à partir de sources que vous ne pouvez pas lire : demandez à {{name}}.",
        someone: "le relecteur",
      },
      bar: {
        sent: "Révision de l’agent {{revision}} · {{counts}}",
        drafted: "Révision de l’agent {{revision}} · réponses pas encore envoyées",
        menu: "Autres actions de relecture",
        resolveAnswered: "Résoudre les fils répondus ({{count}})",
        resolveAnsweredHint:
          "Réponses « Modifié » et « Sans changement ». Les questions restent ouvertes.",
        resolveTitle_one: "Résoudre {{count}} fil répondu",
        resolveTitle_other: "Résoudre {{count}} fils répondus",
        resolveBody:
          "Chaque fil est résolu en votre nom avec la même note. Les fils avec une question restent ouverts.",
        resolved_one: "{{count}} fil résolu",
        resolved_other: "{{count}} fils résolus",
      },
    },
    title: "Wiki",
    openWiki: "Ouvrir le wiki",
    backToProject: "Retour au projet",
    tabs: { pages: "Pages", review: "À relire", declassifications: "Déclassifications" },
    newPage: "Nouvelle page",
    noPages:
      "Aucune page lisible pour l'instant. Enregistrez une réponse dans le wiki, ou rédigez une page.",
    empty: {
      title: "Les connaissances du projet, mises par écrit",
      body: "Le wiki rassemble ce que ce projet sait : des pages relues qui citent leurs sources. Rédigez la première page, ou lancez une conversation et enregistrez une bonne réponse dans le wiki.",
      bodyRead:
        "Le wiki rassemble ce que ce projet sait : des pages relues qui citent leurs sources. Ses pages seront listées ici une fois publiées.",
      startConversation: "Lancer une conversation",
      sources: "Parcourir les sources",
      waiting_one: "{{count}} brouillon attend une relecture",
      waiting_other: "{{count}} brouillons attendent une relecture",
    },
    overview: {
      title: "Vue d’ensemble du wiki",
      pages_one: "{{count}} page",
      pages_other: "{{count}} pages",
      recent: "Mises à jour récentes",
      updated: "Mise à jour le {{date}}",
      review: "En attente de relecture",
      waiting_one: "{{count}} modification attend une relecture",
      waiting_other: "{{count}} modifications attendent une relecture",
      nothingWaiting: "Rien à relire pour vous",
      status: { uploading: "Envoi en cours", processing: "En traitement", failed: "Échec" },
    },
    needsChecking: {
      title: "À vérifier",
      filter: "Pages à vérifier",
      clear: "Afficher toutes les pages",
      none: "Aucune page à vérifier.",
      reason: {
        source_changed: "Une source a changé",
        review_due: "Relecture à faire",
        unverified: "Modifiée depuis sa vérification",
      },
    },
    pages: "Pages",
    log: "Publications récentes",
    version: "Version {{version}}",
    versionBy: "Version {{version}} par {{author}}, {{at}}",
    versionAt: "Version {{version}}, {{at}}",
    newerVersion: "Une version plus récente de cette page existe.",
    openCurrent: "Ouvrir la version actuelle",
    processing: "En cours d'indexation : elle pourra être citée une fois prête.",
    declassified: "Contient du contenu déclassifié",
    declassifiedExplain:
      "Un responsable de projet ou le propriétaire a publié cette version pour des lecteurs qui ne peuvent pas lire toutes ses sources.",
    backlinks: "Pages qui y renvoient",
    noBacklinks: "Aucune page lisible ne renvoie ici.",
    provenance: "Sources (propriétaire et responsables)",
    unreadableSource: "Une source que vous ne pouvez pas lire",
    sourceDeclassified: "déclassifiée",
    edit: "Proposer une modification",
    delete: "Supprimer la page",
    editor: {
      title: "Titre",
      pageType: "Type de page",
      markdown: "Contenu (Markdown)",
      preview: "Aperçu",
      saving: "Enregistrement…",
      cancel: "Annuler",
    },
    pageEditor: {
      new: "Nouvelle page",
      newHint:
        "Vous proposez une nouvelle page. Elle part en revue : rien n'apparaît dans Pages avant sa publication.",
      change: "Proposer une modification de « {{title}} »",
      changeHint:
        "Votre modification porte sur la version {{version}}. Elle part en revue : la page reste inchangée jusqu'à sa publication.",
      why: "Pourquoi (facultatif)",
      whyHint: "Affiché aux relecteurs avec la proposition.",
      previewEmpty: "L'aperçu s'affiche à mesure que vous écrivez.",
      save: "Enregistrer pour revue",
      publish: "Publier",
      noAccess: "Vous pouvez lire ce wiki mais pas y proposer de pages.",
      frozen: "Ce projet est gelé : son wiki ne peut pas changer.",
    },
    pageTypes: { summary: "Synthèse", concept: "Concept", entity: "Entité", other: "Page" },
    draft: {
      provenance: {
        someone: "quelqu'un",
        untitled: "une conversation sans titre",
      },
      sourceDeleted: "Source supprimée",
      changesAgainst: "Modifications par rapport à la version {{version}}",
      added: "ajouté",
      removed: "supprimé",
      unchanged_one: "{{count}} ligne inchangée",
      unchanged_other: "{{count}} lignes inchangées",
      tooLarge:
        "Cette page est trop longue pour être comparée ligne à ligne. Le brouillon est affiché tel qu'il serait publié.",
      noChanges: "Aucune modification par rapport à la version actuelle.",
      publish: "Publier",
    },
    inbox: {
      title: "Modifications",
      views: "Vues des modifications",
      view: {
        toDecide: "À décider",
        mine: "Les miennes",
        following: "Suivies",
        closed: "Terminées",
      },
      countSr_one: "{{count}} en attente",
      countSr_other: "{{count}} en attente",
      loading: "Chargement des modifications…",
      alerts: "Alertes sur les sources",
      someone: "Quelqu’un",
      who: {
        agent: "Agent pour {{name}}",
        answer: "Réponse enregistrée · {{name}}",
        mcp: "{{name}} via un client connecté",
        revert: "Annulation par {{name}}",
        ingestion: "Synthèse de source",
      },
      when: {
        submitted: "soumise {{ago}}",
        started: "commencée {{ago}}",
        sentBack: "modifications demandées {{ago}}",
        sentBackBy: "modifications demandées par {{name}} {{ago}}",
      },
      state: {
        working: "Brouillon · non soumis",
        submitted: "Soumise",
      },
      why: {
        requester: "Vous pouvez publier dans ce projet",
        peer: "Une autre personne que l’auteur doit décider",
        manager: "Un responsable ou le propriétaire décide",
        own_edits: "Quelqu’un d’autre doit décider : vous avez modifié une page",
        waiting: "En attente de votre réponse",
        publisher: "Une personne autorisée à publier décide",
        author: "En attente de son auteur",
      },
      risk: {
        changeRequests_one: "{{count}} demande de modification ouverte",
        changeRequests_other: "{{count}} demandes de modification ouvertes",
        stale_one: "{{count}} page a changé depuis la proposition",
        stale_other: "{{count}} pages ont changé depuis la proposition",
        hidden: "Certaines pages vous sont masquées",
      },
      search: "Rechercher dans les modifications terminées",
      closed: {
        published: "Publiée par {{name}} · {{at}}",
        discarded: "Abandonnée par {{name}} · {{at}}",
        swept: "Abandonnée sans usage · {{at}}",
        hidden_one: "+{{count}} modification que vous ne voyez pas",
        hidden_other: "+{{count}} modifications que vous ne voyez pas",
        comment: "Commentaire :",
      },
      empty: {
        toDecide: {
          title: "Rien à décider",
          body: "Les ensembles de modifications que vous pouvez approuver et publier apparaissent ici dès qu’ils sont soumis.",
        },
        mine: {
          title: "Aucune modification de votre part",
          body: "Les brouillons que vous ou votre agent commencez, et ceux que vous avez soumis, apparaissent ici jusqu’à la décision.",
        },
        following: {
          title: "Rien à suivre",
          body: "Les ensembles ouverts dont quelqu’un d’autre décide, ou que vous avez commentés, apparaissent ici.",
        },
        closed: {
          title: "Aucune modification terminée",
          body: "Les ensembles publiés et abandonnés apparaissent ici, du plus récent au plus ancien.",
          search: "Aucune modification terminée ne correspond à « {{query}} ».",
          recent: "Aucune modification terminée récente que vous puissiez voir.",
        },
      },
    },
    review: {
      waitingChangeRequests_one: "{{count}} demande de modification pour vous",
      waitingChangeRequests_other: "{{count}} demandes de modification pour vous",
      waitingQuestions_one: "{{count}} question pour vous",
      waitingQuestions_other: "{{count}} questions pour vous",
      contradictions_one: "{{count}} contradiction possible",
      contradictions_other: "{{count}} contradictions possibles",
      stale:
        "La page a changé depuis cette proposition : ouvrez-la pour la modifier ou la régénérer.",
      review: "Relire",
      discard: "Abandonner",
      resolve: "Marquer comme relu",
      back: "Modifications",
      summaryMenu: "Autres actions sur cet ensemble de modifications",
      requestedBy: "Modifications demandées par {{name}}",
      requestBody:
        "L’ensemble retourne à son auteur avec votre commentaire. Il ne peut pas être publié tant que la demande n’est pas résolue.",
      requestLabel: "Que faut-il changer ?",
      structureNote:
        "Pages déplacées et dossiers créés ou modifiés. Le contenu et les versions sont inchangés.",
      outline: {
        content: "Contenu",
        structure: "Structure",
        removals: "Suppressions",
        tree: "Dossiers et déplacements",
        flags_one: "{{count}} contradiction possible",
        flags_other: "{{count}} contradictions possibles",
        menu: "Décider de « {{title}} »",
        open_one: "{{count}} modification",
        open_other: "{{count}} modifications",
      },
      margin: {
        flag: "Contradiction possible",
        flagHint: "Approuver cette page la prend en compte.",
      },
      passage: {
        label: "Commenter le passage sélectionné",
        action: {
          comment: "Commenter",
          question: "Question",
          change_request: "Demander une modification",
        },
        comment: "Commenter ce passage",
        question: "Poser une question sur ce passage",
        change_request: "Demander une modification de ce passage",
        askAgent: "et demander à l’agent de réviser",
        visible: "Visible des personnes qui peuvent lire cette page dans la relecture.",
      },
      onPage: "Sur la page :",
      line: {
        contradiction: "Contradiction possible",
        request: "Modifications demandées",
        source: "avec {{title}}",
        who_one: "{{name}} · {{count}} réponse",
        who_other: "{{name}} · {{count}} réponses",
        open: "Ouvrir",
        opened: "Ouvert",
      },
      marker: {
        flag: "Contradiction possible : l’ouvrir",
        thread_one: "{{kind}} de {{name}}, {{count}} commentaire",
        thread_other: "{{kind}} de {{name}}, {{count}} commentaires",
      },
      stepper: {
        label: "Points ouverts sur cette page",
        previous: "Point ouvert précédent",
        next: "Point ouvert suivant",
        of: "{{index}} sur {{count}} ouverts",
        open_one: "{{count}} ouvert",
        open_other: "{{count}} ouverts",
      },
      dock: { bottom: "Ancrer en bas", side: "Ancrer à côté" },
      pane: {
        label: "Point de relecture",
        close: "Fermer le point",
        on: "Sur",
        onPage: "Sur la page",
        jump: "Aller au passage",
        replies_one: "{{count}} réponse",
        replies_other: "{{count}} réponses",
        whoResolves:
          "Son auteur, ou une personne qui peut décider de cet ensemble de modifications, le résout.",
        replyPlaceholder: "Répondre…",
        visible: "Toutes les personnes qui voient cet ensemble de modifications",
        replyResolve: "Répondre et résoudre",
        pageSays: "La page dit",
        sourceSays: "La source dit",
        sourceSaysNamed: "La source dit · {{title}}",
        openSource: "Ouvrir la source",
        acknowledges:
          "Approuver la page prend acte de cette contradiction, et le journal d’audit l’enregistre.",
        commentOn: "La commenter",
        askAgent: "Demander à l’agent de corriger",
        commentLabel: "Commenter ce passage",
        agentLabel: "Que doit changer l’agent ?",
      },
      sheet: { expand: "Afficher le point", collapse: "Masquer le point" },
      strip: {
        views: "Vues de la relecture",
        changes: "Modifications",
        discussion: "Discussion",
        inline: "Afficher les points dans le texte",
        viewMenu: "Options d’affichage",
        page: "Modification {{index}} sur {{count}} : {{title}}",
        position: "{{index}} sur {{count}}",
        new: "Nouvelle",
        removed: "Supprimée",
      },
      discussion: {
        onPages: "Sur les pages",
        requests_one: "{{count}} demande de modifications",
        requests_other: "{{count}} demandes de modifications",
        questions_one: "{{count}} question",
        questions_other: "{{count}} questions",
        comments_one: "{{count}} commentaire",
        comments_other: "{{count}} commentaires",
      },
      bar: {
        label: "Décision",
        request: "Demander des modifications",
        approvePublish: "Approuver et publier",
        approve: "Approuver",
        stale_one: "{{count}} page a changé depuis cette proposition",
        stale_other: "{{count}} pages ont changé depuis cette proposition",
        setRequests_one: "{{count}} demande de modifications sur tout l’ensemble (Discussion)",
        setRequests_other: "{{count}} demandes de modifications sur tout l’ensemble (Discussion)",
        acknowledges_one: "Approuver prend acte de {{count}} contradiction possible",
        acknowledges_other: "Approuver prend acte de {{count}} contradictions possibles",
      },
      kind: {
        source_changed: "Source modifiée, la page est peut-être obsolète",
        contradiction: "Contradiction possible avec une source",
        redeclassify: "À relire : une source déclassifiée a changé",
      },
    },
    publish: {
      title: "Publier dans le wiki",
      titleOf: "Publier « {{title}} »",
      newPage: "Crée une nouvelle page.",
      newVersion: "Publie une nouvelle version de « {{title}} ».",
      readers_one: "{{count}} personne pourra la lire",
      readers_other: "{{count}} personnes pourront la lire",
      readersExplain:
        "Seules les personnes qui peuvent lire toutes les sources de cette page. Si une source est restreinte plus tard, la page l'est aussi.",
      classification: "Classification",
      widen: "Publier pour plus de lecteurs (déclassifier)",
      widenExplain:
        "La page atteindrait tout le projet ({{count}} personnes). Ces sources limitent qui peut la lire :",
      cannotRead_one: "{{count}} lecteur prévu ne peut pas lire cette source",
      cannotRead_other: "{{count}} lecteurs prévus ne peuvent pas lire cette source",
      reason: "Motif (consigné dans le journal d'audit)",
      irreversible:
        "Quiconque lit la page avant que vous retiriez la déclassification garde ce qu'il a lu. Le retrait n'empêche que les lectures suivantes.",
      unavailable: {
        not_admin:
          "Seul un responsable de projet ou le propriétaire peut publier pour plus de lecteurs.",
        forbidden_by_setting: "La déclassification est désactivée pour ce projet.",
        break_glass: "L'accès d'urgence ne permet pas de déclassifier.",
        cannot_read_sources:
          "Vous ne pouvez pas déclassifier un contenu dont vous ne pouvez pas lire toutes les sources.",
      },
      submit: "Publier",
      submitWiden: "Déclassifier et publier",
      publishing: "Publication…",
      cancel: "Annuler",
      published: "Publiée.",
      done: "Terminé",
    },
    declassifications: {
      empty: "Aucune déclassification dans ce projet.",
      withdraw: "Retirer",
      withdrawn: "Retirée le {{at}}",
      unreadable: "Contenu que vous ne pouvez pas lire",
      row: "par {{who}}, {{at}}",
      someone: "quelqu'un",
    },
    export: "Exporter au format OKF",
    exporting: "Préparation de l'export…",
    exportReady: "Télécharger l'export",
    exportFailed: "L'export a échoué.",
    saveAsFileInstead: "Enregistrer plutôt comme fichier",
    saveReviewNote:
      "Crée un brouillon dans la file de relecture. Rien n'est publié avant qu'une personne le relise.",
    saveToWiki: "Enregistrer dans le wiki",
    savedToWiki: "Brouillon ajouté à la file de relecture du wiki.",
    reviewDraft: "Relire le brouillon",
    draftTitle: "Titre du brouillon",
    draftTitlePlaceholder: "Nommez ce brouillon",
    saveWeb: {
      title: "Enregistrer cette réponse dans le wiki ?",
      body: "Une partie de cette réponse provient de pages web publiques. Le brouillon est marqué comme externe : vérifiez-le avant de le publier.",
      confirm: "Enregistrer dans le wiki",
      cancel: "Annuler",
    },
    reviewCount_one: "{{count}} élément à relire",
    reviewCount_other: "{{count}} éléments à relire",
  },
  recommendations: {
    why: "Pourquoi ?",
    whyLabel: "Pourquoi ? (facultatif)",
    reasons: { not_useful: "Pas utile", wrong: "Inexact", duplicate: "Doublon" },
    reasonNoted: "Raison notée.",
    itemRejected: "Page refusée.",
  },
  wikiSuggestion: {
    suggested: "Suggestion pour le wiki",
    proposed_one: "Page wiki proposée",
    proposed_other: "Pages wiki proposées",
    changeTo: "Modification de {{page}}",
    reviewNote: "Passe en relecture seulement si vous en faites un brouillon",
    sources_one: "{{count}} source",
    sources_other: "{{count}} sources",
    external: "Contient du contenu de pages web ; le brouillon est marqué externe",
    showPage: "Afficher la page proposée",
    hidePage: "Masquer la page proposée",
    draft: "En faire un brouillon",
    drafting: "Rédaction…",
    dismiss: "Ignorer",
    dismissed: "Suggestion ignorée.",
    drafted: "Brouillon ajouté à la file de relecture.",
    gone: "Cette suggestion n'est plus disponible.",
    disabled: "Les suggestions wiki sont désactivées pour ce projet.",
    frozen: "Ce projet est gelé : aucun brouillon ne peut être ajouté.",
    role: "Vous ne pouvez plus rédiger de pages wiki dans ce projet.",
    changed: "Le wiki a changé pendant la rédaction. Réessayez.",
    loadingPage: "Chargement de la page proposée…",
  },
  wideBlock: {
    expand: "Agrandir",
    expandNamed: "Agrandir {{name}}",
    expandTable: "Agrandir le tableau",
    expandCode: "Agrandir le code",
    table: "Tableau",
    code: "Code",
    close: "Fermer",
    description: "Affiché en plein écran. Échap le ferme.",
    zoom: "Zoom",
    zoomIn: "Zoom avant",
    zoomOut: "Zoom arrière",
    level: "Niveau de zoom",
    fit: "Ajuster",
    actualSize: "Taille réelle",
    size: "Taille du dessin",
    panArea:
      "{{name}} : faites glisser ou utilisez les flèches pour vous déplacer, + et − pour zoomer",
    tooSmall: "Trop petit pour être lu : Taille réelle ou Agrandir",
    diagram: "Diagramme",
    expandDiagram: "Agrandir le diagramme",
    diagramTooLarge:
      "Ce diagramme est trop grand pour être tracé ici. Sa source est affichée à la place.",
    diagramTooMany:
      "Seuls les {{count}} premiers diagrammes d'une page sont tracés. Sa source est affichée à la place.",
  },
  charts: {
    showTable: "Afficher en tableau",
    showChart: "Afficher en graphique",
    total: "Total",
    share: "Part",
  },
  reports: {
    title: "Rapports",
    hint: "Usage, qualité des réponses, coûts et capacité, santé et sécurité.",
    lead: "Des chiffres uniquement : aucun message, document ni personne n’apparaît ici.",
    tabs: {
      usage: "Usage",
      quality: "Qualité des réponses",
      cost: "Coûts et capacité",
      health: "Santé et sécurité",
    },
    filters: {
      period: "Période",
      last7: "7 derniers jours",
      last30: "30 derniers jours",
      last90: "90 derniers jours",
      last13Months: "13 derniers mois",
      custom: "Personnalisée",
      from: "Du",
      to: "Au",
      entity: "Entité",
      allEntities: "Tout votre périmètre",
      apply: "Appliquer",
    },
    updated: "Mis à jour le {{at}}",
    stale:
      "Ces chiffres datent du {{at}}. Ils sont recalculés toutes les heures ; l’activité plus récente n’est pas encore comptée.",
    neverComputed:
      "Ces chiffres n’ont pas encore été calculés. Ils sont recalculés toutes les heures.",
    synthetic: "Inclut des données de démonstration synthétiques",
    syntheticBody:
      "Une partie des chiffres de cette période a été générée pour une démonstration. Ils s’ajoutent à l’activité réelle et ne correspondent à aucun usage réel.",
    entityReset: "Cette entité est hors de votre périmètre. Affichage de tout votre périmètre.",
    empty: "Aucune activité sur cette période.",
    day: "Jour",
    entity: "Entité",
    project: "Projet",
    personalProject: "Projets personnels",
    standaloneLine: "Conversations hors projet",
    unattributedNote:
      "Les répartitions par entité et par projet excluent l’activité qui n’appartient à aucun des deux.",
    usage: {
      questions: "Questions",
      conversations: "Conversations commencées",
      activeUsers: "Utilisateurs actifs",
      activeUsersIn: "Utilisateurs actifs en {{month}}",
      dailyTitle: "Questions et utilisateurs actifs par jour",
      dailySummary: "{{questions}} questions sur {{days}} jours, pic le {{peak}}.",
      dailySummaryEmpty: "Aucune question sur {{days}} jours.",
      entitiesTitle: "Questions par entité",
      entitiesSummary: "{{top}} a posé le plus de questions ({{count}}).",
      topProjects: "Projets les plus sollicités",
      topProjectsSummary: "{{top}} arrive en tête avec {{count}} questions.",
    },
    recommendations: {
      title: "Recommandations",
      lead: "Ce que les personnes ont décidé des suggestions de l'assistant. Des comptes uniquement : aucun contenu, aucun nom.",
      kind: "Recommandation",
      kinds: {
        memory_proposal: "Propositions de mémoire",
        wiki_suggestion: "Suggestions de wiki",
        wiki_change_item: "Pages du wiki écrites par l'agent",
        wiki_change_set: "Lots de modifications de l'agent",
        feedback_draft: "Brouillons de retours",
        wiki_thread_reply: "Réponses de l’agent aux fils de relecture",
      },
      origins: { requested: "demandés", noticed: "spontanés" },
      dismissal: "Refus",
      shown: "Présentées",
      accepted: "Acceptées",
      edited: "Modifiées",
      dismissed: "Écartées",
      ignored: "Ignorées",
      acceptance: "Acceptation",
      noRate: "—",
      noRateLabel: "Moins de 10 décisions",
      version: "{{role}} · {{model}} · consigne {{version}}",
      unknownModel: "Modèle inconnu",
      unknownVersion: "inconnue",
      unknownRole: "Rôle inconnu",
      reasonsLine: "Raisons : {{reasons}}",
      noReasons: "Aucune raison donnée",
      reasonLabel: {
        not_useful: "Pas utile",
        wrong: "Inexact",
        duplicate: "Doublon",
        expired: "Expirée",
        conversation_deleted: "Conversation supprimée",
        set_closed: "Laissée à la publication",
      },
      note: "L'acceptation compte les décisions acceptées et modifiées, affichée à partir de 10 décisions. Les chiffres de mémoire personnelle d'un projet ou d'une entité de moins de cinq utilisateurs actifs ne comptent que dans le total de l'organisation.",
      empty: "Aucune recommandation n'a été décidée sur cette période.",
      export: "Exporter en JSON",
      exporting: "Export…",
      exportFailed: "L'export n'a pas pu être téléchargé. Réessayez.",
    },
    quality: {
      supported: "Étayées",
      unverified: "Non vérifiées",
      unsupported: "Non étayées",
      citations: "Citations",
      status: "Statut de la citation",
      sharesTitle: "Citations par statut",
      sharesSummary: "{{supported}} citations sur {{total}} étaient étayées par leur source.",
      sharesEmpty: "Aucune citation sur cette période.",
      answers: "Réponses",
      withoutSource: "Réponses sans source",
      withoutSourceHint: "{{share}} des réponses",
      ocrFlagged: "Citations de scans douteux",
      ocrFlaggedHint: "Passages signalés par le contrôle de transcription",
      projectsTitle: "Qualité des réponses par projet",
    },
    cost: {
      total: "Coût estimé des modèles",
      unknown_one: "{{count}} appel de coût inconnu, non inclus",
      unknown_other: "{{count}} appels de coût inconnu, non inclus",
      byRoleTitle: "Coût estimé par jour, par rôle de modèle",
      byRoleSummary: "{{total}} sur la période ; {{top}} coûte le plus.",
      roles: "Rôles de modèle",
      role: "Rôle",
      inputTokens: "Jetons en entrée",
      outputTokens: "Jetons en sortie",
      calls: "Appels",
      cost: "Coût estimé",
      roleLabel: {
        chat: "Conversation",
        reasoning: "Raisonnement",
        fast: "Rapide",
        vision: "Vision (scans et images)",
        embedding: "Indexation pour la recherche",
      },
      capacity: "Capacité",
      ocrPages: "Pages scannées lues ce mois-ci",
      storage: "Documents stockés",
      seats: "Utilisateurs actifs ce mois-ci",
      noLimit: "Aucune limite définie",
      modelSpend: "Dépense IA estimée ce mois-ci",
      spendWarning: "La dépense IA atteint 80 % ou plus de la limite de ce mois-ci.",
      spendExceeded:
        "La dépense IA dépasse la limite de ce mois-ci. Les questions continuent de fonctionner : la limite ne fait qu'avertir.",
      spendReachedHard:
        "La dépense IA a atteint la limite de ce mois-ci : les nouvelles questions sont refusées jusqu'au mois prochain ou jusqu'au relèvement de la limite.",
      projectsTitle: "Coût estimé par projet",
      storedInScope: "Documents stockés dans le périmètre : {{size}}",
      toolsTitle: "Outils externes (hors coût des modèles)",
      tool: "Outil",
      toolLabel: { webSearch: "Recherche web", codeExecution: "Exécution de code" },
      originsTitle: "Coût IA estimé par origine",
      origin: "Origine",
      originLabel: {
        api: "Application ESI",
        worker: "Traitement en arrière-plan",
        mcp: "MCP",
        other: "Autre",
      },
    },
    health: {
      ingestion: "Documents",
      ingestionFailures: "Documents en échec",
      failuresTitle: "Documents en échec par motif",
      failuresSummary: "{{count}} documents en échec ; motif le plus fréquent : {{top}}.",
      reason: "Motif",
      otherReason: "Autre motif",
      uploading: "Envoi incomplet",
      processing: "En attente de traitement",
      models: "Appels aux modèles",
      modelFailures: "En échec",
      modelUnavailable: "Aucun modèle disponible",
      modelFallbacks: "Servis par un modèle de repli",
      access: "Accès",
      notFound: "Demandes de contenu refusées",
      downloadDenied: "Téléchargements refusés",
      flowDenied: "Refus du contrôle de flux",
      toolCallsBlocked: "Appels d’outils bloqués",
      toolCallsDeclined: "Appels d’outils refusés",
      governance: "Accès exceptionnels",
      breakGlass: "Accès bris de glace",
      declassificationsCreated: "Déclassifications",
      declassificationsWithdrawn: "Déclassifications retirées",
      seeEvents: "Voir les événements",
      projectsTitle: "Santé et sécurité par projet",
    },
  },
  governance: {
    memories: {
      title: "Mémoire du projet",
      lead: "Les faits validés par l'équipe du projet. Vous ne voyez le texte que des souvenirs dont vous pouvez lire les sources.",
      empty: "Ce projet n'a aucun souvenir validé.",
      hidden: "Masqué : s'appuie sur des sources que vous ne pouvez pas lire",
      sources_one: "{{count}} source",
      sources_other: "{{count}} sources",
      unverified: "Non vérifié",
      lastUsed: "Utilisé le {{date}}",
      neverUsed: "Pas encore utilisé",
    },
    pending: {
      title: "En attente de l’accord du propriétaire",
      sent: "Envoyé au propriétaire du projet pour accord. Rien ne change tant que le propriétaire ou un gestionnaire du projet n’a pas approuvé.",
      detail:
        "{{change}}. Le propriétaire ou un gestionnaire du projet décide ; la demande expire le {{date}}.",
      group_one: "Elle attend aussi {{count}} autre projet.",
      group_other: "Elle attend aussi {{count}} autres projets.",
      reasonLabel: "Motif pour le propriétaire (facultatif)",
      reasonSave: "Envoyer le motif",
      reasonSaved: "Motif envoyé au propriétaire.",
      breakGlass: "Utiliser plutôt l’accès bris de glace",
      breakGlassHelp:
        "L’accès bris de glace vous permet de lire le projet dès maintenant, en Lecteur. Il est audité, et le propriétaire et les gestionnaires du projet le voient avec votre motif.",
      breakGlassTitle: "Accès bris de glace à {{name}}",
      breakGlassReason: "Motif (obligatoire)",
      breakGlassConfirm: "Ouvrir en Lecteur",
      inList: "En attente de l’accord du propriétaire",
    },
    mine: {
      title: "Vos demandes d’accès",
      lead: "Les changements qui attendent l’accord d’un projet, et leur issue sur les 30 derniers jours.",
      personal: "Un projet personnel",
      requested: "Demandé le {{date}}",
      decided: "Décidé le {{date}}",
      status: {
        pending: "En attente",
        approved: "Approuvée",
        rejected: "Refusée",
        expired: "Expirée",
        cancelled: "Annulée",
      },
    },
    change: {
      visibility: "Visibilité : {{visibility}}",
      details: "Nom et description",
      memberAdd: "Ajouter {{name}} en {{role}}",
      groupAdd: "Donner le rôle {{role}} au groupe {{name}}",
      groupRole: "Passer le groupe {{name}} en {{role}}",
      ruleGrant: "Donner le rôle {{role}} à la règle {{name}}",
      setting: "Modifier le réglage {{key}}",
      transfer: "Transférer à {{name}}",
      entityJoin: "Rejoindre l’entité {{name}}",
      entityMove: "Accès en {{role}}, ouvert par le déplacement de l’entité {{name}}",
      directorySync: "Accès en {{role}}, ouvert par la synchronisation de l’annuaire",
      shareOutside: "Autoriser des membres hors de l’entité propriétaire ({{scope}})",
      shareOutsideReset: "Rétablir la règle héritée sur les membres hors entité ({{scope}})",
      settingReset: "Réinitialiser le réglage {{key}} ({{scope}})",
      wholeTenant: "toute l’organisation",
      unknown: "un élément supprimé",
    },
    title: "Projets",
    hint: "Tous les projets que vous gouvernez, avec leurs 30 derniers jours.",
    lead: "Métadonnées et chiffres uniquement. Gouverner un projet ne vous en rend jamais lecteur.",
    name: "Projet",
    owner: "Propriétaire",
    personal: "Projet personnel",
    personalOwner: "Une personne",
    members: "Membres",
    documents: "Documents",
    flowDenials: "Refus du contrôle de flux",
    questions: "Questions",
    activeUsers: "Utilisateurs actifs",
    cost: "Coût estimé",
    last30: "30 derniers jours",
    frozen: "Gelé",
    empty: "Aucun projet à gouverner.",
    under: "Détenus sous",
    lastActivity: "Dernière activité",
    open: "Ouvrir {{name}}",
    back: "Projets",
    loadFailed: "Ce projet n’a pas pu être chargé.",
    personalDetail:
      "Les membres et réglages d’un projet personnel restent l’affaire de son propriétaire.",
    metadataOnly:
      "Vous gouvernez ce projet : vous gérez qui y tient quel rôle et comment il est configuré. Ses fichiers, son wiki et ses conversations vous restent fermés, sauf si vous en êtes membre ou utilisez l’accès bris de glace audité.",
    facts: "Vue d’ensemble",
    ownerEntity: "Entité propriétaire",
    visibility: "Visibilité",
    files: "Fichiers",
    filesValue_one: "{{count}} fichier · {{size}}",
    filesValue_other: "{{count}} fichiers · {{size}}",
    tasks: "Tâches",
    tasksValue: "{{total}} au total · {{open}} ouvertes · {{overdue}} en retard",
    rolesValue: "{{members}} · {{groups}} · {{rules}}",
    rolesMembers_one: "{{n}} personne",
    rolesMembers_other: "{{n}} personnes",
    rolesGroups_one: "{{n}} groupe",
    rolesGroups_other: "{{n}} groupes",
    rolesRules_one: "{{n}} règle",
    rolesRules_other: "{{n}} règles",
    classifications: "Classifications",
    noClassifications: "Aucun fichier",
    spend: "Dépense (30 derniers jours)",
    created: "Créé le",
    breakGlassActive:
      "Mettez fin à votre accès bris de glace à ce projet avant de le gérer depuis l’administration.",
    selfRefused:
      "La gouvernance ne vous donne jamais de rôle dans un projet, ni directement, ni par un groupe, ni par sa visibilité. Utilisez l’accès bris de glace pour le lire.",
    governed: {
      title: "Projets que vous gouvernez",
      lead: "Vous administrez les entités propriétaires de ces projets sans en être lecteur. Gérez-les depuis l’administration.",
      entityUnknown: "Une entité que vous administrez",
      link: "Gérer {{name}} dans l’administration",
    },
  },
  branding: {
    title: "Identité visuelle",
    hint: "Le nom, les couleurs et le logo de votre organisation dans ESI.",
    lead: "Comment ESI présente votre organisation à ses membres : le nom, une couleur d’accent et un logo par thème. Les onglets du navigateur et la page de connexion restent ESI.",
    readOnly: "Seuls les administrateurs de l’organisation peuvent modifier l’identité visuelle.",
    readOnlySupport: "Lecture seule en accès support : les images des logos ne sont pas chargées.",
    default: "Par défaut",
    remove: "Retirer",
    themeInline: { light: "thème clair", dark: "thème sombre" },
    theme: { light: "Thème clair", dark: "Thème sombre" },
    scope: {
      heading: "Toute l’organisation",
      purpose:
        "Défini une fois pour l’organisation : chaque entité et chaque membre voient la même identité visuelle.",
    },
    name: {
      heading: "Nom",
      label: "Nom affiché",
      help: "Nomme votre logo pour les lecteurs d’écran et apparaît dans le menu du compte.",
      helpFallback:
        "Nomme votre logo pour les lecteurs d’écran et apparaît dans le menu du compte. Sans nom, « {{name}} » est utilisé.",
      invalid: "Utilisez de 1 à 60 caractères visibles, sans caractère invisible ni de contrôle.",
    },
    accent: {
      heading: "Couleur d’accent",
      label: { light: "Thème clair", dark: "Thème sombre" },
      help: "Remplit les boutons principaux, et colore les liens et le focus lorsqu’elle reste lisible.",
      helpDarkInherited:
        "Reprend la couleur du thème clair tant que vous n’en définissez pas une pour le thème sombre.",
      sameAsLight: "Identique au thème clair",
      picker: "Choisir la couleur d’accent du {{theme}}",
      hex: "Couleur d’accent du {{theme}}, en hexadécimal",
      format: "Utilisez une couleur hexadécimale à six chiffres, par exemple #3b5bdb.",
      invalid: "Utilisez une couleur hexadécimale à six chiffres, par exemple #3b5bdb.",
      outcome: {
        applied:
          "Remplit les boutons principaux et colore les liens et le focus dans le {{theme}}.",
        "fills-only":
          "Remplit uniquement les boutons principaux : son contraste est inférieur à 4,5:1 sur le fond du {{theme}}, les liens et le focus gardent la couleur par défaut.",
        default: "La couleur d’accent par défaut s’applique dans le {{theme}}.",
      },
    },
    logo: {
      heading: "Logo",
      label: { light: "Thème clair", dark: "Thème sombre" },
      help: {
        light:
          "Remplace la marque ESI dans la barre latérale et la barre mobile. PNG ou WebP, de 32 à 2 048 pixels de côté, 1 Mo au plus ; affiché sur 24 pixels de haut.",
        dark: "Affiché dans le thème sombre. Sans lui, le logo clair s’affiche dans les deux thèmes.",
      },
      emails:
        "Les e-mails n’affichent pas un logo WebP ou de plus de 200 Ko : ils affichent le nom de votre organisation à la place.",
      on: { light: "Sur fond clair", dark: "Sur fond sombre" },
      none: "Aucun : la marque ESI s’affiche",
      darkNeedsLight: "Ajoutez d’abord un logo clair : le logo sombre ne s’affiche qu’avec lui",
      darkUnused:
        "Ce logo sombre n’est pas affiché : le thème sombre n’utilise un logo de l’organisation qu’avec un logo clair. Importez un logo clair pour l’utiliser.",
      usesLight: "Reprend le logo clair",
      upload: "Importer",
      replace: "Remplacer",
      uploading: "Import…",
      unnamed: "Logo de l’organisation",
      refused: {
        type: "Utilisez une image PNG ou WebP. Les formats SVG, JPEG et GIF ne sont pas acceptés.",
        size: "Le fichier dépasse 1 Mo. Exportez une image plus légère.",
        signature:
          "Ce fichier n’est pas du type d’image annoncé. Exportez-le à nouveau en PNG ou WebP.",
        dimensions: "Chaque côté doit mesurer entre 32 et 2 048 pixels.",
        pixels: "L’image dépasse 4 millions de pixels. Exportez-en une plus petite.",
        animated: "Les images animées ne sont pas acceptées. Utilisez une image fixe.",
        malformed: "L’image n’a pas pu être lue. Exportez-la à nouveau en PNG ou WebP.",
      },
    },
  },
  memoryAdmin: {
    title: "Mémoire",
    lead: "Combien de temps la mémoire est conservée, si les personnes et les projets peuvent l'utiliser, et dans quelle mesure elle sert. Les administrateurs ne voient jamais ce que chacun a enregistré.",
    policies: "Règles",
    figures: "Usage",
    figuresLead:
      "Les personnes sont comptées dans leur entité par défaut ; les chiffres de projet dans l'entité propriétaire du projet. Les entités de moins de 5 personnes n'affichent aucun chiffre, pour que l'usage de chacun reste anonyme.",
    entity: "Entité",
    metrics: {
      personal_memories: "Souvenirs personnels",
      users_with_memories: "Personnes utilisant la mémoire",
      proposals_accepted: "Suggestions enregistrées",
      proposals_dismissed: "Suggestions écartées",
      project_memories: "Souvenirs de projet",
      pending_suggestions: "En attente de validation",
    },
    tooFew: "Trop peu de personnes pour afficher des chiffres",
    total: "Entités affichées",
    activity: "Événements de mémoire",
    activityLead:
      "Changements de règles, effacements, exports et expirations, du plus récent au plus ancien. Jamais les souvenirs eux-mêmes.",
    activityEmpty: "Aucun événement de mémoire pour l'instant.",
    activityTenantAdmins:
      "Les événements de mémoire sont visibles des administrateurs de l'organisation.",
  },
  audit: {
    title: "Événements d’audit",
    lead: "Qui a fait quoi et quand, pour un type d’action. Les motifs et détails restent dans le journal d’audit.",
    when: "Date",
    action: "Action",
    target: "Cible",
    actor: "Par",
    empty: "Aucun événement de ce type.",
    loadMore: "Afficher plus",
    back: "Rapports",
    actionLabel: {
      idp_created: "Fournisseur d'identité ajouté",
      idp_removed: "Fournisseur d'identité supprimé",
      idp_cleanup_completed: "Nettoyage du fournisseur d'identité terminé",
      domain_claimed: "Domaine revendiqué",
      domain_verified: "Domaine vérifié",
      domain_released: "Domaine libéré",
      feedback_sent: "Retour envoyé à l'équipe ESI",
      tenant_budget_pressure: "Budget IA mensuel sous pression",
      memory_expired: "Souvenirs inutilisés expirés",
      project_memories_expired: "Souvenirs du projet inutilisés expirés",
      memory_exported: "Mémoire exportée",
      memory_erased: "Mémoire effacée",
      memory_project_suggested: "Souvenir de projet suggéré",
      memory_project_approved: "Souvenir de projet validé",
      memory_project_rejected: "Souvenir de projet refusé",
      memory_project_edited: "Souvenir de projet modifié",
      memory_project_deleted: "Souvenir de projet supprimé",
      memory_project_withdrawn: "Suggestion de souvenir retirée",
      memory_project_expired: "Suggestion de souvenir expirée",
      setting_changed: "Paramètre modifié",
      tenant_jit_changed: "Création des comptes à la première connexion modifiée",
      auth_jit_refused: "Connexion refusée : aucun compte",
      setting_reset: "Paramètre réinitialisé",
      branding_logo_uploaded: "Logo importé",
      branding_logo_removed: "Logo retiré",
      project_break_glass: "Accès bris de glace",
      declassification_created: "Déclassification",
      declassification_withdrawn: "Déclassification retirée",
    },
    actorType: { user: "Une personne", operator: "Un opérateur", system: "ESI" },
  },
  standalone: {
    ask: "Posez votre question…",
    noKnowledge: "Ces réponses n'utilisent aucune connaissance de projet",
    disabled:
      "Les conversations hors projet sont désactivées pour votre organisation : celle-ci se lit, sans pouvoir être poursuivie.",
  },
  conversationHeader: {
    noProject: "Sans projet",
    viaClient: "via {{host}}",
    viaCli: "via l'outil en ligne de commande ESI",
    viaClientHint: "Démarrée depuis un client externe : {{id}}",
    move: "Déplacer dans un projet",
    more: "Actions de la conversation",
    rename: "Renommer",
    renameLabel: "Titre de la conversation",
    task: "Tâche : {{title}}",
    taskUnavailable: "Tâche indisponible",
    taskUnavailableHint:
      "Cette conversation porte sur une tâche que vous ne pouvez pas ouvrir actuellement.",
  },
  conversationActions: {
    archive: "Archiver",
    unarchive: "Désarchiver",
    delete: "Supprimer",
  },
  conversationView: {
    notFoundTitle: "Conversation introuvable",
    notFound: "Elle n'existe pas, ou vous ne pouvez pas l'ouvrir.",
    backHome: "Retour à l'accueil",
  },
  conversationList: {
    lead: "Toutes les conversations que vous pouvez ouvrir, dans vos projets et hors projet.",
    search: "Rechercher des conversations",
    filter: {
      label: "Afficher",
      all: "Toutes",
      mine: "Les miennes",
      shared: "Partagées avec moi",
      project: "Partagées avec le projet",
      archived: "Archivées",
    },
    projectShared: "Partagée avec le projet",
    projectSharedAnd_one: "Partagée avec le projet et {{count}} personne",
    projectSharedAnd_other: "Partagée avec le projet et {{count}} personnes",
    projectSharedBy: "Partagée avec le projet par {{name}}",
    sharedWith_one: "Partagée avec {{count}} personne",
    sharedWith_other: "Partagée avec {{count}} personnes",
    sharedBy: "Partagée par {{name}}",
    archived: "Archivée",
    levelSources: "Sources {{level}}",
    loadMore: "Charger plus",
    actionsFor: "Actions pour {{title}}",
    empty: {
      noneTitle: "Aucune conversation pour l'instant",
      none: "Posez une question depuis Nouvelle conversation, ou dans un projet.",
      inProject: "Posez une question sur ce projet pour démarrer une conversation.",
      inProjectReadOnly: "Aucune conversation n'a été démarrée dans ce projet.",
      shared: "Personne ne vous a encore partagé de conversation.",
      projectTitle: "Rien de partagé avec le projet",
      project: "Les conversations partagées avec tout le projet apparaissent ici.",
      noMatchTitle: "Aucune conversation ne correspond",
      noMatch: "La recherche porte sur les titres et vos propres questions.",
      archivedTitle: "Rien d'archivé",
      archived:
        "Les conversations archivées quittent vos listes et restent consultables par la recherche.",
    },
  },
  search: {
    title: "Rechercher des conversations",
    label: "Rechercher",
    placeholder: "Rechercher dans les titres et vos questions…",
    recent: "Récentes",
    results: "Conversations",
    none: "Aucune conversation ne correspond.",
    noRecent: "Aucune conversation pour l'instant.",
    hint: "La recherche porte sur les titres et vos propres questions, jamais sur les réponses.",
    count_one: "{{count}} conversation trouvée",
    count_other: "{{count}} conversations trouvées",
  },
  move: {
    title: "Déplacer dans un projet",
    explain:
      "La conversation rejoint le projet définitivement. Voici ce qui change avant de confirmer.",
    project: "Projet",
    pick: "Choisir un projet",
    noProject: "Vous n'avez aucun projet où la déplacer.",
    effects: "Ce qui change",
    into: "Elle devient une conversation de {{project}} et s'ouvre là.",
    noLapsing: "Tous ses destinataires peuvent lire le projet : aucun partage ne tombe.",
    lapsing_one: "{{count}} partage tombe : {{names}} ne peut pas lire ce projet.",
    lapsing_other: "{{count}} partages tombent : {{names}} ne peuvent pas lire ce projet.",
    breakGlass:
      "Les administrateurs du projet peuvent y accéder par un accès d'urgence journalisé.",
    attachments_one: "{{count}} pièce jointe reste avec la conversation.",
    attachments_other: "{{count}} pièces jointes restent avec la conversation.",
    attachmentsPrivate_one:
      "{{count}} d'entre elles reste privée, pour vous et les personnes que vous avez nommées.",
    attachmentsPrivate_other:
      "{{count}} d'entre elles restent privées, pour vous et les personnes que vous avez nommées.",
    attachmentsAllPrivate:
      "Toutes restent privées, pour vous et les personnes que vous avez nommées.",
    sourcesOnly:
      "Le projet ne répond qu'à partir de ses sources : les prochaines réponses suivront ce mode.",
    oneWay: "Elle ne pourra plus sortir du projet.",
    residency:
      "Cette conversation a reçu des réponses de modèles hors UE : elle ne peut pas aller dans ce projet limité à l'UE.",
    changed:
      "Quelque chose a changé depuis cet aperçu (un partage, par exemple). Vérifiez à nouveau.",
    notStandalone: "Seule une conversation hors projet peut être déplacée.",
    turnInProgress: "Une réponse est en cours d'écriture. Réessayez une fois terminée.",
    frozen: "Ce projet est en lecture seule et n'accepte aucune conversation.",
    confirm: "Déplacer dans {{project}}",
    confirmShort: "Déplacer",
    moving: "Déplacement…",
  },
  deleteConversation: {
    title: "Supprimer cette conversation ?",
    gone: "Elle disparaît aussitôt pour tous, de vos listes comme de la recherche.",
    lapsing_one: "Son partage avec {{count}} personne tombe.",
    lapsing_other: "Ses partages avec {{count}} personnes tombent.",
    derived:
      "Les fichiers et brouillons de wiki enregistrés depuis elle restent, avec leurs propres lecteurs.",
    turnInProgress: "Une réponse est en cours d'écriture. Réessayez une fois terminée.",
    confirm: "Supprimer",
    deleting: "Suppression…",
  },
  artefacts: {
    kind: {
      markdown: "Document",
      code: "Code",
      mermaid: "Diagramme",
      svg: "Dessin",
      csv: "Tableau",
      file: "Fichier",
    },
    file: { downloadToOpen: "Téléchargez le fichier pour l'ouvrir." },

    version: "Version {{version}}",
    open: "Ouvrir",
    openNamed: "Ouvrir {{title}}",
    shown: "Ouvert à côté de la conversation",
    writing: "Rédaction…",
    writingNamed: "Rédaction de {{title}}…",
    untitled: "Artefact",
    failed: "Écriture impossible",
    unavailable: "Non accessible pour vous",
    panel: {
      label: "Artefact : {{title}}",
      back: "Connaissances",
      backLabel: "Revenir au panneau des connaissances",
      close: "Fermer l'artefact",
      download: "Télécharger",
      downloadNamed: "Télécharger la version {{version}} de {{title}}",
      loading: "Chargement de l'artefact…",
      notFound: "Cet artefact ne vous est pas accessible, ou n'existe plus.",
      failed: "L'artefact n'a pas pu être chargé.",
      retry: "Réessayer",
      resize: "Redimensionner le panneau de l'artefact",
    },
    versions: {
      label: "Versions",
      position: "Version {{version}} sur {{count}}",
      previous: "Version précédente ({{version}})",
      next: "Version suivante ({{version}})",
      latest: "Dernière",
      latestNamed: "Afficher la dernière version ({{version}})",
      newer: "Une version plus récente est disponible.",
      showNewer: "Afficher la version {{version}}",
    },
    code: {
      copy: "Copier",
      copied: "Copié",
      copyLabel: "Copier le code",
      plain: "Texte brut",
      large: "Coloration désactivée pour les fichiers volumineux.",
    },
    table: {
      caption: "{{title}}",
      partial: "{{shown}} lignes affichées sur {{total}} ; téléchargez pour tout voir.",
      empty: "Ce tableau n'a aucune ligne.",
    },
    diagram: {
      drawing: "Tracé du diagramme…",
      failed: "Ce diagramme n'a pas pu être tracé. Sa source est affichée à la place.",
      source: "Source",
      diagram: "Diagramme",
      view: "Affichage",
    },
    section: {
      title: "Artefacts",
      updated: "Mis à jour {{date}}",
      empty: "Aucun artefact dans cette conversation pour l'instant.",
    },
  },
  projectOverview: {
    loading: "Chargement",
    loadError: "Impossible de charger cette section.",
    open: "Ouvrir",
    by: "par {{name}}",
    pages_one: "{{count}} page",
    pages_other: "{{count}} pages",
    ask: "Posez une question sur ce projet",
    recent: {
      title: "Conversations récentes",
      empty: "Aucune conversation pour l’instant.",
    },
    needsYou: {
      title: "Pour vous",
      count_one: "{{count}} élément",
      count_other: "{{count}} éléments",
      empty: "Rien ne vous attend pour l’instant.",
      more_one: "{{count}} de plus",
      more_other: "{{count}} de plus",
      showReview: "Voir la file de relecture",
      showTasks: "Voir vos tâches",
      showMemories: "Voir les mémoires à examiner",
      showAccessRequests: "Voir les demandes d’accès",
      countAtLeast_one: "au moins {{count}} élément",
      countAtLeast_other: "au moins {{count}} éléments",
      moreAtLeast_one: "{{count}} de plus, et d’autres",
      moreAtLeast_other: "{{count}} de plus, et d’autres",
      review: "Relire {{title}}",
      revise: "Reprendre {{title}}",
      sentBack: "Modifications demandées",
      item: {
        source_changed: "Vérifier {{title}} : une source a changé",
        contradiction: "Vérifier {{title}} : une contradiction",
        redeclassify: "Relire la déclassification de {{title}}",
      },
      itemUntitled: {
        source_changed: "Vérifier une page : une source a changé",
        contradiction: "Vérifier une page : une contradiction",
        redeclassify: "Relire une déclassification",
      },
      hiddenTask: "Une tâche que vous ne pouvez pas lire",
      due: "Échéance {{date}}",
      memories_one: "{{count}} mémoire à examiner",
      memories_other: "{{count}} mémoires à examiner",
      accessRequests_one: "{{count}} demande d’accès à traiter",
      accessRequests_other: "{{count}} demandes d’accès à traiter",
    },
    whatChanged: {
      title: "Nouveautés",
      filter: "Afficher",
      all: "Tout",
      readable: "Seul ce que vous pouvez lire est listé.",
      empty: "Rien n’a encore changé.",
      emptyFilter: "Rien de ce type n’a changé récemment.",
      published: "Publié",
      publishedMore_one: "{{title}} et {{count}} autre page",
      publishedMore_other: "{{title}} et {{count}} autres pages",
      sourceAdded: "Source ajoutée",
      shared: "Partagée avec le projet",
    },
    wiki: {
      pages: "Pages",
      waiting: "En attente de relecture",
      lastPublished: "Dernière publication {{when}}",
      neverPublished: "Rien n’est encore publié",
      needsChecking: "À vérifier",
      waitingCount_one: "{{count}} en attente",
      waitingCount_other: "{{count}} en attente",
      needsCheckingCount_one: "{{count}} à vérifier",
      needsCheckingCount_other: "{{count}} à vérifier",
    },
    sources: {
      files: "Fichiers",
      processing: "En traitement",
      failed: "En échec",
      failedOwn: "Vos envois en échec",
      restricted_one: "{{count}} autre fichier est réservé à d’autres lecteurs.",
      restricted_other: "{{count}} autres fichiers sont réservés à d’autres lecteurs.",
      filesCount_one: "{{count}} fichier",
      filesCount_other: "{{count}} fichiers",
      processingCount_one: "{{count}} en traitement",
      processingCount_other: "{{count}} en traitement",
      failedCount_one: "{{count}} en échec",
      failedCount_other: "{{count}} en échec",
    },
    usage: {
      title: "Utilisation",
      questions: "Questions posées",
      active: "Membres actifs",
      sourced: "Réponses avec sources",
      topCited: "Les plus cités",
      citations_one: "{{count}} citation",
      citations_other: "{{count}} citations",
      questionsCount_one: "{{count}} question",
      questionsCount_other: "{{count}} questions",
      activeCount_one: "{{count}} membre actif",
      activeCount_other: "{{count}} membres actifs",
      updated: "30 derniers jours · mis à jour {{when}}",
      notComputed: "30 derniers jours · chiffres disponibles d’ici une heure",
      notComputedShort: "Pas encore calculé",
    },
    people: {
      title: "Membres",
      unnamed: "Sans nom",
      owner: "Propriétaire",
      managers_one: "Responsable",
      managers_other: "Responsables",
      in: "Dans le projet",
      members_one: "{{count}} membre",
      members_other: "{{count}} membres",
      groups_one: "{{count}} groupe",
      groups_other: "{{count}} groupes",
      yourRole: "Votre rôle : {{role}}",
    },
    start: {
      title: "Ce projet n’a pas encore de contenu",
      lead: "Ajoutez des sources, rédigez une première page de wiki ou invitez des personnes, puis posez une première question ci-dessus.",
      leadReader:
        "Rien n’a encore été ajouté. Vous pouvez déjà poser une première question ci-dessus.",
      leadFrozen: "Rien n’a encore été ajouté, et le projet est en lecture seule.",
      addSources: "Ajouter des sources",
      newPage: "Nouvelle page",
      invite: "Inviter des personnes",
    },
  },
  projectHome: {
    tabsLabel: "Projet",
    tabs: {
      home: "Vue d’ensemble",
      conversations: "Conversations",
      settings: "Paramètres",
      memory: "Mémoire",
    },
    newConversation: "Nouvelle conversation",
    askShort: "Posez une question sur ce projet…",
    askSend: "Demander",
  },
} satisfies Catalogue;
